<template>
  <section class="wc-insights" :class="{ 'wc-insights--dark': isDark }">
    <header class="insights-hero">
      <div class="hero-main">
        <div class="hero-icon">
          <v-icon color="white" size="27">mdi-chart-box-outline</v-icon>
        </div>
        <div class="hero-copy">
          <div class="hero-eyebrow">CENTER-AUSWERTUNG</div>
          <h1>Center im Blick</h1>
          <div class="hero-meta">
            <span><v-icon x-small>mdi-calendar-range</v-icon>{{ rangeLabel }}</span>
            <span><v-icon x-small>mdi-map-marker-multiple-outline</v-icon>{{ studioLabel }}</span>
            <span v-if="data"><v-icon x-small>mdi-database-check-outline</v-icon>{{ meta.rangeDays }} Tage analysiert</span>
          </div>
        </div>
      </div>
      <div class="hero-actions">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon class="hero-action" :disabled="loading" v-bind="attrs" v-on="on" @click="loadData">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </template>
          <span>Statistik aktualisieren</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon class="hero-action" :disabled="!data || loading" v-bind="attrs" v-on="on" @click="exportWorkbook">
              <v-icon>mdi-file-excel-outline</v-icon>
            </v-btn>
          </template>
          <span>Vollständige Auswertung exportieren</span>
        </v-tooltip>
      </div>
    </header>

    <div v-if="loading && !data" class="state-panel loading-state">
      <div class="loading-orbit">
        <v-progress-circular indeterminate color="#20b26b" size="42" width="4" />
      </div>
      <div>
        <strong>Kalender wird ausgewertet</strong>
        <span>Termine, Schichten und Arbeitsaufträge werden sauber zusammengeführt.</span>
      </div>
    </div>

    <div v-else-if="error && !data" class="state-panel error-state">
      <v-icon color="#ef4444" size="36">mdi-alert-circle-outline</v-icon>
      <div>
        <strong>Statistik konnte nicht geladen werden</strong>
        <span>{{ error }}</span>
      </div>
      <v-btn depressed color="primary" class="text-none" @click="loadData">Erneut versuchen</v-btn>
    </div>

    <template v-else-if="data">
      <div class="kpi-grid">
        <article v-for="card in kpiCards" :key="card.key" class="kpi-card" :style="{ '--accent': card.color }">
          <div class="kpi-topline">
            <span class="kpi-icon"
              ><v-icon :color="card.color" size="20">{{ card.icon }}</v-icon></span
            >
            <span v-if="card.badge" class="kpi-badge" :class="card.badgeTone">{{ card.badge }}</span>
          </div>
          <div class="kpi-value">{{ card.value }}</div>
          <div class="kpi-label">{{ card.label }}</div>
          <div class="kpi-sub">{{ card.sub }}</div>
        </article>
      </div>

      <div class="dashboard-shell">
        <v-tabs v-model="activeTab" :dark="isDark" class="insights-tabs" background-color="transparent" color="#16a765" show-arrows>
          <v-tab><v-icon left small>mdi-view-dashboard-outline</v-icon>Cockpit</v-tab>
          <v-tab><v-icon left small>mdi-calendar-account-outline</v-icon>Termine</v-tab>
          <v-tab><v-icon left small>mdi-clipboard-check-multiple-outline</v-icon>Ämtli & Aufgaben</v-tab>
          <v-tab><v-icon left small>mdi-account-group-outline</v-icon>Team & Kapazität</v-tab>
          <v-tab><v-icon left small>mdi-office-building-marker-outline</v-icon>Center & Daten</v-tab>
          <v-tab><v-icon left small>mdi-account-clock-outline</v-icon>Besucher & Kundenkontakte</v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab" :dark="isDark" class="insights-tab-items">
          <v-tab-item>
            <div class="tab-content">
              <div class="content-grid content-grid--wide">
                <article class="panel panel--full">
                  <div class="panel-heading">
                    <div>
                      <span class="panel-eyebrow">AKTIVITÄT</span>
                      <h2>Kundenkontakte im Verlauf</h2>
                    </div>
                    <span class="panel-context">pro Kalendertag</span>
                  </div>
                  <apexchart type="area" height="310" :options="activityTrendOptions" :series="activityTrendSeries" />
                </article>

                <article class="panel">
                  <div class="panel-heading">
                    <div>
                      <span class="panel-eyebrow">TERMINVERTEILUNG</span>
                      <h2>Terminübersicht</h2>
                    </div>
                  </div>
                  <div class="donut-wrap">
                    <apexchart type="donut" height="270" :options="outcomeOptions" :series="outcomeSeries" />
                    <div class="donut-caption">
                      <strong>{{ totals.totalAppointments || 0 }}</strong>
                      <span>Termine</span>
                    </div>
                  </div>
                </article>

                <article class="panel">
                  <div class="panel-heading">
                    <div>
                      <span class="panel-eyebrow">KUNDENBETREUUNG</span>
                      <h2>Aktive Zeit mit Kunden</h2>
                    </div>
                  </div>
                  <apexchart type="radialBar" height="205" :options="careOverviewOptions" :series="careOverviewSeries" />
                  <div class="capacity-breakdown">
                    <div>
                      <span class="dot dot--blue"></span><strong>{{ totals.customerAppointmentHours || 0 }}h</strong><small>Kundentermine</small>
                    </div>
                    <div>
                      <span class="dot dot--violet"></span><strong>{{ totals.customerCareHours || 0 }}h</strong><small>ohne Doppelzählung</small>
                    </div>
                    <div>
                      <span class="dot dot--green"></span><strong>{{ totals.trainerpointHours || 0 }}h</strong><small>Trainerpoint</small>
                    </div>
                    <div>
                      <span class="dot dot--red"></span><strong>{{ totals.noshow || 0 }}</strong
                      ><small>nicht gekommen</small>
                    </div>
                  </div>
                </article>

                <article class="panel">
                  <div class="panel-heading">
                    <div>
                      <span class="panel-eyebrow">STÄRKSTE TAGE</span>
                      <h2>Spitzen im Zeitraum</h2>
                    </div>
                  </div>
                  <div class="top-days">
                    <div v-for="day in topDays" :key="day.date" class="top-day-row">
                      <div class="day-date">
                        <strong>{{ formatDay(day.date) }}</strong>
                        <span>{{ formatShortDate(day.date) }}</span>
                      </div>
                      <div class="day-bar">
                        <span :style="{ width: `${day.intensity}%` }"></span>
                      </div>
                      <div class="day-values">
                        <strong> {{ day.activeCustomerAppointments || 0 }} Kundentermine · {{ day.customerAppointmentHours || 0 }}h </strong>
                        <span>{{ day.trainerpointEntries || 0 }} Trainerpoint-Einsätze</span>
                      </div>
                    </div>
                  </div>
                </article>
              </div>
            </div>
          </v-tab-item>

          <v-tab-item>
            <div class="tab-content">
              <section v-if="canViewCustomerFeedback" class="feedback-command-center">
                <div class="feedback-command-header">
                  <div class="feedback-command-title">
                    <span class="feedback-command-icon"><v-icon color="white">mdi-star-shooting-outline</v-icon></span>
                    <div>
                      <span class="panel-eyebrow">KUNDENFEEDBACK</span>
                      <h2>So werden eure Termine erlebt</h2>
                      <p>Bewertungen aus abgeschlossenen Kundenterminen im ausgewählten Zeitraum.</p>
                    </div>
                  </div>
                </div>

                <div class="feedback-kpi-grid">
                  <article v-for="card in feedbackKpis" :key="card.key" :style="{ '--feedback-accent': card.color }">
                    <span
                      ><v-icon :color="card.color" size="20">{{ card.icon }}</v-icon></span
                    >
                    <small>{{ card.label }}</small>
                    <strong>{{ card.value }}</strong>
                    <p>{{ card.detail }}</p>
                  </article>
                </div>

                <template v-if="feedbackSummary.ratings">
                  <div class="feedback-analysis-grid">
                    <article class="feedback-panel feedback-panel--trend">
                      <div class="panel-heading">
                        <div>
                          <span class="panel-eyebrow">VERLAUF</span>
                          <h2>Bewertungen im Zeitraum</h2>
                        </div>
                        <span class="panel-context">{{ feedbackTrend.length }} Tage mit möglichen Rückmeldungen</span>
                      </div>
                      <apexchart type="line" height="300" :options="feedbackTrendOptions" :series="feedbackTrendSeries" />
                    </article>

                    <article class="feedback-panel">
                      <div class="panel-heading">
                        <div>
                          <span class="panel-eyebrow">VERTEILUNG</span>
                          <h2>Von 5 bis 1 Stern</h2>
                        </div>
                      </div>
                      <div class="feedback-distribution">
                        <div v-for="row in feedbackDistribution" :key="row.stars">
                          <span>{{ row.stars }} <v-icon size="14" color="#f5b942">mdi-star</v-icon></span>
                          <div><i :style="{ width: `${row.percentage}%` }"></i></div>
                          <strong>{{ row.count }}</strong>
                        </div>
                      </div>
                      <div v-if="feedbackTopTags.length" class="feedback-tag-cloud">
                        <span v-for="tag in feedbackTopTags" :key="tag.key">
                          <v-icon size="14">mdi-check-decagram-outline</v-icon>{{ feedbackTagLabel(tag.key) }}<strong>{{ tag.count }}</strong>
                        </span>
                      </div>
                    </article>

                    <article class="feedback-panel feedback-panel--full">
                      <div class="panel-heading">
                        <div>
                          <span class="panel-eyebrow">KUNDENSTIMMEN</span>
                          <h2>Neueste Rückmeldungen</h2>
                        </div>
                        <v-btn text small class="text-none panel-context--interactive" @click="allFeedbackDialog = true">
                          <v-icon left small>mdi-format-list-bulleted</v-icon>Alle {{ feedbackSummary.ratings }} Bewertungen anzeigen
                        </v-btn>
                      </div>
                      <div class="feedback-feed">
                        <div
                          v-for="feedback in feedbackRecent"
                          :key="`${feedback.appointmentId}-${feedback.customerId || 'customer'}-${
                            feedback.createdAt || feedback.updatedAt || feedback.stars
                          }`"
                          class="feedback-feed-card"
                          :class="{ 'feedback-feed-card--attention': feedback.stars <= 2 }">
                          <div class="feedback-feed-topline">
                            <span
                              ><v-icon size="16" color="#f5b942">mdi-star</v-icon><strong>{{ feedback.stars }}.0</strong></span
                            >
                            <small>{{ formatShortDate(feedback.date) }}</small>
                          </div>
                          <div class="feedback-customer">
                            <v-icon size="16">mdi-account-circle-outline</v-icon><strong>{{ feedback.customerName || "Unbekannter Kunde" }}</strong>
                          </div>
                          <p :class="{ 'feedback-comment--empty': !feedback.comment }">{{ feedback.comment || "Bewertung ohne Kommentar" }}</p>
                          <div v-if="feedback.tags && feedback.tags.length" class="feedback-feed-tags">
                            <span v-for="tag in feedback.tags" :key="tag">{{ feedbackTagLabel(tag) }}</span>
                          </div>
                          <div class="feedback-feed-meta">
                            <span><v-icon size="14">mdi-calendar-blank-outline</v-icon>{{ feedback.title }}</span>
                            <span><v-icon size="14">mdi-account-tie-outline</v-icon>{{ feedbackEmployeeName(feedback.employeeId) }}</span>
                            <span><v-icon size="14">mdi-map-marker-outline</v-icon>{{ feedbackCenterName(feedback.studioId) }}</span>
                          </div>
                        </div>
                      </div>
                    </article>
                  </div>
                </template>

                <div v-else class="feedback-empty-state">
                  <span><v-icon size="40" color="#16a765">mdi-star-plus-outline</v-icon></span>
                  <div>
                    <strong>Noch keine Bewertungen in diesem Zeitraum</strong>
                    <p>Sobald Kunden abgeschlossene Termine bewerten, erscheinen hier Verlauf, Stärken und Kommentare.</p>
                  </div>
                </div>
              </section>

              <div class="content-grid content-grid--half">
                <article class="panel">
                  <div class="panel-heading panel-heading--wrap">
                    <div>
                      <span class="panel-eyebrow">ZEITMUSTER</span>
                      <h2>Termine nach Wochentag & Uhrzeit</h2>
                    </div>
                    <span class="panel-context">Anzahl im Zeitraum</span>
                  </div>
                  <apexchart type="heatmap" height="340" :options="appointmentHeatmapOptions" :series="appointmentHeatmapSeries" />
                </article>

                <article class="panel">
                  <div class="panel-heading">
                    <div>
                      <span class="panel-eyebrow">TERMINARTEN</span>
                      <h2>Welche Angebote am häufigsten betreuen</h2>
                    </div>
                  </div>
                  <apexchart type="bar" height="340" :options="appointmentTypeChartOptions" :series="appointmentTypeChartSeries" />
                </article>

                <article class="panel panel--full no-show-analysis">
                  <div class="panel-heading panel-heading--wrap">
                    <div>
                      <span class="panel-eyebrow">NICHT GEKOMMEN</span>
                      <h2>Fälle verstehen und wiederkehrende Muster erkennen</h2>
                    </div>
                    <span class="panel-context">{{ noShowRows.length }} Fälle im Zeitraum</span>
                  </div>

                  <div class="no-show-highlight-grid">
                    <div v-for="highlight in noShowHighlights" :key="highlight.key" :style="{ '--no-show-color': highlight.color }">
                      <v-icon :color="highlight.color">{{ highlight.icon }}</v-icon>
                      <span>{{ highlight.label }}</span>
                      <strong :class="{ 'no-show-value--text': highlight.textValue }">{{ highlight.value }}</strong>
                      <small>{{ highlight.detail }}</small>
                    </div>
                  </div>

                  <div v-if="repeatNoShowCustomers.length" class="repeat-customer-strip">
                    <div class="repeat-customer-strip__title">
                      <v-icon color="#f97316" size="19">mdi-account-alert-outline</v-icon>
                      <div>
                        <strong>Wiederholt nicht gekommen</strong>
                        <small>Für die Kundenanalyse anklicken</small>
                      </div>
                    </div>
                    <button
                      v-for="customer in repeatNoShowCustomers.slice(0, 8)"
                      :key="customer.key"
                      type="button"
                      class="repeat-customer"
                      @click="openCustomer(customer)">
                      <span>{{ customer.name }}</span>
                      <strong>{{ customer.noShows }}×</strong>
                      <small>{{ customer.noShowRate }}%</small>
                    </button>
                  </div>

                  <v-data-table
                    :dark="isDark"
                    :headers="noShowHeaders"
                    :items="noShowRows"
                    :items-per-page="10"
                    class="insights-table no-show-table"
                    no-data-text="Keine Nicht-gekommen-Fälle im gewählten Zeitraum.">
                    <template v-slot:item.date="{ item }">
                      <div class="date-time-cell">
                        <strong>{{ formatShortDate(item.date) }}</strong>
                        <small>{{ item.start }}–{{ item.end }}</small>
                      </div>
                    </template>
                    <template v-slot:item.customer="{ item }">
                      <div v-if="item.customers && item.customers.length" class="customer-link-list">
                        <button v-for="customer in item.customers" :key="customer.key" type="button" @click.stop="openCustomer(customer)">
                          {{ customer.name }}
                          <v-icon size="14">mdi-open-in-new</v-icon>
                        </button>
                      </div>
                      <span v-else>{{ item.customer || "–" }}</span>
                    </template>
                    <template v-slot:item.title="{ item }">
                      <strong>{{ translateAppointmentType(item.appointmentType, item.title) }}</strong>
                    </template>
                    <template v-slot:item.description="{ item }">
                      <span v-if="item.description" class="appointment-note">{{ item.description }}</span>
                      <span v-else class="appointment-note appointment-note--empty">Keine Begründung hinterlegt</span>
                    </template>
                  </v-data-table>
                </article>

                <article class="panel panel--full">
                  <div class="panel-heading panel-heading--wrap">
                    <div>
                      <span class="panel-eyebrow">TERMINARTEN</span>
                      <h2>Welche Angebote wie performen</h2>
                    </div>
                    <v-text-field
                      v-model="appointmentSearch"
                      dense
                      outlined
                      hide-details
                      clearable
                      prepend-inner-icon="mdi-magnify"
                      label="Terminart suchen"
                      class="table-search" />
                  </div>
                  <v-data-table
                    :dark="isDark"
                    :headers="appointmentTypeHeaders"
                    :items="filteredAppointmentTypes"
                    :items-per-page="12"
                    class="insights-table"
                    no-data-text="Keine Terminarten in diesem Zeitraum">
                    <template v-slot:item.title="{ item }">
                      <div class="primary-cell">
                        <span class="type-icon type-icon--appointment"><v-icon size="17">mdi-calendar-account</v-icon></span>
                        <div>
                          <strong>{{ translateAppointmentType(item.key, item.title) }}</strong
                          ><small>{{ item.averageMinutes }} Min. im Durchschnitt</small>
                        </div>
                      </div>
                    </template>
                    <template v-slot:item.activeCustomerHours="{ item }">{{ item.activeCustomerHours || 0 }}h</template>
                    <template v-slot:item.feedbackAverage="{ item }">
                      <span v-if="item.feedbackRatings" class="feedback-table-score">
                        <v-icon size="15" color="#f5b942">mdi-star</v-icon>
                        <strong>{{ item.feedbackAverage.toFixed(1) }}</strong>
                        <small>({{ item.feedbackRatings }})</small>
                      </span>
                      <span v-else class="muted-value">–</span>
                    </template>
                    <template v-slot:item.feedbackResponseRate="{ item }">
                      <span v-if="item.customerFeedback" class="feedback-response-pill">{{ item.feedbackResponseRate }}%</span>
                      <span v-else class="muted-value">–</span>
                    </template>
                    <template v-slot:item.averageMinutes="{ item }">{{ item.averageMinutes }} Min.</template>
                    <template v-slot:item.noShowRate="{ item }">
                      <rate-pill :value="item.noShowRate" inverse />
                    </template>
                  </v-data-table>
                </article>

                <article class="panel panel--full customer-analysis-panel">
                  <div class="panel-heading panel-heading--wrap">
                    <div>
                      <span class="panel-eyebrow">KUNDENANALYSE</span>
                      <h2>Terminhistorie und Betreuung im ausgewählten Zeitraum</h2>
                    </div>
                    <v-text-field
                      v-model="customerSearch"
                      dense
                      outlined
                      hide-details
                      clearable
                      prepend-inner-icon="mdi-account-search-outline"
                      label="Kunde oder Mitarbeiter suchen"
                      class="table-search" />
                  </div>
                  <v-data-table
                    :dark="isDark"
                    :headers="customerHeaders"
                    :items="filteredCustomerRows"
                    :items-per-page="15"
                    class="insights-table customer-analysis-table"
                    no-data-text="Keine Kundentermine im ausgewählten Zeitraum.">
                    <template v-slot:item.name="{ item }">
                      <button type="button" class="customer-primary-link" @click="openCustomer(item)">
                        <span class="type-icon type-icon--appointment">
                          <v-icon size="17">mdi-account-search-outline</v-icon>
                        </span>
                        <span>
                          <strong>{{ item.name }}</strong>
                          <small>{{ item.hasAccount ? "Kundenkonto" : "ohne verknüpftes Konto" }}</small>
                        </span>
                        <v-icon size="16">mdi-chevron-right</v-icon>
                      </button>
                    </template>
                    <template v-slot:item.customerHours="{ item }"
                      ><strong>{{ item.customerHours }}h</strong></template
                    >
                    <template v-slot:item.noShows="{ item }">
                      <span :class="{ 'danger-value': item.noShows > 0 }">{{ item.noShows }}</span>
                    </template>
                    <template v-slot:item.noShowRate="{ item }">
                      <rate-pill :value="item.noShowRate" inverse />
                    </template>
                    <template v-slot:item.lastDate="{ item }">{{ formatShortDate(item.lastDate) }}</template>
                  </v-data-table>
                </article>
              </div>
            </div>
          </v-tab-item>

          <v-tab-item>
            <div class="tab-content">
              <div class="category-grid">
                <article v-for="category in categoryCards" :key="category.key" class="category-card" :style="{ '--category-color': category.color }">
                  <div class="category-icon">
                    <v-icon :color="category.color">{{ category.icon }}</v-icon>
                  </div>
                  <div class="category-main">
                    <span>{{ category.label }}</span>
                    <strong>{{ category.total }}</strong>
                    <small>
                      {{ category.hours }}h eingeplant · {{ category.workShare }}% der Nettozeit
                      <span v-if="category.key === 'trainerpoint'"> · Teil der Aufgaben</span>
                      <span v-if="category.templatesWithoutEntries">
                        · {{ category.templatesWithoutEntries }} Vorlagen im Zeitraum ohne Eintrag
                      </span>
                    </small>
                  </div>
                </article>
              </div>

              <div class="content-grid content-grid--half">
                <article class="panel">
                  <div class="panel-heading">
                    <div>
                      <span class="panel-eyebrow">VERLAUF</span>
                      <h2>Arbeitsaufträge pro Tag</h2>
                    </div>
                  </div>
                  <apexchart type="area" height="320" :options="workItemTrendOptions" :series="workItemTrendSeries" />
                </article>

                <article class="panel">
                  <div class="panel-heading panel-heading--wrap">
                    <div>
                      <span class="panel-eyebrow">EINSATZZEIT</span>
                      <h2>Wann Kundenbetreuung & Aufgaben liegen</h2>
                    </div>
                    <v-btn-toggle v-model="workItemHeatmapMetric" mandatory dense class="metric-toggle">
                      <v-btn small value="trainerpoint">Trainerpoint</v-btn>
                      <v-btn small value="amtli">Ämtli</v-btn>
                      <v-btn small value="tasks">Aufgaben</v-btn>
                    </v-btn-toggle>
                  </div>
                  <apexchart type="heatmap" height="320" :options="workItemHeatmapOptions" :series="workItemHeatmapSeries" />
                </article>

                <article class="panel panel--full">
                  <div class="panel-heading panel-heading--wrap">
                    <div>
                      <span class="panel-eyebrow">VORLAGEN & EINZELAUFTRÄGE</span>
                      <h2>Vorlagen & Nutzung</h2>
                    </div>
                    <div class="table-tools">
                      <v-btn-toggle v-model="taskCategory" mandatory dense class="metric-toggle">
                        <v-btn small value="all">Alle</v-btn>
                        <v-btn small value="amtli">Ämtli</v-btn>
                        <v-btn small value="task">Aufgaben</v-btn>
                        <v-btn small value="unknown">Unklar</v-btn>
                      </v-btn-toggle>
                      <v-text-field
                        v-model="taskSearch"
                        dense
                        outlined
                        hide-details
                        clearable
                        prepend-inner-icon="mdi-magnify"
                        label="Suchen"
                        class="table-search table-search--small" />
                    </div>
                  </div>
                  <v-data-table
                    :dark="isDark"
                    :headers="templateHeaders"
                    :items="filteredTemplateStats"
                    :items-per-page="15"
                    class="insights-table"
                    no-data-text="Keine passenden Arbeitsaufträge">
                    <template v-slot:item.title="{ item }">
                      <div class="primary-cell">
                        <span class="type-icon" :class="item.isTrainerpoint ? 'type-icon--trainerpoint' : `type-icon--${item.category}`">
                          <v-icon size="17">{{ item.isTrainerpoint ? "mdi-account-group-outline" : categoryIcon(item.category) }}</v-icon>
                        </span>
                        <div>
                          <strong>{{ item.title }}</strong
                          ><small>{{ frequencyLabel(item.frequency) }} · {{ item.duration || "–" }} Min.</small>
                        </div>
                      </div>
                    </template>
                    <template v-slot:item.category="{ item }">
                      <span class="category-chip" :class="`category-chip--${item.category}`">{{ categoryLabel(item.category) }}</span>
                    </template>
                    <template v-slot:item.calendarStatus="{ item }">
                      <span class="calendar-status" :class="item.planned ? 'calendar-status--planned' : 'calendar-status--missing'">
                        <v-icon x-small>{{ item.planned ? "mdi-calendar-check-outline" : "mdi-calendar-blank-outline" }}</v-icon>
                        {{ item.planned ? `${item.planned}× eingeplant` : "Im Zeitraum ohne Eintrag" }}
                      </span>
                    </template>
                    <template v-slot:item.plannedHours="{ item }">{{ item.plannedHours }}h</template>
                  </v-data-table>
                  <div v-if="totals.unclassifiedWorkItems && totals.unclassifiedWorkItems.total" class="data-note">
                    <v-icon size="18">mdi-information-outline</v-icon>
                    <span>
                      {{ totals.unclassifiedWorkItems.total }} ältere oder kopierte Einträge besitzen keine Vorlage. Sie werden bewusst als
                      <strong>nicht klassifiziert</strong> ausgewiesen und nicht künstlich als Ämtli oder Aufgabe geraten.
                    </span>
                  </div>
                </article>
              </div>
            </div>
          </v-tab-item>

          <v-tab-item>
            <div class="tab-content">
              <div class="team-summary-grid">
                <article v-for="card in teamSummaryCards" :key="card.key" class="team-summary-card" :style="{ '--team-card-color': card.color }">
                  <span class="team-summary-icon">
                    <v-icon :color="card.color" size="21">{{ card.icon }}</v-icon>
                  </span>
                  <div>
                    <small>{{ card.label }}</small>
                    <strong>{{ card.value }}</strong>
                    <span>{{ card.detail }}</span>
                  </div>
                </article>
              </div>

              <div class="content-grid content-grid--half">
                <article class="panel">
                  <div class="panel-heading">
                    <div>
                      <span class="panel-eyebrow">ZEITVERTEILUNG</span>
                      <h2>Nettozeit und Kalenderbindung</h2>
                    </div>
                    <span class="panel-context">Bis zu 14 Mitarbeitende nach Nettozeit</span>
                  </div>
                  <apexchart type="bar" :height="teamCapacityChartHeight" :options="teamCapacityOptions" :series="teamCapacitySeries" />
                </article>

                <article class="panel">
                  <div class="panel-heading">
                    <div>
                      <span class="panel-eyebrow">EINGEPLANTE ZEIT</span>
                      <h2>Kundentermine, Trainerpoint, Ämtli & Aufgaben</h2>
                    </div>
                  </div>
                  <apexchart type="bar" :height="teamCapacityChartHeight" :options="teamWorkloadOptions" :series="teamWorkloadSeries" />
                </article>

                <article class="panel">
                  <div class="panel-heading">
                    <div>
                      <span class="panel-eyebrow">TEAM-BALANCE</span>
                      <h2>Verteilung der Kalenderbindung</h2>
                    </div>
                  </div>
                  <apexchart type="donut" height="330" :options="teamBalanceOptions" :series="teamBalanceSeries" />
                  <p class="analysis-caption">
                    Die Kalenderbindung zeigt, wie viel Netto-Schichtzeit durch Termine oder Arbeitsaufträge belegt ist. Sie ist keine
                    Leistungsbewertung.
                  </p>
                </article>

                <article class="panel">
                  <div class="panel-heading">
                    <div>
                      <span class="panel-eyebrow">PERSONALDECKUNG</span>
                      <h2>Ø Mitarbeitende je Zeitfenster</h2>
                    </div>
                  </div>
                  <apexchart type="heatmap" height="330" :options="staffingHeatmapOptions" :series="staffingHeatmapSeries" />
                </article>

                <article class="panel panel--full">
                  <div class="panel-heading panel-heading--wrap">
                    <div>
                      <span class="panel-eyebrow">TEAMDETAILS</span>
                      <h2>Mitarbeitende im Detail</h2>
                    </div>
                    <v-text-field
                      v-model="employeeSearch"
                      dense
                      outlined
                      hide-details
                      clearable
                      prepend-inner-icon="mdi-account-search-outline"
                      label="Mitarbeiter suchen"
                      class="table-search" />
                  </div>
                  <v-data-table
                    :dark="isDark"
                    :headers="employeeHeaders"
                    :items="filteredEmployees"
                    :items-per-page="20"
                    class="insights-table clickable-table"
                    no-data-text="Keine Mitarbeiterdaten"
                    @click:row="openEmployee">
                    <template v-slot:item.name="{ item }">
                      <div class="primary-cell employee-cell">
                        <v-avatar size="34">
                          <img :src="employeeImage(item)" :alt="item.name" />
                        </v-avatar>
                        <div>
                          <strong>{{ item.name }}</strong>
                          <small>
                            {{ item.shifts }} Schichten · {{ item.customerCareHours || 0 }}h Kundenbetreuung
                            <span v-if="item.sickHours"> · {{ item.sickHours }}h krank</span>
                          </small>
                        </div>
                      </div>
                    </template>
                    <template v-slot:item.workHours="{ item }">
                      <strong>{{ item.workHours }}h</strong>
                    </template>
                    <template v-slot:item.customerCareRate="{ item }">
                      <div class="rate-with-bar">
                        <strong>{{ item.customerCareRate || 0 }}%</strong>
                        <span><i :style="{ width: `${Math.min(item.customerCareRate || 0, 100)}%` }"></i></span>
                      </div>
                    </template>
                    <template v-slot:item.customerCareHours="{ item }"
                      ><strong>{{ item.customerCareHours || 0 }}h</strong></template
                    >
                    <template v-slot:item.feedbackAverage="{ item }">
                      <span v-if="item.feedbackRatings" class="feedback-table-score feedback-table-score--clickable">
                        <v-icon size="15" color="#f5b942">mdi-star</v-icon>
                        <strong>{{ item.feedbackAverage.toFixed(1) }}</strong>
                        <small>{{ item.feedbackRatings }}×</small>
                      </span>
                      <span v-else class="muted-value">–</span>
                    </template>
                    <template v-slot:item.customerAppointmentHours="{ item }">{{ item.customerAppointmentHours || 0 }}h</template>
                    <template v-slot:item.trainerpointHours="{ item }">{{ item.trainerpointHours || 0 }}h</template>
                    <template v-slot:item.amtliRate="{ item }">
                      <span class="time-share time-share--amtli">{{ item.amtliHours }}h · {{ item.amtliRate }}%</span>
                    </template>
                    <template v-slot:item.taskRate="{ item }">
                      <span class="time-share time-share--task">{{ item.taskHours }}h · {{ item.taskRate }}%</span>
                    </template>
                    <template v-slot:item.unboundHours="{ item }">
                      <span class="hours-pill">{{ item.unboundHours }}h</span>
                    </template>
                    <template v-slot:item.workItemMix="{ item }">
                      <div class="work-item-mix">
                        <span><v-icon x-small>mdi-broom</v-icon>{{ item.amtli }}</span>
                        <span>
                          <v-icon x-small>mdi-clipboard-text-outline</v-icon>
                          {{ Number(item.tasks || 0) + Number(item.infoItems || 0) + Number(item.unclassified || 0) }}
                        </span>
                      </div>
                    </template>
                    <template v-slot:item.noShows="{ item }">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <span class="no-show-count" v-bind="attrs" v-on="on">
                            {{ item.noShows || 0 }}
                          </span>
                        </template>
                        <span>Nicht erschienene Kundentermine im ausgewählten Zeitraum</span>
                      </v-tooltip>
                    </template>
                    <template v-slot:item.alerts="{ item }">
                      <div class="alert-badges">
                        <v-tooltip v-if="item.outsideShiftAppointments" bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <span class="alert-badge alert-badge--orange" v-bind="attrs" v-on="on">
                              <v-icon x-small>mdi-calendar-alert</v-icon>{{ item.outsideShiftAppointments }}
                            </span>
                          </template>
                          <span>Termine in diesem Center nicht vollständig durch Netto-Schicht gedeckt</span>
                        </v-tooltip>
                        <v-tooltip v-if="item.doubleBookings" bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <span class="alert-badge alert-badge--red" v-bind="attrs" v-on="on">
                              <v-icon x-small>mdi-calendar-multiple</v-icon>{{ item.doubleBookings }}
                            </span>
                          </template>
                          <span>Termine mit Parallelbelegung</span>
                        </v-tooltip>
                        <v-tooltip v-if="item.taskAppointmentConflicts" bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <span class="alert-badge alert-badge--violet" v-bind="attrs" v-on="on">
                              <v-icon x-small>mdi-clipboard-alert-outline</v-icon>{{ item.taskAppointmentConflicts }}
                            </span>
                          </template>
                          <span>Arbeitsaufträge parallel zu Terminen</span>
                        </v-tooltip>
                        <v-tooltip v-if="item.outsideShiftWorkItems" bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <span class="alert-badge alert-badge--orange" v-bind="attrs" v-on="on">
                              <v-icon x-small>mdi-clipboard-clock-outline</v-icon>{{ item.outsideShiftWorkItems }}
                            </span>
                          </template>
                          <span>Arbeitsaufträge in diesem Center nicht vollständig durch Netto-Schicht gedeckt</span>
                        </v-tooltip>
                        <span
                          v-if="
                            !item.outsideShiftAppointments && !item.outsideShiftWorkItems && !item.doubleBookings && !item.taskAppointmentConflicts
                          "
                          class="clean-badge">
                          <v-icon x-small>mdi-check</v-icon>sauber
                        </span>
                      </div>
                    </template>
                  </v-data-table>
                </article>
              </div>
            </div>
          </v-tab-item>

          <v-tab-item>
            <div class="tab-content">
              <div class="content-grid content-grid--half">
                <article class="panel">
                  <div class="panel-heading">
                    <div>
                      <span class="panel-eyebrow">CENTER-VERGLEICH</span>
                      <h2>Volumen und Auslastung</h2>
                    </div>
                  </div>
                  <apexchart type="bar" height="340" :options="centerComparisonOptions" :series="centerComparisonSeries" />
                </article>

                <article class="panel">
                  <div class="panel-heading">
                    <div>
                      <span class="panel-eyebrow">SCHICHTMIX</span>
                      <h2>Welche Schichten geplant wurden</h2>
                    </div>
                  </div>
                  <apexchart type="bar" height="340" :options="shiftMixOptions" :series="shiftMixSeries" />
                </article>

                <article v-if="canViewCustomerFeedback" class="panel panel--full center-feedback-panel">
                  <div class="panel-heading panel-heading--wrap">
                    <div>
                      <span class="panel-eyebrow">KUNDENFEEDBACK</span>
                      <h2>Bewertung und Rücklauf nach Center</h2>
                    </div>
                  </div>
                  <apexchart type="line" height="320" :options="centerFeedbackOptions" :series="centerFeedbackSeries" />
                </article>

                <article class="panel panel--full">
                  <div class="panel-heading">
                    <div>
                      <span class="panel-eyebrow">CENTERDETAILS</span>
                      <h2>Ausgewählte Standorte im Vergleich</h2>
                    </div>
                  </div>
                  <v-data-table
                    :dark="isDark"
                    :headers="centerHeaders"
                    :items="centerRows"
                    :items-per-page="-1"
                    class="insights-table"
                    hide-default-footer>
                    <template v-slot:item.name="{ item }">
                      <div class="primary-cell">
                        <span class="type-icon type-icon--center"><v-icon size="17">mdi-store-marker-outline</v-icon></span>
                        <div>
                          <strong>{{ item.name }}</strong>
                          <small
                            >{{ item.shifts }} Schichten · {{ item.workHours }}h verfügbar<span v-if="item.sickHours">
                              · {{ item.sickHours }}h krank</span
                            ></small
                          >
                        </div>
                      </div>
                    </template>
                    <template v-slot:item.utilizationRate="{ item }">
                      <rate-pill :value="item.utilizationRate" positive />
                    </template>
                    <template v-slot:item.noShowRate="{ item }">
                      <rate-pill :value="item.noShowRate" inverse />
                    </template>
                    <template v-slot:item.feedbackAverage="{ item }">
                      <span v-if="item.feedbackRatings" class="feedback-table-score">
                        <v-icon size="15" color="#f5b942">mdi-star</v-icon>
                        <strong>{{ item.feedbackAverage.toFixed(1) }}</strong>
                        <small>({{ item.feedbackRatings }})</small>
                      </span>
                      <span v-else class="muted-value">–</span>
                    </template>
                    <template v-slot:item.feedbackResponseRate="{ item }">
                      <span v-if="item.customerFeedback" class="feedback-response-pill">{{ item.feedbackResponseRate }}%</span>
                      <span v-else class="muted-value">–</span>
                    </template>
                  </v-data-table>
                </article>

                <article class="panel panel--full">
                  <div class="panel-heading panel-heading--wrap">
                    <div>
                      <span class="panel-eyebrow">PRÜFBARE EINZELDATEN</span>
                      <h2>Termine, Ämtli und Aufgaben</h2>
                    </div>
                    <div class="table-tools">
                      <v-select
                        v-model="rawCategory"
                        :items="rawCategoryOptions"
                        dense
                        outlined
                        hide-details
                        label="Kategorie"
                        class="category-select" />
                      <v-text-field
                        v-model="rawSearch"
                        dense
                        outlined
                        hide-details
                        clearable
                        prepend-inner-icon="mdi-magnify"
                        label="Einträge durchsuchen"
                        class="table-search" />
                    </div>
                  </div>
                  <v-data-table
                    :dark="isDark"
                    :headers="rawHeaders"
                    :items="filteredRawRows"
                    :items-per-page="20"
                    class="insights-table"
                    no-data-text="Keine passenden Einträge">
                    <template v-slot:item.entryType="{ item }">
                      <span class="category-chip" :class="`category-chip--${item.category}`">{{ rawTypeLabel(item) }}</span>
                    </template>
                    <template v-slot:item.date="{ item }">
                      <strong>{{ formatShortDate(item.date) }}</strong>
                    </template>
                  </v-data-table>
                  <div class="export-footer">
                    <div>
                      <v-icon size="20">mdi-shield-check-outline</v-icon>
                      <span>Export enthält keine Notizen, Kontaktdaten oder Telefonnummern.</span>
                    </div>
                    <v-btn depressed color="#16a765" dark class="text-none font-weight-bold" @click="exportWorkbook">
                      <v-icon left small>mdi-file-excel-outline</v-icon>Komplette Statistik exportieren
                    </v-btn>
                  </div>
                </article>
              </div>
            </div>
          </v-tab-item>

          <v-tab-item>
            <div class="tab-content">
              <template v-if="traffic.available">
                <div class="mini-kpis mini-kpis--traffic">
                  <div v-for="highlight in trafficHighlights" :key="highlight.key" :style="{ '--highlight-color': highlight.color }">
                    <span>{{ highlight.label }}</span>
                    <strong :class="{ 'mini-kpi-value--text': highlight.textValue }">{{ highlight.value }}</strong>
                    <small>{{ highlight.detail }}</small>
                    <v-icon :color="highlight.color">{{ highlight.icon }}</v-icon>
                  </div>
                </div>

                <div class="content-grid content-grid--half">
                  <article class="panel panel--full">
                    <div class="panel-heading">
                      <div>
                        <span class="panel-eyebrow">NACHFRAGE & PLANUNG</span>
                        <h2>Besuche, Kundentermine und Trainerpoint</h2>
                      </div>
                      <span class="panel-context panel-context--interactive">
                        <v-icon size="16">mdi-cursor-default-click-outline</v-icon>
                        Tag anklicken
                      </span>
                    </div>
                    <apexchart class="traffic-click-chart" type="line" height="350" :options="trafficDailyOptions" :series="trafficDailySeries" />
                  </article>

                  <article class="panel">
                    <div class="panel-heading">
                      <div>
                        <span class="panel-eyebrow">TAGESVERLAUF</span>
                        <h2>Wann die Kunden kommen</h2>
                      </div>
                    </div>
                    <apexchart type="line" height="330" :options="trafficHourlyOptions" :series="trafficHourlySeries" />
                  </article>

                  <article class="panel">
                    <div class="panel-heading">
                      <div>
                        <span class="panel-eyebrow">WOCHENMUSTER</span>
                        <h2>Besuche je Wochentag</h2>
                      </div>
                    </div>
                    <apexchart type="line" height="330" :options="trafficWeekdayOptions" :series="trafficWeekdaySeries" />
                  </article>

                  <article class="panel panel--full">
                    <div class="panel-heading">
                      <div>
                        <span class="panel-eyebrow">CENTER-VERGLEICH</span>
                        <h2>Kundenaufkommen und Betreuungsangebot</h2>
                      </div>
                    </div>
                    <v-data-table
                      :dark="isDark"
                      :headers="trafficCenterHeaders"
                      :items="traffic.centers || []"
                      :items-per-page="-1"
                      class="insights-table"
                      hide-default-footer>
                      <template v-slot:item.name="{ item }">
                        <div class="primary-cell">
                          <span class="type-icon type-icon--center">
                            <v-icon size="17">mdi-store-marker-outline</v-icon>
                          </span>
                          <div>
                            <strong>{{ item.name }}</strong>
                            <small>
                              Mitgliederstand
                              {{ item.memberSnapshotDate ? formatShortDate(item.memberSnapshotDate) : "nicht verfügbar" }}
                            </small>
                          </div>
                        </div>
                      </template>
                      <template v-slot:item.checkins="{ item }"
                        ><strong>{{ item.checkins || 0 }}</strong></template
                      >
                      <template v-slot:item.activeMembers="{ item }">{{ item.activeMembers || "–" }}</template>
                      <template v-slot:item.visitsPerMember="{ item }">{{ item.visitsPerMember || 0 }}</template>
                      <template v-slot:item.customerAppointmentHoursPer100Visits="{ item }">
                        <strong>{{ item.customerAppointmentHoursPer100Visits || 0 }}h</strong>
                      </template>
                      <template v-slot:item.trainerpointHoursPer100Visits="{ item }">
                        <strong>{{ item.trainerpointHoursPer100Visits || 0 }}h</strong>
                      </template>
                    </v-data-table>
                  </article>

                  <article class="panel panel--full traffic-method">
                    <v-icon color="#2587e8">mdi-information-outline</v-icon>
                    <div>
                      <strong>So sind die Werte zu verstehen</strong>
                      <p>
                        Ein Besuch entspricht einem in der täglichen Check-in-Statistik erfassten Kunden. „Kundentermine je 100 Besuche“ und
                        „Trainerpoint je 100 Besuche“ setzen die beiden Kalenderbereiche getrennt ins Verhältnis zum Kundenaufkommen. Die Werte zeigen
                        die eingeplanten Stunden – nicht, dass jeder einzelne Besucher persönlich betreut wurde.
                      </p>
                    </div>
                  </article>
                </div>
              </template>

              <div v-else class="state-panel traffic-empty">
                <v-icon color="#2587e8" size="36">mdi-account-clock-outline</v-icon>
                <div>
                  <strong>Für diesen Zeitraum liegen keine Check-in-Daten vor</strong>
                  <span>Kalenderplanung und Mitgliederstand bleiben weiterhin vollständig auswertbar.</span>
                </div>
              </div>
            </div>
          </v-tab-item>
        </v-tabs-items>
      </div>
    </template>

    <div v-if="loading && data" class="refresh-overlay">
      <v-progress-circular indeterminate color="#20b26b" size="34" width="4" />
    </div>

    <v-dialog
      v-if="canViewCustomerFeedback"
      v-model="allFeedbackDialog"
      max-width="1180"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      overlay-color="#020617"
      :overlay-opacity="0.88"
      scrollable>
      <v-card class="employee-dialog feedback-all-dialog" :class="{ 'employee-dialog--dark': isDark }" :dark="isDark">
        <div class="employee-dialog-header">
          <div class="feedback-command-title">
            <span class="feedback-command-icon"><v-icon color="white">mdi-star-shooting-outline</v-icon></span>
            <div>
              <span class="panel-eyebrow">KUNDENFEEDBACK</span>
              <h2>Alle Kundenbewertungen</h2>
              <p>{{ feedbackSummary.ratings }} Bewertungen · {{ rangeLabel }}</p>
            </div>
          </div>
          <v-btn icon aria-label="Alle Bewertungen schließen" @click="allFeedbackDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>
        <v-card-text class="feedback-all-dialog__body">
          <v-data-iterator
            :dark="isDark"
            :items="feedbackAll"
            :items-per-page="24"
            :footer-props="{ 'items-per-page-options': [12, 24, 48, -1], 'items-per-page-text': 'Bewertungen pro Seite' }"
            class="feedback-all-iterator">
            <template v-slot:default="{ items }">
              <div class="feedback-feed feedback-feed--all">
                <div
                  v-for="feedback in items"
                  :key="`all-${feedback.appointmentId}-${feedback.customerId || 'customer'}-${
                    feedback.createdAt || feedback.updatedAt || feedback.stars
                  }`"
                  class="feedback-feed-card"
                  :class="{ 'feedback-feed-card--attention': feedback.stars <= 2 }">
                  <div class="feedback-feed-topline">
                    <span
                      ><v-icon size="16" color="#f5b942">mdi-star</v-icon><strong>{{ feedback.stars }}.0</strong></span
                    >
                    <small>{{ formatShortDate(feedback.date) }}</small>
                  </div>
                  <div class="feedback-customer">
                    <v-icon size="16">mdi-account-circle-outline</v-icon><strong>{{ feedback.customerName || "Unbekannter Kunde" }}</strong>
                  </div>
                  <p :class="{ 'feedback-comment--empty': !feedback.comment }">{{ feedback.comment || "Bewertung ohne Kommentar" }}</p>
                  <div v-if="feedback.tags && feedback.tags.length" class="feedback-feed-tags">
                    <span v-for="tag in feedback.tags" :key="tag">{{ feedbackTagLabel(tag) }}</span>
                  </div>
                  <div class="feedback-feed-meta">
                    <span><v-icon size="14">mdi-calendar-blank-outline</v-icon>{{ feedback.title }}</span>
                    <span><v-icon size="14">mdi-account-tie-outline</v-icon>{{ feedbackEmployeeName(feedback.employeeId) }}</span>
                    <span><v-icon size="14">mdi-map-marker-outline</v-icon>{{ feedbackCenterName(feedback.studioId) }}</span>
                  </div>
                </div>
              </div>
            </template>
          </v-data-iterator>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="employeeDialog"
      max-width="1320"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      overlay-color="#020617"
      :overlay-opacity="0.88"
      scrollable>
      <v-card v-if="employeeDetail" class="employee-dialog employee-dialog--v2" :class="{ 'employee-dialog--dark': isDark }" :dark="isDark">
        <div class="employee-dialog-header">
          <div class="employee-dialog-person">
            <v-avatar size="58"><img :src="employeeImage(employeeDetail)" :alt="employeeDetail.name" /></v-avatar>
            <div>
              <span>MITARBEITER-ANALYSE</span>
              <h2>{{ employeeDetail.name }}</h2>
              <small>{{ rangeLabel }} · {{ employeeDetail.workDays || 0 }} Einsatztage · {{ employeeDetail.shifts }} Schichten</small>
            </div>
          </div>
          <div class="employee-dialog-focus">
            <span>EINGEPLANTE KUNDENBETREUUNG</span>
            <strong>{{ employeeDetail.customerCareRate || 0 }}%</strong>
            <small>{{ employeeDetail.customerCareHours || 0 }}h in Netto-Schichten</small>
          </div>
          <v-btn icon @click="employeeDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <v-tabs v-model="employeeDialogTab" :dark="isDark" background-color="transparent" color="#16a765" class="employee-dialog-tabs" show-arrows>
          <v-tab><v-icon left small>mdi-account-heart-outline</v-icon>Kunden & Termine</v-tab>
          <v-tab><v-icon left small>mdi-clock-check-outline</v-icon>Einsatz & Zeit</v-tab>
          <v-tab><v-icon left small>mdi-format-list-bulleted</v-icon>Einträge</v-tab>
          <v-tab v-if="canViewCustomerFeedback"><v-icon left small>mdi-star-circle-outline</v-icon>Kundenfeedback</v-tab>
        </v-tabs>

        <v-card-text class="employee-dialog-body">
          <v-tabs-items v-model="employeeDialogTab" :dark="isDark" class="employee-dialog-tab-items">
            <v-tab-item>
              <div v-if="employeeDialog && employeeDialogTab === 0" class="employee-dialog-pane">
                <section class="employee-customer-hero">
                  <div class="employee-care-chart">
                    <apexchart type="radialBar" height="250" :options="selectedEmployeeCareOptions" :series="selectedEmployeeCareSeries" />
                    <p>Planungswert aus aktiven Kundenterminen und Trainerpoint innerhalb der Netto-Schicht.</p>
                  </div>
                  <div class="employee-customer-kpis">
                    <div class="employee-customer-kpi--primary">
                      <span><v-icon size="18">mdi-account-heart-outline</v-icon>Kundenbetreuung</span>
                      <strong>{{ employeeDetail.customerCareHours || 0 }}h</strong>
                      <small>{{ employeeDetail.customerCareRate || 0 }}% der Netto-Schichtzeit</small>
                    </div>
                    <div>
                      <span><v-icon size="18">mdi-calendar-account-outline</v-icon>Aktive Kundentermine</span>
                      <strong>{{ employeeDetail.customerAppointmentHours || 0 }}h</strong>
                      <small>{{ employeeDetail.activeCustomerAppointments || 0 }} Termine innerhalb der Schichten</small>
                    </div>
                    <div class="employee-customer-kpi--trainerpoint">
                      <span><v-icon size="18">mdi-account-group-outline</v-icon>Trainerpoint</span>
                      <strong>{{ employeeDetail.trainerpointHours || 0 }}h</strong>
                      <small>{{ employeeDetail.trainerpointEntries || 0 }} Einsätze auf der Fläche</small>
                    </div>
                    <div class="employee-customer-kpi--danger">
                      <span><v-icon size="18">mdi-account-off-outline</v-icon>Nicht gekommen</span>
                      <strong>{{ employeeDetail.noShows || 0 }}</strong>
                      <small>{{ employeeDetail.noShowRate || 0 }}% der relevanten Termine</small>
                    </div>
                  </div>
                </section>

                <div class="employee-analysis-grid">
                  <article class="employee-insight-panel">
                    <div class="employee-section-heading">
                      <div>
                        <span>TERMINARTEN</span>
                        <h3>Welche Kundenkontakte geplant sind</h3>
                      </div>
                    </div>
                    <apexchart
                      type="bar"
                      height="310"
                      :options="selectedEmployeeAppointmentTypeOptions"
                      :series="selectedEmployeeAppointmentTypeSeries" />
                  </article>
                  <article class="employee-insight-panel">
                    <div class="employee-section-heading">
                      <div>
                        <span>WOCHENMUSTER</span>
                        <h3>Kundentermine & Trainerpoint</h3>
                      </div>
                    </div>
                    <apexchart
                      type="bar"
                      height="310"
                      :options="selectedEmployeeCustomerWeekdayOptions"
                      :series="selectedEmployeeCustomerWeekdaySeries" />
                  </article>
                </div>

                <section class="employee-insight-panel">
                  <div class="employee-section-heading">
                    <div>
                      <span>TERMINDETAILS</span>
                      <h3>Terminarten der Person</h3>
                    </div>
                    <strong>
                      {{ employeeDetail.activeCustomerAppointments || 0 }} Termine · {{ employeeDetail.customerAppointmentHours || 0 }}h
                    </strong>
                  </div>
                  <v-data-table
                    :dark="isDark"
                    :headers="employeeAppointmentTypeHeaders"
                    :items="selectedEmployeeAppointmentTypes"
                    :items-per-page="8"
                    class="insights-table">
                    <template v-slot:item.title="{ item }"
                      ><strong>{{ translateAppointmentType(item.key, item.title) }}</strong></template
                    >
                    <template v-slot:item.activeCustomerHours="{ item }">{{ item.activeCustomerHours || 0 }}h</template>
                    <template v-slot:item.noShowRate="{ item }"><rate-pill :value="item.noShowRate" inverse /></template>
                  </v-data-table>
                </section>
              </div>
            </v-tab-item>

            <v-tab-item>
              <div v-if="employeeDialog && employeeDialogTab === 1" class="employee-dialog-pane">
                <div class="employee-detail-kpis employee-detail-kpis--compact">
                  <div>
                    <span><v-icon size="16">mdi-clock-check-outline</v-icon>Netto-Schichtzeit</span><strong>{{ employeeDetail.workHours }}h</strong
                    ><small>Pausen und Krankheit abgezogen</small>
                  </div>
                  <div>
                    <span><v-icon size="16">mdi-calendar-check-outline</v-icon>Kalendergebunden</span
                    ><strong>{{ employeeDetail.productiveHours }}h</strong><small>Ohne Doppelzählung</small>
                  </div>
                  <div>
                    <span><v-icon size="16">mdi-timer-sand-empty</v-icon>Noch nicht gebunden</span><strong>{{ employeeDetail.unboundHours }}h</strong
                    ><small>Nicht durch Kalendereinträge belegt</small>
                  </div>
                  <div>
                    <span><v-icon size="16">mdi-account-injury-outline</v-icon>Krankheitszeit</span
                    ><strong>{{ employeeDetail.sickHours || 0 }}h</strong><small>{{ employeeDetail.sickRate }}% der verfügbaren Zeit</small>
                  </div>
                </div>
                <section class="employee-capacity-panel">
                  <div class="employee-section-heading">
                    <div>
                      <span>ZEITVERTEILUNG</span>
                      <h3>Was tatsächlich im Kalender eingeplant ist</h3>
                    </div>
                    <strong>{{ employeeDetail.productiveHours }}h / {{ employeeDetail.workHours }}h</strong>
                  </div>
                  <div class="employee-capacity-bar">
                    <span
                      v-for="part in selectedEmployeeCapacityParts"
                      :key="part.key"
                      :style="{ width: `${part.percent}%`, background: part.color }"></span>
                  </div>
                  <div class="employee-capacity-legend">
                    <div v-for="part in selectedEmployeeCapacityParts" :key="part.key">
                      <i :style="{ background: part.color }"></i><span>{{ part.label }}</span
                      ><strong>{{ part.hours }}h · {{ part.percent }}%</strong>
                    </div>
                  </div>
                  <p class="analysis-caption">
                    Ämtli und Aufgaben zählen nur, wenn sie im ausgewählten Zeitraum als Kalendereintrag vorhanden sind. „Nicht im Kalender belegt“
                    ist keine Pausenzeit, sondern noch nicht durch einen Eintrag gebundene Nettozeit.
                  </p>
                </section>
                <section class="employee-shift-profile">
                  <div class="employee-section-heading">
                    <div>
                      <span>SCHICHTPROFIL</span>
                      <h3>Verteilung der {{ employeeDetail.shifts }} Schichten</h3>
                    </div>
                    <strong>{{ employeeDetail.weekendWorkDays || 0 }} Wochenendtage</strong>
                  </div>
                  <div class="employee-shift-mix">
                    <div v-for="shift in selectedEmployeeShiftMix" :key="shift.key" :class="{ 'employee-shift-mix--empty': !shift.count }">
                      <span :style="{ background: `${shift.color}1f`, color: shift.color }"
                        ><v-icon size="17" :color="shift.color">{{ shift.icon }}</v-icon></span
                      >
                      <div>
                        <small>{{ shift.label }}</small
                        ><strong>{{ shift.count }}</strong>
                      </div>
                    </div>
                  </div>
                </section>
                <div v-if="selectedEmployeeWarnings.length" class="dialog-warnings">
                  <div v-for="warning in selectedEmployeeWarnings" :key="warning.text">
                    <v-icon :color="warning.color" size="18">{{ warning.icon }}</v-icon
                    ><span>{{ warning.text }}</span>
                  </div>
                </div>
                <article class="employee-insight-panel">
                  <div class="employee-section-heading">
                    <div>
                      <span>CENTER-VERTEILUNG</span>
                      <h3>Wo die Einträge geplant sind</h3>
                    </div>
                  </div>
                  <apexchart type="bar" height="285" :options="selectedEmployeeCenterOptions" :series="selectedEmployeeCenterSeries" />
                </article>
              </div>
            </v-tab-item>

            <v-tab-item>
              <div v-if="employeeDialog && employeeDialogTab === 2" class="employee-dialog-pane">
                <div class="employee-detail-toolbar">
                  <div>
                    <span>PRÜFBARE EINZELDATEN</span>
                    <h3>Einträge im Zeitraum</h3>
                  </div>
                  <v-btn-toggle v-model="employeeEntryFilter" mandatory dense class="metric-toggle">
                    <v-btn small value="appointment">Termine</v-btn>
                    <v-btn small value="work_item">Aufgaben</v-btn>
                    <v-btn small value="all">Alle</v-btn>
                  </v-btn-toggle>
                </div>
                <v-data-table
                  :dark="isDark"
                  :headers="employeeRawHeaders"
                  :items="selectedEmployeeDetailRows"
                  :items-per-page="15"
                  class="insights-table">
                  <template v-slot:item.entryType="{ item }"
                    ><span class="category-chip" :class="`category-chip--${item.category}`">{{ rawTypeLabel(item) }}</span></template
                  >
                  <template v-slot:item.date="{ item }">{{ formatShortDate(item.date) }}</template>
                </v-data-table>
              </div>
            </v-tab-item>

            <v-tab-item v-if="canViewCustomerFeedback">
              <div v-if="employeeDialog && employeeDialogTab === 3" class="employee-dialog-pane employee-feedback-pane">
                <div class="employee-feedback-heading">
                  <div>
                    <span class="panel-eyebrow">KUNDENFEEDBACK</span>
                    <h3>Kundenfeedback zu {{ employeeDetail.name }}</h3>
                    <p>Bewertungen aus abgeschlossenen Terminen im ausgewählten Zeitraum.</p>
                  </div>
                </div>

                <template v-if="employeeFeedbackSummary.ratings">
                  <div class="employee-feedback-kpis">
                    <div>
                      <span>Ø Bewertung</span
                      ><strong><v-icon color="#f5b942">mdi-star</v-icon>{{ Number(employeeFeedbackSummary.average).toFixed(1) }}</strong
                      ><small>{{ employeeFeedbackSummary.ratings }} Bewertungen</small>
                    </div>
                    <div>
                      <span>Rücklauf</span><strong>{{ employeeFeedbackSummary.responseRate }}%</strong
                      ><small>{{ employeeFeedbackSummary.ratings }} von {{ employeeFeedbackSummary.eligible }}</small>
                    </div>
                    <div>
                      <span>Positiv</span><strong>{{ employeeFeedbackSummary.favorableRate }}%</strong><small>4 oder 5 Sterne</small>
                    </div>
                    <div>
                      <span>Gesprächsbedarf</span><strong>{{ employeeFeedbackSummary.critical }}</strong
                      ><small>1 oder 2 Sterne</small>
                    </div>
                  </div>

                  <div class="employee-feedback-grid">
                    <article class="employee-insight-panel">
                      <div class="employee-section-heading">
                        <div>
                          <span>VERTEILUNG</span>
                          <h3>Bewertungen im Zeitraum</h3>
                        </div>
                      </div>
                      <div class="feedback-distribution feedback-distribution--employee">
                        <div v-for="row in employeeFeedbackSummary.distribution || []" :key="row.stars">
                          <span>{{ row.stars }} <v-icon size="14" color="#f5b942">mdi-star</v-icon></span>
                          <div><i :style="{ width: `${row.percentage}%` }"></i></div>
                          <strong>{{ row.count }}</strong>
                        </div>
                      </div>
                    </article>

                    <article class="employee-insight-panel">
                      <div class="employee-section-heading">
                        <div>
                          <span>STÄRKEN</span>
                          <h3>Was Kunden hervorheben</h3>
                        </div>
                      </div>
                      <div v-if="employeeFeedbackTopTags.length" class="feedback-tag-cloud feedback-tag-cloud--employee">
                        <span v-for="tag in employeeFeedbackTopTags" :key="tag.key">
                          <v-icon size="14">mdi-check-decagram-outline</v-icon>{{ feedbackTagLabel(tag.key) }}<strong>{{ tag.count }}</strong>
                        </span>
                      </div>
                      <div v-else class="employee-feedback-soft-empty">Noch keine Stichworte ausgewählt.</div>
                    </article>
                  </div>

                  <section class="employee-insight-panel">
                    <div class="employee-section-heading">
                      <div>
                        <span>KUNDENSTIMMEN</span>
                        <h3>Rückmeldungen im Zeitraum</h3>
                      </div>
                      <strong>{{ employeeFeedbackSummary.comments || 0 }} Kommentare</strong>
                    </div>
                    <div class="feedback-feed feedback-feed--employee">
                      <div
                        v-for="feedback in employeeFeedbackRecent"
                        :key="`${feedback.appointmentId}-${feedback.customerId || 'customer'}-${
                          feedback.createdAt || feedback.updatedAt || feedback.stars
                        }`"
                        class="feedback-feed-card"
                        :class="{ 'feedback-feed-card--attention': feedback.stars <= 2 }">
                        <div class="feedback-feed-topline">
                          <span
                            ><v-icon size="16" color="#f5b942">mdi-star</v-icon><strong>{{ feedback.stars }}.0</strong></span
                          ><small>{{ formatShortDate(feedback.date) }}</small>
                        </div>
                        <div class="feedback-customer">
                          <v-icon size="16">mdi-account-circle-outline</v-icon><strong>{{ feedback.customerName || "Unbekannter Kunde" }}</strong>
                        </div>
                        <p :class="{ 'feedback-comment--empty': !feedback.comment }">{{ feedback.comment || "Bewertung ohne Kommentar" }}</p>
                        <div v-if="feedback.tags && feedback.tags.length" class="feedback-feed-tags">
                          <span v-for="tag in feedback.tags" :key="tag">{{ feedbackTagLabel(tag) }}</span>
                        </div>
                        <div class="feedback-feed-meta">
                          <span><v-icon size="14">mdi-calendar-blank-outline</v-icon>{{ feedback.title }}</span
                          ><span><v-icon size="14">mdi-map-marker-outline</v-icon>{{ feedbackCenterName(feedback.studioId) }}</span>
                        </div>
                      </div>
                    </div>
                  </section>
                </template>

                <div v-else class="feedback-empty-state feedback-empty-state--employee">
                  <span><v-icon size="40" color="#16a765">mdi-star-plus-outline</v-icon></span>
                  <div>
                    <strong>Noch keine Bewertungen im Zeitraum</strong>
                    <p>Für diese Person liegt aktuell noch kein Kundenfeedback aus abgeschlossenen Terminen vor.</p>
                  </div>
                </div>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-if="false" v-model="employeeDialog" max-width="1240" :fullscreen="$vuetify.breakpoint.smAndDown" scrollable>
      <v-card v-if="employeeDetail" class="employee-dialog" :dark="isDark">
        <div class="employee-dialog-header">
          <div class="employee-dialog-person">
            <v-avatar size="64"><img :src="employeeImage(employeeDetail)" :alt="employeeDetail.name" /></v-avatar>
            <div>
              <span>MITARBEITER-ANALYSE</span>
              <h2>{{ employeeDetail.name }}</h2>
              <small>
                {{ rangeLabel }} · {{ employeeDetail.workDays || 0 }} Einsatztage · {{ employeeDetail.shifts }} Schichten ·
                {{ employeeDetail.centerCount || 0 }} Center
              </small>
            </div>
          </div>
          <div class="employee-dialog-score">
            <div>
              <span>KALENDERBINDUNG</span>
              <strong>{{ employeeDetail.utilizationRate }}%</strong>
              <small>keine Leistungsbewertung</small>
            </div>
            <v-progress-circular :value="Math.min(employeeDetail.utilizationRate, 100)" :size="58" :width="6" color="#16a765">
              <v-icon size="22" color="#16a765">mdi-calendar-check-outline</v-icon>
            </v-progress-circular>
          </div>
          <v-btn icon @click="employeeDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>
        <v-card-text class="employee-dialog-body">
          <div class="employee-detail-kpis">
            <div>
              <span><v-icon size="16">mdi-clock-check-outline</v-icon>Netto-Schichtzeit</span>
              <strong>{{ employeeDetail.workHours }}h</strong>
              <small>Pausen und Krankheit bereits abgezogen</small>
            </div>
            <div>
              <span><v-icon size="16">mdi-calendar-check-outline</v-icon>Kalendergebunden</span>
              <strong>{{ employeeDetail.productiveHours }}h</strong>
              <small>Termine und Arbeitsaufträge ohne Doppelzählung</small>
            </div>
            <div>
              <span><v-icon size="16">mdi-timer-sand-empty</v-icon>Noch nicht gebunden</span>
              <strong>{{ employeeDetail.unboundHours }}h</strong>
              <small>Nicht durch Kalendereinträge belegt</small>
            </div>
            <div>
              <span><v-icon size="16">mdi-account-injury-outline</v-icon>Krankheitszeit</span>
              <strong>{{ employeeDetail.sickHours || 0 }}h</strong>
              <small>{{ employeeDetail.sickRate }}% von Netto- plus Krankheitszeit</small>
            </div>
            <div>
              <span><v-icon size="16">mdi-calendar-account-outline</v-icon>Termine</span>
              <strong>{{ employeeDetail.appointments }}</strong>
              <small>{{ employeeDetail.activeAppointments }} ohne Absage oder Nichterscheinen</small>
            </div>
            <div>
              <span><v-icon size="16">mdi-clipboard-text-outline</v-icon>Arbeitsaufträge</span>
              <strong>{{ employeeDetail.workItemCount }}</strong>
              <small>{{ employeeDetail.amtli }} Ämtli · {{ employeeDetail.tasks }} Aufgaben</small>
            </div>
            <div>
              <span><v-icon size="16">mdi-account-off-outline</v-icon>Nicht gekommen</span>
              <strong>{{ employeeDetail.noShows || 0 }}</strong>
              <small>Kundentermine im ausgewählten Zeitraum</small>
            </div>
            <div>
              <span><v-icon size="16">mdi-calendar-week-outline</v-icon>Einsatzprofil</span>
              <strong>{{ employeeDetail.workDays || 0 }} Tage</strong>
              <small>
                Ø {{ employeeDetail.averageWorkdayHours || 0 }}h · {{ employeeDetail.weekendWorkDays || 0 }} Wochenendtage ·
                {{ employeeDetail.pauseHours || 0 }}h Pausen
              </small>
            </div>
          </div>

          <section class="employee-capacity-panel">
            <div class="employee-section-heading">
              <div>
                <span>ZEITBILANZ</span>
                <h3>Wie die Netto-Schichtzeit belegt ist</h3>
              </div>
              <strong>{{ employeeDetail.productiveHours }}h / {{ employeeDetail.workHours }}h</strong>
            </div>
            <div class="employee-capacity-bar">
              <span
                v-for="part in selectedEmployeeCapacityParts"
                :key="part.key"
                :style="{ width: `${part.percent}%`, background: part.color }"></span>
            </div>
            <div class="employee-capacity-legend">
              <div v-for="part in selectedEmployeeCapacityParts" :key="part.key">
                <i :style="{ background: part.color }"></i>
                <span>{{ part.label }}</span>
                <strong>{{ part.hours }}h · {{ part.percent }}%</strong>
              </div>
            </div>
            <p class="analysis-caption">
              „Noch nicht gebunden“ bedeutet nur, dass die Zeit nicht durch einen Termin oder Arbeitsauftrag im Kalender belegt ist. Andere operative
              Arbeiten können darin enthalten sein.
            </p>
          </section>

          <section class="employee-shift-profile">
            <div class="employee-section-heading">
              <div>
                <span>SCHICHTPROFIL</span>
                <h3>Verteilung der {{ employeeDetail.shifts }} Schichten</h3>
              </div>
              <strong>{{ employeeDetail.weekendWorkDays || 0 }} Wochenendtage</strong>
            </div>
            <div class="employee-shift-mix">
              <div v-for="shift in selectedEmployeeShiftMix" :key="shift.key" :class="{ 'employee-shift-mix--empty': !shift.count }">
                <span :style="{ background: `${shift.color}1f`, color: shift.color }">
                  <v-icon size="17" :color="shift.color">{{ shift.icon }}</v-icon>
                </span>
                <div>
                  <small>{{ shift.label }}</small>
                  <strong>{{ shift.count }}</strong>
                </div>
              </div>
            </div>
          </section>

          <div v-if="selectedEmployeeWarnings.length" class="dialog-warnings">
            <div v-for="warning in selectedEmployeeWarnings" :key="warning.text">
              <v-icon :color="warning.color" size="18">{{ warning.icon }}</v-icon>
              <span>{{ warning.text }}</span>
            </div>
          </div>

          <div class="employee-analysis-grid">
            <article class="employee-insight-panel">
              <div class="employee-section-heading">
                <div>
                  <span>WOCHENMUSTER</span>
                  <h3>Einträge nach Wochentag</h3>
                </div>
              </div>
              <apexchart type="bar" height="285" :options="selectedEmployeeWeekdayOptions" :series="selectedEmployeeWeekdaySeries" />
            </article>

            <article class="employee-insight-panel">
              <div class="employee-section-heading">
                <div>
                  <span>CENTER-VERTEILUNG</span>
                  <h3>Wo die Einträge geplant sind</h3>
                </div>
              </div>
              <apexchart type="bar" height="285" :options="selectedEmployeeCenterOptions" :series="selectedEmployeeCenterSeries" />
            </article>

            <article class="employee-insight-panel employee-insight-panel--full">
              <div class="employee-section-heading">
                <div>
                  <span>HÄUFIGSTE EINTRÄGE</span>
                  <h3>Was am häufigsten geplant wurde</h3>
                </div>
              </div>
              <div v-if="selectedEmployeeTopEntries.length" class="employee-top-entries">
                <div v-for="entry in selectedEmployeeTopEntries" :key="entry.key">
                  <span class="type-icon" :class="`type-icon--${entry.category}`">
                    <v-icon size="17">
                      {{ entry.entryType === "appointment" ? "mdi-calendar-account" : categoryIcon(entry.category) }}
                    </v-icon>
                  </span>
                  <div>
                    <strong>{{ entry.title }}</strong>
                    <small>{{ rawTypeLabel(entry) }} · {{ Number(entry.minutes / 60).toFixed(1) }}h</small>
                  </div>
                  <b>{{ entry.count }}×</b>
                </div>
              </div>
              <div v-else class="employee-empty-analysis">Keine Einträge im ausgewählten Zeitraum.</div>
            </article>
          </div>

          <div class="employee-section-heading employee-table-heading">
            <div>
              <span>PRÜFBARE EINZELDATEN</span>
              <h3>Einträge im Zeitraum</h3>
            </div>
            <strong>{{ selectedEmployeeRows.length }} Einträge</strong>
          </div>
          <v-data-table :dark="isDark" :headers="employeeRawHeaders" :items="selectedEmployeeRows" :items-per-page="10" class="insights-table">
            <template v-slot:item.entryType="{ item }">
              <span class="category-chip" :class="`category-chip--${item.category}`">{{ rawTypeLabel(item) }}</span>
            </template>
            <template v-slot:item.date="{ item }">{{ formatShortDate(item.date) }}</template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="trafficDayDialog"
      max-width="1180"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      overlay-color="#020617"
      :overlay-opacity="0.82"
      scrollable>
      <v-card v-if="selectedTrafficDay" class="traffic-day-dialog" :class="{ 'traffic-day-dialog--dark': isDark }" :dark="isDark">
        <div class="traffic-day-dialog__header">
          <div class="traffic-day-dialog__identity">
            <span class="traffic-day-dialog__icon">
              <v-icon color="#2587e8">mdi-chart-timeline-variant-shimmer</v-icon>
            </span>
            <div>
              <span class="panel-eyebrow">STUNDENDETAIL</span>
              <h2>{{ selectedTrafficDayTitle }}</h2>
              <p>Nachfrage und eingeplante Kundenkontakte im direkten Vergleich</p>
            </div>
          </div>
          <v-btn icon aria-label="Schliessen" @click="trafficDayDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="traffic-day-dialog__content">
          <div class="traffic-day-summary">
            <div v-for="highlight in selectedTrafficDayHighlights" :key="highlight.key" :style="{ '--day-color': highlight.color }">
              <span>{{ highlight.label }}</span>
              <strong>{{ highlight.value }}</strong>
              <small>{{ highlight.detail }}</small>
              <v-icon :color="highlight.color">{{ highlight.icon }}</v-icon>
            </div>
          </div>

          <article class="traffic-day-chart">
            <div class="panel-heading">
              <div>
                <span class="panel-eyebrow">STUNDE FÜR STUNDE</span>
                <h3>Wann Nachfrage und Planung zusammenkommen</h3>
              </div>
              <span class="panel-context">Planung in Mitarbeiterstunden</span>
            </div>
            <apexchart type="line" height="315" :options="trafficDayHourlyOptions" :series="trafficDayHourlySeries" />
          </article>

          <article class="traffic-day-table">
            <div class="panel-heading">
              <div>
                <span class="panel-eyebrow">GENAUE AUFLISTUNG</span>
                <h3>Werte je Stunde</h3>
              </div>
            </div>
            <v-data-table
              :dark="isDark"
              :headers="trafficDayHeaders"
              :items="selectedTrafficHourRows"
              :items-per-page="-1"
              class="insights-table traffic-hour-table"
              hide-default-footer
              no-data-text="Für diesen Tag liegen keine Stundenwerte vor.">
              <template v-slot:item.hour="{ item }">
                <strong>{{ trafficHourLabel(item.hour) }}</strong>
              </template>
              <template v-slot:item.checkins="{ item }">
                <strong class="traffic-hour-value traffic-hour-value--visits">{{ item.checkins || 0 }}</strong>
              </template>
              <template v-slot:item.staffing="{ item }">{{ item.staffing || 0 }}</template>
              <template v-slot:item.customerAppointmentStarts="{ item }">
                {{ item.customerAppointmentStarts || 0 }}
              </template>
              <template v-slot:item.customerAppointmentHours="{ item }">
                <strong>{{ Number(item.customerAppointmentHours || 0).toFixed(1) }}h</strong>
              </template>
              <template v-slot:item.trainerpointStarts="{ item }">{{ item.trainerpointStarts || 0 }}</template>
              <template v-slot:item.trainerpointHours="{ item }">
                <strong>{{ Number(item.trainerpointHours || 0).toFixed(1) }}h</strong>
              </template>
            </v-data-table>
          </article>

          <div class="traffic-day-note">
            <v-icon color="#2587e8" size="20">mdi-information-outline</v-icon>
            <span>
              Zeitwerte zeigen eingeplante Mitarbeiterstunden innerhalb der Schichten. Bei paralleler Betreuung kann deshalb in einer Stunde mehr als
              1,0h Planung entstehen.
            </span>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="customerDialog"
      max-width="1320"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      overlay-color="#020617"
      :overlay-opacity="0.86"
      scrollable>
      <v-card v-if="selectedCustomer" class="customer-dialog" :class="{ 'customer-dialog--dark': isDark }" :dark="isDark">
        <div class="customer-dialog__header">
          <div class="customer-dialog__identity">
            <span class="customer-dialog__avatar">
              {{
                String(selectedCustomer.name || "?")
                  .trim()
                  .charAt(0)
                  .toUpperCase()
              }}
            </span>
            <div>
              <span class="panel-eyebrow">KUNDENANALYSE</span>
              <h2>{{ selectedCustomer.name }}</h2>
              <p>
                {{ formatShortDate(selectedCustomer.firstDate) }}–{{ formatShortDate(selectedCustomer.lastDate) }} ·
                {{ selectedCustomer.hasAccount ? "mit Kundenkonto" : "ohne verknüpftes Kundenkonto" }}
              </p>
            </div>
          </div>
          <v-btn icon aria-label="Schliessen" @click="customerDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <v-card-text class="customer-dialog__content">
          <div class="customer-dialog-kpis">
            <div v-for="highlight in selectedCustomerHighlights" :key="highlight.key" :style="{ '--customer-color': highlight.color }">
              <v-icon :color="highlight.color">{{ highlight.icon }}</v-icon>
              <span>{{ highlight.label }}</span>
              <strong>{{ highlight.value }}</strong>
              <small>{{ highlight.detail }}</small>
            </div>
          </div>

          <div class="customer-dialog-grid">
            <article class="customer-dialog-panel">
              <div class="panel-heading">
                <div>
                  <span class="panel-eyebrow">BETREUUNGSTEAM</span>
                  <h3>Von wem die Termine betreut wurden</h3>
                </div>
              </div>
              <v-data-table
                :dark="isDark"
                :headers="customerEmployeeHeaders"
                :items="selectedCustomer.employees || []"
                :items-per-page="-1"
                class="insights-table"
                hide-default-footer>
                <template v-slot:item.minutes="{ item }">
                  <strong>{{ Number((item.minutes || 0) / 60).toFixed(1) }}h</strong>
                </template>
              </v-data-table>
            </article>

            <article class="customer-dialog-panel">
              <div class="panel-heading">
                <div>
                  <span class="panel-eyebrow">TERMINMIX</span>
                  <h3>Welche Leistungen genutzt wurden</h3>
                </div>
              </div>
              <v-data-table
                :dark="isDark"
                :headers="customerTypeHeaders"
                :items="selectedCustomer.types || []"
                :items-per-page="-1"
                class="insights-table"
                hide-default-footer>
                <template v-slot:item.title="{ item }">
                  <strong>{{ translateAppointmentType(item.key, item.title) }}</strong>
                </template>
                <template v-slot:item.minutes="{ item }">
                  <strong>{{ Number((item.minutes || 0) / 60).toFixed(1) }}h</strong>
                </template>
              </v-data-table>
            </article>

            <article class="customer-dialog-panel customer-dialog-panel--full">
              <div class="panel-heading">
                <div>
                  <span class="panel-eyebrow">CHRONOLOGIE</span>
                  <h3>Alle Termine im ausgewählten Zeitraum</h3>
                </div>
              </div>
              <v-data-table
                :dark="isDark"
                :headers="customerHistoryHeaders"
                :items="selectedCustomer.rows || []"
                :items-per-page="12"
                class="insights-table customer-history-table">
                <template v-slot:item.date="{ item }"
                  ><strong>{{ formatShortDate(item.date) }}</strong></template
                >
                <template v-slot:item.title="{ item }">
                  {{ translateAppointmentType(item.appointmentType, item.title) }}
                </template>
                <template v-slot:item.status="{ item }">
                  <span class="appointment-status" :class="`appointment-status--${appointmentStatusTone(item.status)}`">
                    {{ appointmentStatusLabel(item.status) }}
                  </span>
                </template>
                <template v-slot:item.description="{ item }">
                  <span v-if="item.description" class="appointment-note">{{ item.description }}</span>
                  <span v-else class="appointment-note appointment-note--empty">Keine Notiz</span>
                </template>
              </v-data-table>
            </article>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import api from "../../../api"

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"]
const WEEKDAYS_LONG = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"]

const APPOINTMENT_TYPE_LABELS = {
  trial: "Probetraining",
  intro: "Einführungstraining",
  followup: "Folgetermin",
  check: "Check-up",
  milon: "Milon-Training",
  nutrition: "Ernährungsberatung",
  consultation: "Beratungsgespräch",
  workout: "Training",
  trainingplan: "Trainingsplan",
  anamnese: "Anamnese / Check-up",
  new_contract: "Vertragserstellung",
  upgrade: "Vertrag / Upgrade",
  normal: "Normaler Termin",
  first: "Erstberatung",
  follow: "Folgetermin",
}

const RatePill = {
  functional: true,
  props: {
    value: { type: Number, default: 0 },
    positive: Boolean,
    inverse: Boolean,
  },
  render(h, context) {
    const value = Number(context.props.value || 0)
    let tone = "neutral"
    if (context.props.inverse) tone = value <= 5 ? "good" : value <= 12 ? "warning" : "danger"
    else if (context.props.positive) tone = value >= 80 ? "good" : value >= 55 ? "warning" : "danger"
    return h("span", { class: ["rate-pill", `rate-pill--${tone}`] }, `${value}%`)
  },
}

export default {
  name: "HomeDashboardWorkCalendarStatistics",
  components: { RatePill },
  props: {
    studios: { type: Array, required: true },
    datePickerRange: { type: Array, required: true },
    darkMode: { type: Boolean, default: true },
  },
  data() {
    return {
      data: null,
      loading: false,
      error: "",
      requestSerial: 0,
      loadedRequestKey: "",
      loadTimer: null,
      activeTab: 0,
      appointmentSearch: "",
      customerSearch: "",
      taskSearch: "",
      taskCategory: "all",
      workItemHeatmapMetric: "trainerpoint",
      employeeSearch: "",
      rawSearch: "",
      rawCategory: "all",
      employeeDialog: false,
      employeeDialogTab: 0,
      employeeEntryFilter: "appointment",
      selectedEmployee: null,
      allFeedbackDialog: false,
      trafficDayDialog: false,
      selectedTrafficDate: "",
      customerDialog: false,
      selectedCustomerKey: "",
    }
  },
  computed: {
    isDark() {
      return this.darkMode
    },
    requestKey() {
      const ids = (this.studios || []).map((studio) => String(studio?._id || studio || "")).sort()
      return `${ids.join(",")}|${(this.datePickerRange || []).join("|")}`
    },
    meta() {
      return this.data?.meta || { rangeDays: 0, studioCount: 0 }
    },
    totals() {
      return this.data?.totals || {}
    },
    canViewCustomerFeedback() {
      return this.meta?.permissions?.customerFeedback === true
    },
    customerFeedback() {
      return this.data?.customerFeedback || { summary: null, trend: [] }
    },
    feedbackSummary() {
      return (
        this.customerFeedback.summary || {
          eligible: 0,
          eligibleAppointments: 0,
          ratings: 0,
          ratedAppointments: 0,
          average: 0,
          responseRate: 0,
          favorableRate: 0,
          critical: 0,
          comments: 0,
          distribution: [],
          tags: [],
          feedback: [],
        }
      )
    },
    feedbackTrend() {
      return (this.customerFeedback.trend || []).filter((row) => Number(row.eligible || 0) > 0 || Number(row.ratings || 0) > 0)
    },
    feedbackTrendSeries() {
      return [
        { name: "Bewertungen", type: "column", data: this.feedbackTrend.map((row) => Number(row.ratings || 0)) },
        {
          name: "Ø Sterne",
          type: "line",
          data: this.feedbackTrend.map((row) => (Number(row.ratings || 0) ? Number(row.average || 0) : null)),
        },
      ]
    },
    feedbackTrendOptions() {
      return {
        ...this.chartBase(),
        colors: ["#16a765", "#f5b942"],
        stroke: { width: [0, 3], curve: "smooth" },
        markers: { size: [0, 4], strokeWidth: 0 },
        plotOptions: { bar: { borderRadius: 5, columnWidth: "56%" } },
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.feedbackTrend.map((row) => this.formatShortDate(row.date)),
          labels: { rotate: -45, style: { colors: this.chartText, fontSize: "11px" } },
        },
        yaxis: [
          {
            min: 0,
            title: { text: "Bewertungen", style: { color: this.chartText } },
            labels: { decimalsInFloat: 0, style: { colors: this.chartText } },
          },
          {
            min: 0,
            max: 5,
            tickAmount: 5,
            opposite: true,
            title: { text: "Ø Sterne", style: { color: this.chartText } },
            labels: { formatter: (value) => Number(value || 0).toFixed(1), style: { colors: this.chartText } },
          },
        ],
        grid: { borderColor: this.chartGrid, strokeDashArray: 4 },
        legend: { position: "top", labels: { colors: this.chartText } },
        tooltip: { shared: true, theme: this.isDark ? "dark" : "light" },
      }
    },
    feedbackDistribution() {
      return this.feedbackSummary.distribution || []
    },
    feedbackTopTags() {
      return (this.feedbackSummary.tags || []).slice(0, 6)
    },
    feedbackRecent() {
      return (this.feedbackSummary.feedback || []).slice(0, 12)
    },
    feedbackAll() {
      return this.feedbackSummary.feedback || []
    },
    feedbackKpis() {
      const feedback = this.feedbackSummary
      return [
        {
          key: "average",
          label: "Gesamteindruck",
          value: feedback.ratings ? `${Number(feedback.average).toFixed(1)} / 5` : "–",
          detail: `${feedback.ratings || 0} Bewertungen`,
          icon: "mdi-star-circle-outline",
          color: "#f5b942",
        },
        {
          key: "response",
          label: "Rücklauf",
          value: `${feedback.responseRate || 0}%`,
          detail: `${feedback.ratings || 0} von ${feedback.eligible || 0} möglichen Rückmeldungen`,
          icon: "mdi-message-star-outline",
          color: "#16a765",
        },
        {
          key: "positive",
          label: "Positiv",
          value: `${feedback.favorableRate || 0}%`,
          detail: "4 oder 5 Sterne",
          icon: "mdi-thumb-up-outline",
          color: "#20c9a0",
        },
        {
          key: "critical",
          label: "Gesprächsbedarf",
          value: feedback.critical || 0,
          detail: "1 oder 2 Sterne",
          icon: "mdi-alert-circle-outline",
          color: "#f59e0b",
        },
      ]
    },
    traffic() {
      return (
        this.data?.traffic || {
          available: false,
          checkins: 0,
          activeMembers: 0,
          daily: [],
          hourly: [],
          weekdays: [],
          centers: [],
        }
      )
    },
    selectedTrafficDay() {
      if (!this.selectedTrafficDate) return null
      return (this.traffic.daily || []).find((day) => day.date === this.selectedTrafficDate) || null
    },
    selectedTrafficDayTitle() {
      if (!this.selectedTrafficDate) return ""
      return this.$moment ? this.$moment(this.selectedTrafficDate, "YYYY-MM-DD").format("dddd, DD. MMMM YYYY") : this.selectedTrafficDate
    },
    selectedTrafficHourRows() {
      const rows = (this.selectedTrafficDay?.hours || []).map((row) => ({
        ...row,
        hour: Number(row.hour || 0),
      }))
      if (!rows.length) return []
      const activeRows = rows.filter(
        (row) =>
          Number(row.checkins || 0) +
            Number(row.staffing || 0) +
            Number(row.customerAppointmentMinutes || 0) +
            Number(row.trainerpointMinutes || 0) +
            Number(row.customerAppointmentStarts || 0) +
            Number(row.trainerpointStarts || 0) >
          0,
      )
      if (!activeRows.length) return rows.filter((row) => row.hour >= 6 && row.hour <= 22)
      const firstHour = Math.max(0, Math.min(...activeRows.map((row) => row.hour)) - 1)
      const lastHour = Math.min(23, Math.max(...activeRows.map((row) => row.hour)) + 1)
      return rows.filter((row) => row.hour >= firstHour && row.hour <= lastHour)
    },
    selectedTrafficDayPeak() {
      return this.selectedTrafficHourRows.reduce((peak, row) => (Number(row.checkins || 0) > Number(peak.checkins || 0) ? row : peak), {
        hour: null,
        checkins: 0,
      })
    },
    selectedTrafficDayHighlights() {
      const day = this.selectedTrafficDay || {}
      const rows = this.selectedTrafficHourRows
      const appointmentStarts = rows.reduce((sum, row) => sum + Number(row.customerAppointmentStarts || 0), 0)
      const trainerpointStarts = rows.reduce((sum, row) => sum + Number(row.trainerpointStarts || 0), 0)
      const maxStaffing = rows.reduce((max, row) => Math.max(max, Number(row.staffing || 0)), 0)
      return [
        {
          key: "visits",
          label: "Besuche",
          value: day.checkins || 0,
          detail:
            this.selectedTrafficDayPeak.hour === null
              ? "keine Besuchszeit erfasst"
              : `Spitze ${this.trafficHourLabel(this.selectedTrafficDayPeak.hour)} · ${this.selectedTrafficDayPeak.checkins}`,
          icon: "mdi-door-open",
          color: "#2587e8",
        },
        {
          key: "appointments",
          label: "Kundentermine",
          value: `${Number(day.customerAppointmentHours || 0).toFixed(1)}h`,
          detail: `${appointmentStarts} Starts`,
          icon: "mdi-calendar-account-outline",
          color: "#f59e0b",
        },
        {
          key: "trainerpoint",
          label: "Trainerpoint",
          value: `${Number(day.trainerpointHours || 0).toFixed(1)}h`,
          detail: `${trainerpointStarts} Einsätze`,
          icon: "mdi-account-group-outline",
          color: "#16a765",
        },
        {
          key: "staffing",
          label: "Besetzung",
          value: maxStaffing,
          detail: "max. gleichzeitig im Dienst",
          icon: "mdi-account-multiple-check-outline",
          color: "#7c3aed",
        },
      ]
    },
    trafficDayHourlySeries() {
      const rows = this.selectedTrafficHourRows
      return [
        { name: "Besuche", type: "column", data: rows.map((row) => Number(row.checkins || 0)) },
        {
          name: "Kundentermine",
          type: "line",
          data: rows.map((row) => Number(row.customerAppointmentHours || 0)),
        },
        {
          name: "Trainerpoint",
          type: "line",
          data: rows.map((row) => Number(row.trainerpointHours || 0)),
        },
      ]
    },
    trafficDayHourlyOptions() {
      const base = this.chartBase()
      return {
        ...base,
        colors: ["#2587e8", "#f59e0b", "#16a765"],
        stroke: { width: [0, 3, 3], curve: "smooth" },
        markers: { size: [0, 4, 4], strokeWidth: 0 },
        plotOptions: { bar: { borderRadius: 4, columnWidth: "66%" } },
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.selectedTrafficHourRows.map((row) => `${String(row.hour).padStart(2, "0")}:00`),
          labels: { rotate: -45, style: { colors: this.chartText, fontSize: "12px" } },
        },
        yaxis: [
          {
            seriesName: "Besuche",
            min: 0,
            title: { text: "Besuche", style: { color: this.chartText, fontSize: "12px" } },
            labels: { style: { colors: this.chartText, fontSize: "12px" } },
          },
          {
            seriesName: ["Kundentermine", "Trainerpoint"],
            min: 0,
            opposite: true,
            title: { text: "Mitarbeiterstunden", style: { color: this.chartText, fontSize: "12px" } },
            labels: {
              formatter: (value) => `${Number(value || 0).toFixed(1)}h`,
              style: { colors: this.chartText, fontSize: "12px" },
            },
          },
        ],
        grid: { borderColor: this.chartGrid, strokeDashArray: 4 },
        legend: { position: "top", labels: { colors: this.chartText }, fontSize: "12px" },
        tooltip: {
          shared: true,
          theme: this.isDark ? "dark" : "light",
          y: {
            formatter: (value, context) => (context.seriesIndex === 0 ? `${value} Besuche` : `${Number(value || 0).toFixed(1)}h`),
          },
        },
      }
    },
    trafficDayHeaders() {
      return [
        { text: "Stunde", value: "hour" },
        { text: "Besuche", value: "checkins", align: "end" },
        { text: "MA im Dienst", value: "staffing", align: "end" },
        { text: "Terminstarts", value: "customerAppointmentStarts", align: "end" },
        { text: "Terminzeit", value: "customerAppointmentHours", align: "end" },
        { text: "Trainerpoint-Starts", value: "trainerpointStarts", align: "end" },
        { text: "Trainerpoint-Zeit", value: "trainerpointHours", align: "end" },
      ]
    },
    memberSnapshotLabel() {
      const from = this.traffic.memberSnapshotFrom
      const to = this.traffic.memberSnapshotTo
      if (!from && !to) return "kein Mitgliederstand"
      if (!from || from === to) return `Stand ${this.formatShortDate(from || to)}`
      return `Stände ${this.formatShortDate(from)}–${this.formatShortDate(to)}`
    },
    trafficHighlights() {
      return [
        {
          key: "checkins",
          label: "Besuche",
          value: this.traffic.checkins || 0,
          detail: `Ø ${this.traffic.averageCheckinsPerDay || 0} pro Tag`,
          icon: "mdi-door-open",
          color: "#2587e8",
        },
        {
          key: "members",
          label: "Aktive Mitglieder",
          value: this.traffic.activeMembers || "–",
          detail: this.memberSnapshotLabel,
          icon: "mdi-account-group-outline",
          color: "#7c3aed",
        },
        {
          key: "frequency",
          label: "Ø Besuche je Mitglied",
          value: this.traffic.visitsPerMember || 0,
          detail: "Durchschnitt im gewählten Zeitraum",
          icon: "mdi-chart-timeline-variant",
          color: "#06a7a2",
        },
        {
          key: "trainerpoint",
          label: "Trainerpoint je 100 Besuche",
          value: `${this.traffic.trainerpointHoursPer100Visits || 0}h`,
          detail: "aktive Flächenbetreuung",
          icon: "mdi-account-group-outline",
          color: "#16a765",
        },
        {
          key: "appointments",
          label: "Kundentermine je 100 Besuche",
          value: `${this.traffic.customerAppointmentHoursPer100Visits || 0}h`,
          detail: "aktive Terminzeit",
          icon: "mdi-calendar-account-outline",
          color: "#f59e0b",
        },
        {
          key: "peak",
          label: "Stärkste Besuchszeit",
          value: this.traffic.busiestHour || "–",
          detail: `${this.traffic.busiestWeekday || "–"} · Ø ${this.traffic.busiestWeekdayAverage || 0}`,
          icon: "mdi-chart-bell-curve-cumulative",
          color: "#ef4444",
        },
      ]
    },
    rangeLabel() {
      const range = this.datePickerRange || []
      if (!range[0] || !range[1]) return "Kein Zeitraum gewählt"
      return `${this.formatShortDate(range[0])} – ${this.formatShortDate(range[1])}`
    },
    studioLabel() {
      const count = (this.studios || []).length
      if (!count) return "Kein Center gewählt"
      if (count === 1) return this.studios[0]?.name || "1 Center"
      return `${count} Center im Vergleich`
    },
    kpiCards() {
      const summary = this.totals
      const cards = [
        {
          key: "customer-care",
          label: "Eingeplante Kundenbetreuung",
          value: `${summary.customerCareRate || 0}%`,
          sub: `${summary.customerCareHours || 0}h aus Kundenterminen und Trainerpoint`,
          icon: "mdi-account-heart-outline",
          color: "#16b978",
        },
        {
          key: "customer-appointments",
          label: "Aktive Kundentermine",
          value: `${summary.customerAppointmentHours || 0}h`,
          sub: `${summary.activeCustomerAppointments || 0} Termine innerhalb von Netto-Schichten`,
          icon: "mdi-calendar-account-outline",
          color: "#2587e8",
        },
        {
          key: "trainerpoint",
          label: "Trainerpoint",
          value: `${summary.trainerpointHours || 0}h`,
          sub: `${summary.trainerpointEntries || 0} Einsätze zur aktiven Flächenbetreuung`,
          icon: "mdi-account-group-outline",
          color: "#20c9a0",
        },
        {
          key: "net-capacity",
          label: "Verfügbare Nettozeit",
          value: `${summary.workHours || 0}h`,
          sub: `${summary.totalShifts || 0} Schichten · ${summary.sickHours || 0}h Krankheit abgezogen`,
          icon: "mdi-clock-check-outline",
          color: "#f59e0b",
        },
      ]
      if (this.canViewCustomerFeedback) {
        cards.splice(2, 0, {
          key: "customer-feedback",
          label: "Kundenfeedback",
          value: this.feedbackSummary.ratings ? `${Number(this.feedbackSummary.average).toFixed(1)} / 5` : "–",
          sub: `${this.feedbackSummary.ratings || 0} Bewertungen · ${this.feedbackSummary.responseRate || 0}% Rücklauf`,
          icon: "mdi-star-circle-outline",
          color: "#f5b942",
          badge: this.feedbackSummary.critical ? `${this.feedbackSummary.critical} kritisch` : null,
          badgeTone: this.feedbackSummary.critical ? "badge--warning" : "",
        })
      }
      return cards
    },
    activityTrendSeries() {
      const rows = this.data?.dailyStats || []
      return [
        { name: "Aktive Kundentermine", data: rows.map((row) => ({ x: row.date, y: row.activeCustomerAppointments || 0 })) },
        { name: "Trainerpoint", data: rows.map((row) => ({ x: row.date, y: row.trainerpointEntries || 0 })) },
        { name: "Nicht gekommen", data: rows.map((row) => ({ x: row.date, y: row.noShows || 0 })) },
      ]
    },
    activityTrendOptions() {
      return this.areaOptions(["#2587e8", "#20c9a0", "#ef4444"])
    },
    outcomeSeries() {
      return [Number(this.totals.calendarAppointments || 0), Number(this.totals.noshow || 0), Number(this.totals.cancelled || 0)]
    },
    outcomeOptions() {
      return {
        ...this.chartBase(),
        labels: ["Ohne Abweichung", "Nicht gekommen", "Abgesagt"],
        colors: ["#16a765", "#ef4444", "#f59e0b"],
        stroke: { width: 3, colors: [this.panelColor] },
        legend: { position: "bottom", labels: { colors: this.chartText }, fontSize: "12px" },
        dataLabels: { enabled: false },
        plotOptions: { pie: { donut: { size: "72%" } } },
      }
    },
    careOverviewSeries() {
      return [Math.min(100, Math.max(0, Number(this.totals.customerCareRate || 0)))]
    },
    careOverviewOptions() {
      return {
        ...this.chartBase(),
        chart: { ...this.chartBase().chart, sparkline: { enabled: true } },
        colors: ["#16b978"],
        labels: ["Kundenbetreuung"],
        stroke: { lineCap: "round" },
        plotOptions: {
          radialBar: {
            hollow: { size: "64%" },
            track: { background: this.isDark ? "#263341" : "#e7eef3" },
            dataLabels: {
              name: { show: true, color: this.chartText, fontSize: "12px", offsetY: 20 },
              value: {
                color: this.isDark ? "#f8fafc" : "#142230",
                fontSize: "30px",
                fontWeight: 900,
                offsetY: -16,
                formatter: (value) => `${Number(value).toFixed(1)}%`,
              },
            },
          },
        },
      }
    },
    capacitySeries() {
      const used = Math.min(Number(this.totals.productiveHours || 0), Number(this.totals.workHours || 0))
      const free = Math.max(0, Number(this.totals.workHours || 0) - used)
      return [
        { name: "Gebunden", data: [used] },
        { name: "Ungebunden", data: [Number(free.toFixed(1))] },
      ]
    },
    capacityOptions() {
      return {
        ...this.chartBase(),
        chart: { ...this.chartBase().chart, stacked: true, stackType: "100%" },
        colors: ["#16a765", this.isDark ? "#334155" : "#dbe5ec"],
        plotOptions: { bar: { horizontal: true, borderRadius: 8, barHeight: "38%" } },
        dataLabels: { enabled: false },
        xaxis: { categories: ["Netto-Arbeitszeit"], labels: { formatter: (value) => `${Math.round(value)}%`, style: { colors: this.chartText } } },
        yaxis: { labels: { show: false } },
        grid: { show: false },
        legend: { show: false },
        tooltip: { theme: this.isDark ? "dark" : "light", y: { formatter: (value) => `${value}h` } },
      }
    },
    topDays() {
      const rows = [...(this.data?.dailyStats || [])]
        .map((row) => ({
          ...row,
          score: Number(row.activeCustomerAppointments || 0) + Number(row.trainerpointEntries || 0),
        }))
        .filter((row) => row.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 5)
      const max = Math.max(...rows.map((row) => row.score), 1)
      return rows.map((row) => ({ ...row, intensity: Math.max(8, Math.round((row.score / max) * 100)) }))
    },
    appointmentHeatmapSeries() {
      return this.heatmapSeries("appointments")
    },
    appointmentHeatmapOptions() {
      return this.heatmapOptions("#2587e8", "Termine")
    },
    appointmentTypeChartRows() {
      return [...(this.data?.appointmentTypes || [])]
        .filter((row) => Number(row.activeCustomerAppointments || row.calendarAppointments || 0) > 0)
        .sort(
          (a, b) =>
            Number(b.activeCustomerHours || 0) - Number(a.activeCustomerHours || 0) ||
            Number(b.activeCustomerAppointments || b.calendarAppointments || 0) - Number(a.activeCustomerAppointments || a.calendarAppointments || 0),
        )
        .slice(0, 10)
    },
    appointmentTypeChartSeries() {
      return [
        {
          name: "Aktive Kundenzeit",
          data: this.appointmentTypeChartRows.map((row) => Number(row.activeCustomerHours || 0)),
        },
      ]
    },
    appointmentTypeChartOptions() {
      return {
        ...this.chartBase(),
        colors: ["#4f9cf9"],
        plotOptions: { bar: { horizontal: true, borderRadius: 5, barHeight: "58%" } },
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.appointmentTypeChartRows.map((row) => this.translateAppointmentType(row.key, row.title)),
          labels: {
            formatter: (value) => `${value}h`,
            style: { colors: this.chartText },
          },
        },
        yaxis: { labels: { style: { colors: this.chartText }, maxWidth: 190 } },
        grid: { borderColor: this.chartGrid },
        legend: { show: false },
        tooltip: {
          theme: this.isDark ? "dark" : "light",
          y: { formatter: (value) => `${value}h aktive Kundenzeit` },
        },
      }
    },
    weekdayAppointmentSeries() {
      const rows = this.data?.weekdayStats || []
      return [
        { name: "Ø Termine", type: "column", data: rows.map((row) => row.avgAppointments) },
        { name: "Nicht gekommen %", type: "line", data: rows.map((row) => row.noShowRate) },
      ]
    },
    weekdayAppointmentOptions() {
      return {
        ...this.chartBase(),
        colors: ["#2587e8", "#ef4444"],
        stroke: { width: [0, 3], curve: "smooth" },
        plotOptions: { bar: { borderRadius: 5, columnWidth: "48%" } },
        dataLabels: { enabled: false },
        xaxis: { categories: WEEKDAYS, labels: { style: { colors: this.chartText } } },
        yaxis: [
          { title: { text: "Ø Termine", style: { color: this.chartText } }, labels: { style: { colors: this.chartText } } },
          {
            opposite: true,
            max: 100,
            title: { text: "Nicht gekommen %", style: { color: this.chartText } },
            labels: { formatter: (value) => `${Math.round(value)}%`, style: { colors: this.chartText } },
          },
        ],
        grid: { borderColor: this.chartGrid },
        legend: { position: "top", labels: { colors: this.chartText } },
        tooltip: { theme: this.isDark ? "dark" : "light" },
      }
    },
    appointmentTypeHeaders() {
      const headers = [
        { text: "Terminart", value: "title" },
        { text: "Termine", value: "appointments", align: "end" },
        { text: "Aktive Kundenzeit", value: "activeCustomerHours", align: "end" },
        { text: "Ø Dauer", value: "averageMinutes", align: "end" },
        { text: "Nicht gekommen", value: "noShows", align: "end" },
        { text: "Quote", value: "noShowRate", align: "end" },
        { text: "Abgesagt", value: "cancelled", align: "end" },
      ]
      if (this.canViewCustomerFeedback) {
        headers.splice(
          3,
          0,
          { text: "Bewertung", value: "feedbackAverage", align: "end" },
          { text: "Rücklauf", value: "feedbackResponseRate", align: "end" },
        )
      }
      return headers
    },
    filteredAppointmentTypes() {
      const term = String(this.appointmentSearch || "")
        .trim()
        .toLocaleLowerCase("de-CH")
      const rows = (this.data?.appointmentTypes || []).map((row) => ({
        ...row,
        feedbackAverage: Number(row.customerFeedback?.average || 0),
        feedbackRatings: Number(row.customerFeedback?.ratings || 0),
        feedbackResponseRate: Number(row.customerFeedback?.responseRate || 0),
        averageMinutes: row.activeCustomerAppointments
          ? Math.round(Number(row.activeCustomerMinutes || 0) / Number(row.activeCustomerAppointments || 1))
          : 0,
      }))
      if (!term) return rows
      return rows.filter((row) => `${row.key} ${row.title}`.toLocaleLowerCase("de-CH").includes(term))
    },
    customerAnalysisRows() {
      const customers = new Map()
      ;(this.data?.rawRows || [])
        .filter((row) => row.entryType === "appointment" && row.isCustomerAppointment)
        .forEach((row) => {
          const profiles =
            Array.isArray(row.customers) && row.customers.length
              ? row.customers
              : row.customer
              ? [
                  {
                    key: `name:${String(row.customer).toLocaleLowerCase("de-CH")}`,
                    id: null,
                    name: row.customer,
                    hasAccount: false,
                  },
                ]
              : []
          profiles.forEach((profile) => {
            const key = String(profile.key || profile.id || profile.name || "")
            if (!key) return
            if (!customers.has(key)) {
              customers.set(key, {
                key,
                id: profile.id || null,
                name: profile.name || "Unbekannter Kunde",
                hasAccount: Boolean(profile.hasAccount),
                appointments: 0,
                relevantAppointments: 0,
                productiveAppointments: 0,
                noShows: 0,
                cancelled: 0,
                customerMinutes: 0,
                firstDate: row.date,
                lastDate: row.date,
                employees: new Map(),
                types: new Map(),
                centers: new Map(),
                rows: [],
              })
            }
            const customer = customers.get(key)
            const status = String(row.status || "planned")
            customer.appointments += 1
            if (status !== "cancelled") customer.relevantAppointments += 1
            if (status === "noshow") customer.noShows += 1
            else if (status === "cancelled") customer.cancelled += 1
            else {
              customer.productiveAppointments += 1
              customer.customerMinutes += Number(row.durationMinutes || 0)
            }
            customer.firstDate = !customer.firstDate || row.date < customer.firstDate ? row.date : customer.firstDate
            customer.lastDate = !customer.lastDate || row.date > customer.lastDate ? row.date : customer.lastDate
            const employeeName = row.employee || "Nicht zugewiesen"
            const employee = customer.employees.get(employeeName) || {
              name: employeeName,
              appointments: 0,
              productiveAppointments: 0,
              noShows: 0,
              minutes: 0,
            }
            employee.appointments += 1
            if (status === "noshow") employee.noShows += 1
            if (!["noshow", "cancelled"].includes(status)) {
              employee.productiveAppointments += 1
              employee.minutes += Number(row.durationMinutes || 0)
            }
            customer.employees.set(employeeName, employee)

            const typeKey = String(row.appointmentType || row.title || "normal")
            const type = customer.types.get(typeKey) || {
              key: typeKey,
              title: row.title || typeKey,
              appointments: 0,
              noShows: 0,
              minutes: 0,
            }
            type.appointments += 1
            if (status === "noshow") type.noShows += 1
            if (!["noshow", "cancelled"].includes(status)) type.minutes += Number(row.durationMinutes || 0)
            customer.types.set(typeKey, type)
            if (row.studio) customer.centers.set(row.studio, (customer.centers.get(row.studio) || 0) + 1)
            customer.rows.push(row)
          })
        })

      return [...customers.values()]
        .map((customer) => ({
          ...customer,
          noShowRate: this.percent(customer.noShows, customer.relevantAppointments),
          customerHours: Number((customer.customerMinutes / 60).toFixed(1)),
          employeeCount: customer.employees.size,
          centerCount: customer.centers.size,
          employees: [...customer.employees.values()].sort((a, b) => b.appointments - a.appointments || a.name.localeCompare(b.name, "de")),
          types: [...customer.types.values()].sort((a, b) => b.appointments - a.appointments || a.title.localeCompare(b.title, "de")),
          centers: [...customer.centers.entries()]
            .map(([name, appointments]) => ({ name, appointments }))
            .sort((a, b) => b.appointments - a.appointments || a.name.localeCompare(b.name, "de")),
          rows: [...customer.rows].sort((a, b) => b.date.localeCompare(a.date) || String(b.start).localeCompare(String(a.start))),
        }))
        .sort((a, b) => b.noShows - a.noShows || b.appointments - a.appointments || a.name.localeCompare(b.name, "de"))
    },
    filteredCustomerRows() {
      const term = String(this.customerSearch || "")
        .trim()
        .toLocaleLowerCase("de-CH")
      if (!term) return this.customerAnalysisRows
      return this.customerAnalysisRows.filter((customer) =>
        `${customer.name} ${customer.employees.map((employee) => employee.name).join(" ")}`.toLocaleLowerCase("de-CH").includes(term),
      )
    },
    repeatNoShowCustomers() {
      return this.customerAnalysisRows.filter((customer) => customer.noShows >= 2)
    },
    noShowRows() {
      return (this.data?.rawRows || [])
        .filter((row) => row.entryType === "appointment" && row.status === "noshow")
        .sort((a, b) => b.date.localeCompare(a.date) || String(b.start).localeCompare(String(a.start)))
    },
    noShowNoteCoverage() {
      const withNote = this.noShowRows.filter((row) => String(row.description || "").trim()).length
      return this.percent(withNote, this.noShowRows.length)
    },
    highestNoShowType() {
      return [...(this.data?.appointmentTypes || [])]
        .filter((type) => Number(type.noShows || 0) > 0)
        .sort((a, b) => Number(b.noShows || 0) - Number(a.noShows || 0) || Number(b.noShowRate || 0) - Number(a.noShowRate || 0))[0]
    },
    noShowHighlights() {
      const type = this.highestNoShowType
      return [
        {
          key: "rate",
          label: "Nicht-gekommen-Quote",
          value: `${this.totals.noShowRate || 0}%`,
          detail: `${this.totals.noshow || 0} von ${this.totals.outcomeAppointments || 0} relevanten Terminen`,
          icon: "mdi-account-off-outline",
          color: "#ef4444",
        },
        {
          key: "repeat",
          label: "Wiederholt nicht gekommen",
          value: this.repeatNoShowCustomers.length,
          detail: "Kunden mit mindestens 2 Fällen",
          icon: "mdi-account-alert-outline",
          color: "#f97316",
        },
        {
          key: "notes",
          label: "Mit Begründung / Notiz",
          value: `${this.noShowNoteCoverage}%`,
          detail: `${this.noShowRows.filter((row) => String(row.description || "").trim()).length} dokumentiert`,
          icon: "mdi-note-text-outline",
          color: "#7c3aed",
        },
        {
          key: "type",
          label: "Häufigste Terminart",
          value: type ? this.translateAppointmentType(type.key, type.title) : "–",
          detail: type ? `${type.noShows || 0} Fälle · ${type.noShowRate || 0}%` : "keine Fälle",
          icon: "mdi-calendar-search-outline",
          color: "#2587e8",
          textValue: true,
        },
      ]
    },
    noShowHeaders() {
      return [
        { text: "Datum & Zeit", value: "date" },
        { text: "Kunde", value: "customer" },
        { text: "Termin", value: "title" },
        { text: "Zuständig", value: "employee" },
        { text: "Center", value: "studio" },
        { text: "Begründung / Notiz", value: "description" },
      ]
    },
    customerHeaders() {
      return [
        { text: "Kunde", value: "name" },
        { text: "Termine", value: "appointments", align: "end" },
        { text: "Kundenzeit", value: "customerHours", align: "end" },
        { text: "Nicht gekommen", value: "noShows", align: "end" },
        { text: "Quote", value: "noShowRate", align: "end" },
        { text: "Zuständige MA", value: "employeeCount", align: "end" },
        { text: "Letzter Termin", value: "lastDate", align: "end" },
      ]
    },
    selectedCustomer() {
      return this.customerAnalysisRows.find((customer) => String(customer.key) === String(this.selectedCustomerKey)) || null
    },
    selectedCustomerHighlights() {
      const customer = this.selectedCustomer || {}
      return [
        {
          key: "appointments",
          label: "Termine",
          value: customer.appointments || 0,
          detail: `${customer.productiveAppointments || 0} aktiv · ${customer.cancelled || 0} abgesagt`,
          icon: "mdi-calendar-account-outline",
          color: "#2587e8",
        },
        {
          key: "hours",
          label: "Aktive Kundenzeit",
          value: `${customer.customerHours || 0}h`,
          detail: "ohne Absagen und Nichtkommen",
          icon: "mdi-clock-check-outline",
          color: "#16a765",
        },
        {
          key: "noshow",
          label: "Nicht gekommen",
          value: customer.noShows || 0,
          detail: `${customer.noShowRate || 0}% der relevanten Termine`,
          icon: "mdi-account-off-outline",
          color: "#ef4444",
        },
        {
          key: "employees",
          label: "Zuständige Mitarbeitende",
          value: customer.employeeCount || 0,
          detail:
            (customer.employees || [])
              .slice(0, 2)
              .map((employee) => employee.name)
              .join(" · ") || "keine Zuweisung",
          icon: "mdi-account-multiple-outline",
          color: "#7c3aed",
        },
        {
          key: "centers",
          label: "Center",
          value: customer.centerCount || 0,
          detail: (customer.centers || []).map((center) => center.name).join(" · ") || "–",
          icon: "mdi-store-marker-outline",
          color: "#f59e0b",
        },
      ]
    },
    customerEmployeeHeaders() {
      return [
        { text: "Mitarbeiter", value: "name" },
        { text: "Termine", value: "appointments", align: "end" },
        { text: "Aktiv", value: "productiveAppointments", align: "end" },
        { text: "Nicht gekommen", value: "noShows", align: "end" },
        { text: "Kundenzeit", value: "minutes", align: "end" },
      ]
    },
    customerTypeHeaders() {
      return [
        { text: "Terminart", value: "title" },
        { text: "Termine", value: "appointments", align: "end" },
        { text: "Nicht gekommen", value: "noShows", align: "end" },
        { text: "Kundenzeit", value: "minutes", align: "end" },
      ]
    },
    customerHistoryHeaders() {
      return [
        { text: "Datum", value: "date" },
        { text: "Zeit", value: "start" },
        { text: "Termin", value: "title" },
        { text: "Zuständig", value: "employee" },
        { text: "Center", value: "studio" },
        { text: "Status", value: "status" },
        { text: "Notiz", value: "description" },
      ]
    },
    categoryCards() {
      const total = this.totals
      const workMinutes = Math.max(0, Number(total.workMinutes || 0))
      const definitions = [
        {
          key: "trainerpoint",
          label: "Trainerpoint · Kundenbetreuung",
          icon: "mdi-account-group-outline",
          color: "#20c9a0",
          values: { total: total.trainerpointEntries || 0, minutes: total.trainerpointMinutes || 0 },
        },
        { key: "amtli", label: "Ämtli", icon: "mdi-broom", color: "#a78bfa", values: total.amtli },
        {
          key: "tasks",
          label: "Übrige Aufgaben",
          icon: "mdi-clipboard-text-outline",
          color: "#f59e0b",
          values: {
            total: Math.max(0, Number(total.tasks?.total || 0) - Number(total.trainerpointEntries || 0)),
            minutes: Math.max(0, Number(total.tasks?.minutes || 0) - Number(total.trainerpointMinutes || 0)),
          },
        },
        { key: "info", label: "Informationen", icon: "mdi-information-outline", color: "#06a7a2", values: total.infoItems },
        {
          key: "unknown",
          label: "Nicht klassifiziert",
          icon: "mdi-help-rhombus-outline",
          color: "#64748b",
          values: total.unclassifiedWorkItems,
        },
      ]
      return definitions.map((definition) => ({
        ...definition,
        total: definition.values?.total || 0,
        hours: Number((Number(definition.values?.minutes || 0) / 60).toFixed(1)),
        workShare: this.percent(Number(definition.values?.minutes || 0), workMinutes),
        configuredTemplates: (this.data?.templateStats || []).filter((row) => {
          if (definition.key === "trainerpoint") return row.isTrainerpoint
          if (definition.key === "tasks") return row.category === "task" && !row.isTrainerpoint
          return row.category === definition.key
        }).length,
        templatesWithoutEntries: (this.data?.templateStats || []).filter((row) => {
          const matches =
            definition.key === "trainerpoint"
              ? row.isTrainerpoint
              : definition.key === "tasks"
              ? row.category === "task" && !row.isTrainerpoint
              : row.category === definition.key
          return matches && !Number(row.planned || 0)
        }).length,
      }))
    },
    workItemTrendSeries() {
      const rows = this.data?.dailyStats || []
      return [
        { name: "Ämtli", data: rows.map((row) => ({ x: row.date, y: row.amtli })) },
        { name: "Aufgaben", data: rows.map((row) => ({ x: row.date, y: row.tasks })) },
        {
          name: "Weitere Einträge",
          data: rows.map((row) => ({ x: row.date, y: Number(row.infoItems || 0) + Number(row.unclassified || 0) })),
        },
      ]
    },
    workItemTrendOptions() {
      return this.areaOptions(["#a78bfa", "#f59e0b", "#16a765"])
    },
    workItemHeatmapSeries() {
      return this.heatmapSeries(this.workItemHeatmapMetric)
    },
    workItemHeatmapOptions() {
      const colors = { trainerpoint: "#20c9a0", amtli: "#a78bfa", tasks: "#f59e0b" }
      return this.heatmapOptions(colors[this.workItemHeatmapMetric] || "#f59e0b", "Einträge")
    },
    templateHeaders() {
      return [
        { text: "Vorlage / Eintrag", value: "title" },
        { text: "Typ", value: "category" },
        { text: "Kalenderstatus", value: "calendarStatus" },
        { text: "Gesamtzeit", value: "plannedHours", align: "end" },
      ]
    },
    filteredTemplateStats() {
      const term = String(this.taskSearch || "")
        .trim()
        .toLocaleLowerCase("de-CH")
      return (this.data?.templateStats || [])
        .filter((row) => {
          if (this.taskCategory !== "all" && row.category !== this.taskCategory) return false
          return !term || `${row.title} ${row.category}`.toLocaleLowerCase("de-CH").includes(term)
        })
        .map((row) => ({
          ...row,
          plannedHours: Number(((Number(row.planned || 0) * Number(row.duration || 0)) / 60).toFixed(1)),
        }))
    },
    employeeRawIndex() {
      const index = new Map()
      ;(this.data?.rawRows || []).forEach((entry) => {
        const key = String(entry.employeeId)
        let bucket = index.get(key)
        if (!bucket) {
          bucket = { rows: [], amtliMinutes: 0, taskMinutes: 0 }
          index.set(key, bucket)
        }

        bucket.rows.push(entry)
        const durationMinutes = Math.max(0, Number(entry.durationMinutes || 0))
        if (entry.entryType === "work_item" && entry.category === "amtli") {
          bucket.amtliMinutes += durationMinutes
        } else if (entry.entryType === "work_item" && !entry.isTrainerpoint) {
          bucket.taskMinutes += durationMinutes
        }
      })
      return index
    },
    employeeAnalysisRows() {
      return (this.data?.employeeStats || []).map((row) => {
        const employeeBucket = this.employeeRawIndex.get(String(row.id))
        const workHours = Number(row.workHours || 0)
        const productiveHours = Math.min(workHours, Number(row.productiveHours || 0))
        const unboundHours = Math.max(0, workHours - productiveHours)
        const workItemCount = Number(row.amtli || 0) + Number(row.tasks || 0) + Number(row.infoItems || 0) + Number(row.unclassified || 0)
        const activeAppointments = Math.max(0, Number(row.appointments || 0) - Number(row.cancelled || 0) - Number(row.noShows || 0))
        const issueCount =
          Number(row.outsideShiftAppointments || 0) +
          Number(row.outsideShiftWorkItems || 0) +
          Number(row.doubleBookings || 0) +
          Number(row.taskAppointmentConflicts || 0)
        const amtliHours = Number((Number(employeeBucket?.amtliMinutes || 0) / 60).toFixed(1))
        const taskHours = Number((Number(employeeBucket?.taskMinutes || 0) / 60).toFixed(1))
        return {
          ...row,
          workHours,
          productiveHours: Number(productiveHours.toFixed(1)),
          unboundHours: Number(unboundHours.toFixed(1)),
          customerCareHours: Number(row.customerCareHours || 0),
          customerCareRate: Number(row.customerCareRate || 0),
          customerAppointmentHours: Number(row.customerAppointmentHours || 0),
          trainerpointHours: Number(row.trainerpointHours || 0),
          trainerpointEntries: Number(row.trainerpointEntries || 0),
          activeCustomerAppointments: Number(row.activeCustomerAppointments || 0),
          amtliHours,
          amtliRate: this.percent(amtliHours, workHours),
          taskHours,
          taskRate: this.percent(taskHours, workHours),
          workItemCount,
          activeAppointments,
          issueCount,
          feedbackAverage: Number(row.customerFeedback?.average || 0),
          feedbackRatings: Number(row.customerFeedback?.ratings || 0),
          feedbackResponseRate: Number(row.customerFeedback?.responseRate || 0),
          feedbackFavorableRate: Number(row.customerFeedback?.favorableRate || 0),
          sickRate: this.percent(Number(row.sickHours || 0), workHours + Number(row.sickHours || 0)),
        }
      })
    },
    teamSummaryCards() {
      const rows = this.employeeAnalysisRows.filter((row) => row.workHours > 0 || row.appointments > 0 || row.workItemCount > 0)
      const employeesWithShifts = rows.filter((row) => Number(row.shifts || 0) > 0).length
      const employeesWithIssues = rows.filter((row) => row.issueCount > 0).length
      const unboundHours = Math.max(0, Number(this.totals.workHours || 0) - Number(this.totals.productiveHours || 0))
      return [
        {
          key: "employees",
          label: "Mitarbeitende",
          value: rows.length,
          detail: `${employeesWithShifts} mit Schichten`,
          icon: "mdi-account-group-outline",
          color: "#3b82f6",
        },
        {
          key: "net-hours",
          label: "Netto-Schichtzeit",
          value: `${this.totals.workHours || 0}h`,
          detail: `${this.totals.totalShifts || 0} Schichten`,
          icon: "mdi-clock-check-outline",
          color: "#06a7a2",
        },
        {
          key: "bound-hours",
          label: "Kalendergebunden",
          value: `${this.totals.productiveHours || 0}h`,
          detail: `${this.totals.utilizationRate || 0}% der Nettozeit`,
          icon: "mdi-chart-timeline-variant-shimmer",
          color: "#16a765",
        },
        {
          key: "unbound-hours",
          label: "Noch nicht gebunden",
          value: `${Number(unboundHours.toFixed(1))}h`,
          detail: "Nicht durch Einträge belegt",
          icon: "mdi-timer-sand-empty",
          color: "#a78bfa",
        },
        {
          key: "sickness",
          label: "Krankheitszeit",
          value: `${this.totals.sickHours || 0}h`,
          detail: "Separat von der Nettozeit",
          icon: "mdi-account-injury-outline",
          color: "#f59e0b",
        },
        {
          key: "issues",
          label: "Mit Hinweisen",
          value: employeesWithIssues,
          detail: employeesWithIssues ? "Details in der Tabelle" : "Keine Auffälligkeiten",
          icon: employeesWithIssues ? "mdi-alert-decagram-outline" : "mdi-check-decagram-outline",
          color: employeesWithIssues ? "#ef4444" : "#16a765",
        },
      ]
    },
    teamChartRows() {
      return [...this.employeeAnalysisRows]
        .filter((row) => row.workHours > 0)
        .sort((a, b) => b.workHours - a.workHours || b.productiveHours - a.productiveHours)
        .slice(0, 14)
    },
    teamCapacityChartHeight() {
      return Math.max(360, this.teamChartRows.length * 34)
    },
    teamCapacitySeries() {
      const rows = this.teamChartRows
      return [
        { name: "Kalendergebunden", data: rows.map((row) => row.productiveHours) },
        { name: "Noch nicht gebunden", data: rows.map((row) => row.unboundHours) },
      ]
    },
    teamCapacityOptions() {
      const rows = this.teamChartRows
      return {
        ...this.chartBase(),
        chart: { ...this.chartBase().chart, stacked: true },
        colors: ["#16a765", this.isDark ? "#3b4654" : "#dbe5ec"],
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4,
            barHeight: "64%",
          },
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: rows.map((row) => row.name),
          labels: { formatter: (value) => `${Math.round(value)}h`, style: { colors: this.chartText } },
        },
        yaxis: { labels: { style: { colors: this.chartText }, maxWidth: 145 } },
        grid: { borderColor: this.chartGrid },
        legend: { position: "top", labels: { colors: this.chartText } },
        tooltip: {
          shared: true,
          intersect: false,
          theme: this.isDark ? "dark" : "light",
          y: { formatter: (value) => `${Number(value).toFixed(1)}h` },
        },
      }
    },
    teamWorkloadSeries() {
      const rows = this.teamChartRows
      return [
        { name: "Kundentermine", data: rows.map((row) => Number(row.customerAppointmentHours || 0)) },
        { name: "Trainerpoint", data: rows.map((row) => Number(row.trainerpointHours || 0)) },
        { name: "Ämtli", data: rows.map((row) => Number(row.amtliHours || 0)) },
        { name: "Übrige Aufgaben", data: rows.map((row) => Number(row.taskHours || 0)) },
      ]
    },
    teamWorkloadOptions() {
      const rows = this.teamChartRows
      return {
        ...this.chartBase(),
        chart: { ...this.chartBase().chart, stacked: true },
        colors: ["#2587e8", "#20c9a0", "#a78bfa", "#f59e0b"],
        plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: "64%" } },
        dataLabels: { enabled: false },
        xaxis: {
          categories: rows.map((row) => row.name),
          labels: {
            formatter: (value) => `${Number(value).toFixed(0)}h`,
            style: { colors: this.chartText },
          },
        },
        yaxis: { labels: { style: { colors: this.chartText }, maxWidth: 145 } },
        grid: { borderColor: this.chartGrid },
        legend: { position: "top", labels: { colors: this.chartText } },
        tooltip: {
          shared: true,
          intersect: false,
          theme: this.isDark ? "dark" : "light",
          y: { formatter: (value) => `${Number(value).toFixed(1)}h eingeplant` },
        },
      }
    },
    teamBalanceRows() {
      return this.employeeAnalysisRows.filter((row) => row.workHours > 0)
    },
    teamBalanceSeries() {
      const rows = this.teamBalanceRows
      return [
        rows.filter((row) => row.utilizationRate < 35).length,
        rows.filter((row) => row.utilizationRate >= 35 && row.utilizationRate <= 80).length,
        rows.filter((row) => row.utilizationRate > 80).length,
      ]
    },
    teamBalanceOptions() {
      return {
        ...this.chartBase(),
        labels: ["Unter 35%", "35–80%", "Über 80%"],
        colors: ["#38bdf8", "#16a765", "#f59e0b"],
        stroke: { width: 3, colors: [this.panelColor] },
        dataLabels: { enabled: true, formatter: (value) => `${Math.round(value)}%` },
        plotOptions: {
          pie: {
            donut: {
              size: "68%",
              labels: {
                show: true,
                total: {
                  show: true,
                  label: "Mitarbeitende",
                  formatter: () => String(this.teamBalanceRows.length),
                },
              },
            },
          },
        },
        legend: { position: "bottom", labels: { colors: this.chartText } },
        tooltip: { theme: this.isDark ? "dark" : "light" },
      }
    },
    staffingHeatmapSeries() {
      return this.heatmapSeries("avgStaffing")
    },
    staffingHeatmapOptions() {
      return this.heatmapOptions("#06a7a2", "Ø Mitarbeitende", true)
    },
    employeeHeaders() {
      const headers = [
        { text: "Mitarbeiter", value: "name" },
        { text: "Kundenbetreuung", value: "customerCareRate" },
        { text: "Betreuungszeit", value: "customerCareHours", align: "end" },
        { text: "Kundentermine", value: "activeCustomerAppointments", align: "end" },
        { text: "Terminzeit", value: "customerAppointmentHours", align: "end" },
        { text: "Trainerpoint", value: "trainerpointHours", align: "end" },
        { text: "Ämtli-Zeit", value: "amtliRate", align: "end" },
        { text: "Aufgaben-Zeit", value: "taskRate", align: "end" },
        { text: "Nicht gekommen", value: "noShows", align: "end" },
        { text: "Hinweise", value: "alerts", sortable: false, align: "end" },
      ]
      if (this.canViewCustomerFeedback) {
        headers.splice(3, 0, { text: "Kundenfeedback", value: "feedbackAverage", align: "end" })
      }
      return headers
    },
    filteredEmployees() {
      const term = String(this.employeeSearch || "")
        .trim()
        .toLocaleLowerCase("de-CH")
      const rows = this.employeeAnalysisRows
      if (!term) return rows
      return rows.filter((row) => row.name.toLocaleLowerCase("de-CH").includes(term))
    },
    centerComparisonSeries() {
      const rows = this.data?.centers || []
      return [
        { name: "Termine", data: rows.map((row) => row.appointments) },
        { name: "Ämtli & Aufgaben", data: rows.map((row) => row.amtli + row.tasks + row.unclassified) },
      ]
    },
    centerComparisonOptions() {
      const rows = this.data?.centers || []
      return {
        ...this.chartBase(),
        colors: ["#2587e8", "#a78bfa"],
        plotOptions: { bar: { borderRadius: 5, columnWidth: "54%" } },
        dataLabels: { enabled: false },
        xaxis: { categories: rows.map((row) => row.name), labels: { style: { colors: this.chartText }, trim: true } },
        yaxis: { labels: { style: { colors: this.chartText } } },
        grid: { borderColor: this.chartGrid },
        legend: { position: "top", labels: { colors: this.chartText } },
        tooltip: { theme: this.isDark ? "dark" : "light" },
      }
    },
    centerFeedbackSeries() {
      return [
        { name: "Ø Bewertung", type: "column", data: this.centerRows.map((row) => row.feedbackAverage || 0) },
        { name: "Rücklauf", type: "line", data: this.centerRows.map((row) => row.feedbackResponseRate || 0) },
      ]
    },
    centerFeedbackOptions() {
      return {
        ...this.chartBase(),
        colors: ["#f5b942", "#16a765"],
        stroke: { width: [0, 3], curve: "smooth" },
        markers: { size: [0, 4], strokeWidth: 0 },
        plotOptions: { bar: { borderRadius: 6, columnWidth: "48%" } },
        dataLabels: { enabled: false },
        xaxis: { categories: this.centerRows.map((row) => row.name), labels: { style: { colors: this.chartText } } },
        yaxis: [
          {
            min: 0,
            max: 5,
            tickAmount: 5,
            title: { text: "Ø Sterne", style: { color: this.chartText } },
            labels: { formatter: (value) => Number(value || 0).toFixed(1), style: { colors: this.chartText } },
          },
          {
            min: 0,
            max: 100,
            opposite: true,
            title: { text: "Rücklauf", style: { color: this.chartText } },
            labels: { formatter: (value) => `${Math.round(value)}%`, style: { colors: this.chartText } },
          },
        ],
        grid: { borderColor: this.chartGrid, strokeDashArray: 4 },
        legend: { position: "top", labels: { colors: this.chartText } },
        tooltip: { shared: true, theme: this.isDark ? "dark" : "light" },
      }
    },
    shiftMixSeries() {
      const rows = this.data?.centers || []
      return [
        { name: "Früh", data: rows.map((row) => row.shiftMix?.morning || 0) },
        { name: "Zwischen", data: rows.map((row) => (row.shiftMix?.intermediate || 0) + (row.shiftMix?.other || 0)) },
        { name: "Spät", data: rows.map((row) => row.shiftMix?.late || 0) },
        { name: "Split", data: rows.map((row) => row.shiftMix?.split || 0) },
      ]
    },
    shiftMixOptions() {
      const rows = this.data?.centers || []
      return {
        ...this.chartBase(),
        chart: { ...this.chartBase().chart, stacked: true },
        colors: ["#38bdf8", "#16a765", "#a78bfa", "#f59e0b"],
        plotOptions: { bar: { borderRadius: 4, columnWidth: "52%" } },
        dataLabels: { enabled: false },
        xaxis: { categories: rows.map((row) => row.name), labels: { style: { colors: this.chartText } } },
        yaxis: { labels: { style: { colors: this.chartText } } },
        grid: { borderColor: this.chartGrid },
        legend: { position: "top", labels: { colors: this.chartText } },
        tooltip: { theme: this.isDark ? "dark" : "light" },
      }
    },
    trafficDailySeries() {
      const rows = this.traffic.daily || []
      return [
        {
          name: "Besuche",
          type: "column",
          data: rows.map((row) => ({ x: row.date, y: Number(row.checkins || 0) })),
        },
        {
          name: "Kundentermine",
          type: "line",
          data: rows.map((row) => ({ x: row.date, y: Number(row.customerAppointmentHours || 0) })),
        },
        {
          name: "Trainerpoint",
          type: "line",
          data: rows.map((row) => ({ x: row.date, y: Number(row.trainerpointHours || 0) })),
        },
      ]
    },
    trafficDailyOptions() {
      const base = this.chartBase()
      const openSelectedDay = (_event, _chartContext, selection) => {
        const index = Number(selection?.dataPointIndex)
        if (!Number.isInteger(index) || index < 0) return
        this.openTrafficDay((this.traffic.daily || [])[index]?.date)
      }
      return {
        ...base,
        chart: {
          ...base.chart,
          events: {
            dataPointSelection: openSelectedDay,
            markerClick: openSelectedDay,
          },
        },
        colors: ["#2587e8", "#f59e0b", "#16a765"],
        stroke: { width: [0, 3, 3], curve: "smooth" },
        markers: { size: [0, 4, 4], strokeWidth: 0, hover: { sizeOffset: 2 } },
        plotOptions: { bar: { borderRadius: 3, columnWidth: "62%" } },
        dataLabels: { enabled: false },
        xaxis: {
          type: "datetime",
          labels: { style: { colors: this.chartText, fontSize: "12px" } },
        },
        yaxis: [
          {
            seriesName: "Besuche",
            min: 0,
            title: { text: "Besuche", style: { color: this.chartText, fontSize: "12px" } },
            labels: { style: { colors: this.chartText, fontSize: "12px" } },
          },
          {
            seriesName: ["Kundentermine", "Trainerpoint"],
            min: 0,
            opposite: true,
            title: { text: "Stunden", style: { color: this.chartText, fontSize: "12px" } },
            labels: {
              formatter: (value) => `${Number(value || 0).toFixed(1)}h`,
              style: { colors: this.chartText, fontSize: "12px" },
            },
          },
        ],
        grid: { borderColor: this.chartGrid, strokeDashArray: 4 },
        legend: { position: "top", labels: { colors: this.chartText }, fontSize: "12px" },
        tooltip: { shared: true, theme: this.isDark ? "dark" : "light", x: { format: "dd.MM.yyyy" } },
      }
    },
    trafficHourlyRows() {
      const rows = this.traffic.hourly || []
      const activeHours = rows.filter(
        (row) => Number(row.checkins || 0) + Number(row.customerAppointmentStarts || 0) + Number(row.trainerpointStarts || 0) > 0,
      )
      if (!activeHours.length) return rows.filter((row) => row.hour >= 5 && row.hour <= 23)
      const first = Math.max(0, Math.min(...activeHours.map((row) => row.hour)) - 1)
      const last = Math.min(23, Math.max(...activeHours.map((row) => row.hour)) + 1)
      return rows.filter((row) => row.hour >= first && row.hour <= last)
    },
    trafficHourlySeries() {
      const rows = this.trafficHourlyRows
      return [
        { name: "Besuche", type: "column", data: rows.map((row) => Number(row.checkins || 0)) },
        {
          name: "Kundentermine starten",
          type: "line",
          data: rows.map((row) => Number(row.customerAppointmentStarts || 0)),
        },
        {
          name: "Trainerpoint startet",
          type: "line",
          data: rows.map((row) => Number(row.trainerpointStarts || 0)),
        },
      ]
    },
    trafficHourlyOptions() {
      return {
        ...this.chartBase(),
        colors: ["#2587e8", "#f59e0b", "#16a765"],
        stroke: { width: [0, 3, 3], curve: "smooth" },
        plotOptions: { bar: { borderRadius: 4, columnWidth: "66%" } },
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.trafficHourlyRows.map((row) => `${String(row.hour).padStart(2, "0")}:00`),
          labels: { rotate: -45, style: { colors: this.chartText, fontSize: "12px" } },
        },
        yaxis: [
          {
            seriesName: "Besuche",
            min: 0,
            labels: { style: { colors: this.chartText, fontSize: "12px" } },
          },
          {
            seriesName: ["Kundentermine starten", "Trainerpoint startet"],
            min: 0,
            opposite: true,
            labels: { style: { colors: this.chartText, fontSize: "12px" } },
          },
        ],
        grid: { borderColor: this.chartGrid, strokeDashArray: 4 },
        legend: { position: "top", labels: { colors: this.chartText }, fontSize: "12px" },
        tooltip: { shared: true, theme: this.isDark ? "dark" : "light" },
      }
    },
    trafficWeekdaySeries() {
      const rows = this.traffic.weekdays || []
      return [
        {
          name: "Ø Besuche pro Tag",
          type: "column",
          data: rows.map((row) => Number(row.averageCheckins || 0)),
        },
        {
          name: "Kundentermine",
          type: "line",
          data: rows.map((row) => Number(row.customerAppointmentHours || 0)),
        },
        {
          name: "Trainerpoint",
          type: "line",
          data: rows.map((row) => Number(row.trainerpointHours || 0)),
        },
      ]
    },
    trafficWeekdayOptions() {
      const rows = this.traffic.weekdays || []
      return {
        ...this.chartBase(),
        colors: ["#2587e8", "#f59e0b", "#16a765"],
        stroke: { width: [0, 3, 3], curve: "smooth" },
        plotOptions: { bar: { borderRadius: 5, columnWidth: "58%" } },
        dataLabels: { enabled: false },
        xaxis: {
          categories: rows.map((row) => String(row.label || "").slice(0, 2)),
          labels: { style: { colors: this.chartText, fontSize: "12px" } },
        },
        yaxis: [
          {
            seriesName: "Ø Besuche pro Tag",
            min: 0,
            labels: { style: { colors: this.chartText, fontSize: "12px" } },
          },
          {
            seriesName: ["Kundentermine", "Trainerpoint"],
            min: 0,
            opposite: true,
            labels: {
              formatter: (value) => `${Number(value || 0).toFixed(1)}h`,
              style: { colors: this.chartText, fontSize: "12px" },
            },
          },
        ],
        grid: { borderColor: this.chartGrid, strokeDashArray: 4 },
        legend: { position: "top", labels: { colors: this.chartText }, fontSize: "12px" },
        tooltip: { shared: true, theme: this.isDark ? "dark" : "light" },
      }
    },
    trafficCenterHeaders() {
      return [
        { text: "Center", value: "name" },
        { text: "Besuche", value: "checkins", align: "end" },
        { text: "Aktive Mitglieder", value: "activeMembers", align: "end" },
        { text: "Ø Besuche / Mitglied", value: "visitsPerMember", align: "end" },
        {
          text: "Kundentermine / 100 Besuche",
          value: "customerAppointmentHoursPer100Visits",
          align: "end",
        },
        {
          text: "Trainerpoint / 100 Besuche",
          value: "trainerpointHoursPer100Visits",
          align: "end",
        },
      ]
    },
    centerHeaders() {
      const headers = [
        { text: "Center", value: "name" },
        { text: "Auslastung", value: "utilizationRate", align: "end" },
        { text: "Termine", value: "appointments", align: "end" },
        { text: "Nicht gekommen", value: "noShowRate", align: "end" },
        { text: "Ämtli", value: "amtli", align: "end" },
        { text: "Aufgaben", value: "tasks", align: "end" },
        { text: "Arbeitsaufträge", value: "workItemCount", align: "end" },
        { text: "Außerhalb Schicht", value: "outsideShiftAppointments", align: "end" },
        { text: "Aufgaben außerhalb", value: "outsideShiftWorkItems", align: "end" },
      ]
      if (this.canViewCustomerFeedback) {
        headers.splice(
          3,
          0,
          { text: "Bewertung", value: "feedbackAverage", align: "end" },
          { text: "Rücklauf", value: "feedbackResponseRate", align: "end" },
        )
      }
      return headers
    },
    centerRows() {
      return (this.data?.centers || []).map((center) => ({
        ...center,
        feedbackAverage: Number(center.customerFeedback?.average || 0),
        feedbackRatings: Number(center.customerFeedback?.ratings || 0),
        feedbackResponseRate: Number(center.customerFeedback?.responseRate || 0),
      }))
    },
    rawCategoryOptions() {
      return [
        { text: "Alle Einträge", value: "all" },
        { text: "Termine", value: "appointment" },
        { text: "Ämtli", value: "amtli" },
        { text: "Aufgaben", value: "task" },
        { text: "Informationen", value: "info" },
        { text: "Nicht klassifiziert", value: "unknown" },
      ]
    },
    rawHeaders() {
      return [
        { text: "Datum", value: "date" },
        { text: "Zeit", value: "start" },
        { text: "Typ", value: "entryType" },
        { text: "Titel", value: "title" },
        { text: "Mitarbeiter", value: "employee" },
        { text: "Kunde", value: "customer" },
        { text: "Center", value: "studio" },
      ]
    },
    filteredRawRows() {
      const term = String(this.rawSearch || "")
        .trim()
        .toLocaleLowerCase("de-CH")
      return (this.data?.rawRows || []).filter((row) => {
        if (this.rawCategory !== "all" && row.category !== this.rawCategory) return false
        if (!term) return true
        return `${row.title} ${row.employee} ${row.customer} ${row.date} ${row.studio}`.toLocaleLowerCase("de-CH").includes(term)
      })
    },
    employeeDetail() {
      if (!this.selectedEmployee) return null
      return this.employeeAnalysisRows.find((row) => String(row.id) === String(this.selectedEmployee.id)) || this.selectedEmployee
    },
    employeeFeedbackSummary() {
      return (
        this.employeeDetail?.customerFeedback || {
          eligible: 0,
          eligibleAppointments: 0,
          ratings: 0,
          average: 0,
          responseRate: 0,
          favorableRate: 0,
          critical: 0,
          distribution: [],
          tags: [],
          feedback: [],
        }
      )
    },
    employeeFeedbackRecent() {
      return (this.employeeFeedbackSummary.feedback || []).slice(0, 12)
    },
    employeeFeedbackTopTags() {
      return (this.employeeFeedbackSummary.tags || []).slice(0, 6)
    },
    selectedEmployeeRows() {
      if (!this.employeeDetail) return []
      const employeeBucket = this.employeeRawIndex.get(String(this.employeeDetail.id))
      return employeeBucket ? employeeBucket.rows : []
    },
    selectedEmployeeDetailRows() {
      if (this.employeeEntryFilter === "all") return this.selectedEmployeeRows
      return this.selectedEmployeeRows.filter((row) => row.entryType === this.employeeEntryFilter)
    },
    selectedEmployeeAppointmentTypes() {
      return [...(this.employeeDetail?.appointmentTypes || [])]
        .filter((row) => Number(row.customerAppointments || row.appointments || 0) > 0)
        .sort(
          (a, b) =>
            Number(b.activeCustomerAppointments || b.activeAppointments || 0) - Number(a.activeCustomerAppointments || a.activeAppointments || 0) ||
            Number(b.activeCustomerHours || b.activeHours || 0) - Number(a.activeCustomerHours || a.activeHours || 0),
        )
    },
    selectedEmployeeCareSeries() {
      return [Math.min(100, Math.max(0, Number(this.employeeDetail?.customerCareRate || 0)))]
    },
    selectedEmployeeCareOptions() {
      return {
        ...this.chartBase(),
        chart: { ...this.chartBase().chart, sparkline: { enabled: true } },
        colors: ["#16b978"],
        labels: ["Kundenbetreuung"],
        stroke: { lineCap: "round" },
        plotOptions: {
          radialBar: {
            hollow: { size: "66%" },
            track: { background: this.isDark ? "#263341" : "#e7eef3" },
            dataLabels: {
              name: { show: true, color: this.chartText, fontSize: "13px", offsetY: 22 },
              value: {
                show: true,
                color: this.isDark ? "#f8fafc" : "#142230",
                fontSize: "34px",
                fontWeight: 900,
                offsetY: -17,
                formatter: (value) => `${Number(value).toFixed(1)}%`,
              },
            },
          },
        },
      }
    },
    selectedEmployeeAppointmentTypeSeries() {
      const rows = this.selectedEmployeeAppointmentTypes.slice(0, 8)
      return [{ name: "Aktive Kundenzeit", data: rows.map((row) => Number(row.activeCustomerHours || 0)) }]
    },
    selectedEmployeeAppointmentTypeOptions() {
      const rows = this.selectedEmployeeAppointmentTypes.slice(0, 8)
      return {
        ...this.chartBase(),
        colors: ["#4f9cf9"],
        plotOptions: { bar: { horizontal: true, borderRadius: 5, barHeight: "58%" } },
        dataLabels: { enabled: false },
        xaxis: {
          categories: rows.map((row) => this.translateAppointmentType(row.key, row.title)),
          labels: {
            formatter: (value) => `${value}h`,
            style: { colors: this.chartText },
          },
        },
        yaxis: { labels: { style: { colors: this.chartText }, maxWidth: 170 } },
        grid: { borderColor: this.chartGrid },
        legend: { show: false },
        tooltip: {
          theme: this.isDark ? "dark" : "light",
          y: { formatter: (value) => `${value}h aktive Kundenzeit` },
        },
      }
    },
    selectedEmployeeCustomerWeekdaySeries() {
      const buckets = WEEKDAYS.map(() => ({ appointments: 0, trainerpoint: 0 }))
      this.selectedEmployeeRows.forEach((row) => {
        const weekday = this.weekdayIndex(row.date)
        if (weekday < 0 || !buckets[weekday]) return
        if (row.entryType === "appointment" && row.isCustomerAppointment) buckets[weekday].appointments += 1
        if (row.entryType === "work_item" && row.isTrainerpoint) buckets[weekday].trainerpoint += 1
      })
      return [
        { name: "Kundentermine", data: buckets.map((bucket) => bucket.appointments) },
        { name: "Trainerpoint", data: buckets.map((bucket) => bucket.trainerpoint) },
      ]
    },
    selectedEmployeeCustomerWeekdayOptions() {
      return {
        ...this.chartBase(),
        chart: { ...this.chartBase().chart, stacked: true },
        colors: ["#4f9cf9", "#20c9a0"],
        plotOptions: { bar: { borderRadius: 5, columnWidth: "52%" } },
        dataLabels: { enabled: false },
        xaxis: { categories: WEEKDAYS, labels: { style: { colors: this.chartText } } },
        yaxis: { min: 0, forceNiceScale: true, labels: { style: { colors: this.chartText } } },
        grid: { borderColor: this.chartGrid },
        legend: { position: "top", labels: { colors: this.chartText } },
        tooltip: { shared: true, intersect: false, theme: this.isDark ? "dark" : "light" },
      }
    },
    employeeAppointmentTypeHeaders() {
      return [
        { text: "Terminart", value: "title" },
        { text: "Aktiv", value: "activeCustomerAppointments", align: "end" },
        { text: "Zeit in Terminen", value: "activeCustomerHours", align: "end" },
        { text: "Nicht gekommen", value: "noShows", align: "end" },
        { text: "Quote", value: "noShowRate", align: "end" },
      ]
    },
    selectedEmployeeCapacityParts() {
      const employee = this.employeeDetail
      if (!employee) return []
      const total = Math.max(Number(employee.workHours || 0), 0)
      const bound = Math.min(total, Number(employee.productiveHours || 0))
      const unbound = Math.max(0, total - bound)
      let remainingBound = bound
      const take = (value) => {
        const hours = Math.min(remainingBound, Math.max(0, Number(value || 0)))
        remainingBound = Math.max(0, remainingBound - hours)
        return Number(hours.toFixed(1))
      }
      const customerAppointments = take(employee.customerAppointmentHours)
      const trainerpoint = take(Math.max(0, Number(employee.customerCareHours || 0) - Number(employee.customerAppointmentHours || 0)))
      const amtli = take(employee.amtliHours)
      const tasks = take(employee.taskHours)
      const otherCalendar = Number(remainingBound.toFixed(1))
      return [
        { key: "customers", label: "Kundentermine", hours: customerAppointments, color: "#2587e8" },
        { key: "trainerpoint", label: "Trainerpoint", hours: trainerpoint, color: "#20c9a0" },
        { key: "amtli", label: "Ämtli", hours: amtli, color: "#a78bfa" },
        { key: "tasks", label: "Übrige Aufgaben", hours: tasks, color: "#f59e0b" },
        { key: "other", label: "Weitere Kalendereinträge", hours: otherCalendar, color: "#64748b" },
        {
          key: "unbound",
          label: "Nicht im Kalender belegt",
          hours: Number(unbound.toFixed(1)),
          color: this.isDark ? "#3b4654" : "#d7e1e8",
        },
      ]
        .filter((part) => part.hours > 0)
        .map((part) => ({ ...part, percent: this.percent(part.hours, total) }))
    },
    selectedEmployeeShiftMix() {
      const mix = this.employeeDetail?.shiftMix || {}
      return [
        { key: "morning", label: "Früh", icon: "mdi-weather-sunset-up", color: "#38bdf8" },
        { key: "intermediate", label: "Zwischen", icon: "mdi-weather-sunny", color: "#16a765" },
        { key: "late", label: "Spät", icon: "mdi-weather-sunset-down", color: "#a78bfa" },
        { key: "split", label: "Split", icon: "mdi-call-split", color: "#f59e0b" },
        { key: "other", label: "Weitere", icon: "mdi-clock-outline", color: "#64748b" },
      ].map((item) => ({ ...item, count: Number(mix[item.key] || 0) }))
    },
    selectedEmployeeWeekdaySeries() {
      const buckets = WEEKDAYS.map(() => ({ appointments: 0, amtli: 0, tasks: 0 }))
      this.selectedEmployeeRows.forEach((row) => {
        const weekday = this.weekdayIndex(row.date)
        if (weekday < 0 || !buckets[weekday]) return
        if (row.entryType === "appointment") buckets[weekday].appointments += 1
        else if (row.category === "amtli") buckets[weekday].amtli += 1
        else buckets[weekday].tasks += 1
      })
      return [
        { name: "Termine", data: buckets.map((bucket) => bucket.appointments) },
        { name: "Ämtli", data: buckets.map((bucket) => bucket.amtli) },
        { name: "Aufgaben", data: buckets.map((bucket) => bucket.tasks) },
      ]
    },
    selectedEmployeeWeekdayOptions() {
      return {
        ...this.chartBase(),
        chart: { ...this.chartBase().chart, stacked: true },
        colors: ["#2587e8", "#a78bfa", "#f59e0b"],
        plotOptions: { bar: { borderRadius: 4, columnWidth: "56%" } },
        dataLabels: { enabled: false },
        xaxis: { categories: WEEKDAYS, labels: { style: { colors: this.chartText } } },
        yaxis: { min: 0, forceNiceScale: true, labels: { style: { colors: this.chartText } } },
        grid: { borderColor: this.chartGrid },
        legend: { position: "top", labels: { colors: this.chartText } },
        tooltip: { shared: true, intersect: false, theme: this.isDark ? "dark" : "light" },
      }
    },
    selectedEmployeeCenterRows() {
      const centers = new Map()
      this.selectedEmployeeRows.forEach((row) => {
        const key = row.studio || "Unbekanntes Center"
        if (!centers.has(key)) {
          centers.set(key, { name: key, appointments: 0, workItems: 0, minutes: 0 })
        }
        const center = centers.get(key)
        if (row.entryType === "appointment") center.appointments += 1
        else center.workItems += 1
        center.minutes += Number(row.durationMinutes || 0)
      })
      return [...centers.values()].sort((a, b) => b.appointments + b.workItems - (a.appointments + a.workItems) || a.name.localeCompare(b.name, "de"))
    },
    selectedEmployeeCenterSeries() {
      return [
        { name: "Termine", data: this.selectedEmployeeCenterRows.map((row) => row.appointments) },
        { name: "Arbeitsaufträge", data: this.selectedEmployeeCenterRows.map((row) => row.workItems) },
      ]
    },
    selectedEmployeeCenterOptions() {
      return {
        ...this.chartBase(),
        chart: { ...this.chartBase().chart, stacked: true },
        colors: ["#2587e8", "#a78bfa"],
        plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: "52%" } },
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.selectedEmployeeCenterRows.map((row) => row.name),
          labels: { style: { colors: this.chartText } },
        },
        yaxis: { labels: { style: { colors: this.chartText }, maxWidth: 150 } },
        grid: { borderColor: this.chartGrid },
        legend: { position: "top", labels: { colors: this.chartText } },
        tooltip: { shared: true, intersect: false, theme: this.isDark ? "dark" : "light" },
      }
    },
    selectedEmployeeTopEntries() {
      const entries = new Map()
      this.selectedEmployeeRows.forEach((row) => {
        const key = `${row.entryType}:${row.category}:${row.title || "Ohne Titel"}`
        if (!entries.has(key)) {
          entries.set(key, {
            key,
            title: row.title || "Ohne Titel",
            category: row.category,
            entryType: row.entryType,
            count: 0,
            minutes: 0,
          })
        }
        const entry = entries.get(key)
        entry.count += 1
        entry.minutes += Number(row.durationMinutes || 0)
      })
      return [...entries.values()].sort((a, b) => b.count - a.count || b.minutes - a.minutes).slice(0, 6)
    },
    employeeRawHeaders() {
      return [
        { text: "Datum", value: "date" },
        { text: "Start", value: "start" },
        { text: "Typ", value: "entryType" },
        { text: "Titel", value: "title" },
        { text: "Center", value: "studio" },
      ]
    },
    selectedEmployeeWarnings() {
      const employee = this.employeeDetail
      if (!employee) return []
      const warnings = []
      if (employee.outsideShiftAppointments) {
        warnings.push({
          icon: "mdi-calendar-alert",
          color: "#f59e0b",
          text: `${employee.outsideShiftAppointments} Termine sind in diesem Center nicht vollständig durch eine Netto-Schicht gedeckt (${(
            employee.outsideShiftMinutes / 60
          ).toFixed(1)}h).`,
        })
      }
      if (employee.doubleBookings) {
        warnings.push({
          icon: "mdi-calendar-multiple",
          color: "#ef4444",
          text: `${employee.doubleBookings} Termine sind von Parallelbelegungen betroffen.`,
        })
      }
      if (employee.taskAppointmentConflicts) {
        warnings.push({
          icon: "mdi-clipboard-alert-outline",
          color: "#a78bfa",
          text: `${employee.taskAppointmentConflicts} Arbeitsaufträge überschneiden sich mit Terminen.`,
        })
      }
      if (employee.outsideShiftWorkItems) {
        warnings.push({
          icon: "mdi-clipboard-clock-outline",
          color: "#f59e0b",
          text: `${employee.outsideShiftWorkItems} Arbeitsaufträge sind in diesem Center nicht vollständig durch eine Netto-Schicht gedeckt (${(
            employee.outsideShiftWorkItemMinutes / 60
          ).toFixed(1)}h).`,
        })
      }
      return warnings
    },
    chartText() {
      return this.isDark ? "#cbd5e1" : "#64748b"
    },
    chartGrid() {
      return this.isDark ? "rgba(148,163,184,.14)" : "rgba(100,116,139,.14)"
    },
    panelColor() {
      return this.isDark ? "#202124" : "#ffffff"
    },
  },
  watch: {
    requestKey: {
      immediate: true,
      handler() {
        this.queueLoadData()
      },
    },
  },
  beforeDestroy() {
    if (this.loadTimer) clearTimeout(this.loadTimer)
    this.requestSerial += 1
  },
  methods: {
    queueLoadData() {
      this.requestSerial += 1
      if (this.loadedRequestKey && this.loadedRequestKey !== this.requestKey) this.data = null
      if (this.loadTimer) clearTimeout(this.loadTimer)
      this.loadTimer = setTimeout(() => {
        this.loadTimer = null
        this.loadData()
      }, 180)
    },
    async loadData() {
      if (this.loadTimer) {
        clearTimeout(this.loadTimer)
        this.loadTimer = null
      }
      const requestId = ++this.requestSerial
      const studioIds = (this.studios || []).map((studio) => studio?._id || studio).filter(Boolean)
      const range = this.datePickerRange || []
      if (!studioIds.length || !range[0] || !range[1]) {
        this.data = null
        this.loadedRequestKey = ""
        this.loading = false
        this.error = "Bitte Center und Zeitraum auswählen."
        return
      }
      const requestedKey = this.requestKey
      if (this.loadedRequestKey && this.loadedRequestKey !== requestedKey) {
        this.data = null
      }
      this.loading = true
      this.error = ""
      try {
        const result = await api.shiftManagement.fetchWorkCalendarStatistics({ studios: studioIds, range })
        if (requestId !== this.requestSerial) return
        this.data = result
        if (this.selectedEmployee) {
          const refreshedEmployee = (result?.employeeStats || []).find((employee) => String(employee.id) === String(this.selectedEmployee.id))
          if (refreshedEmployee) this.selectedEmployee = refreshedEmployee
          else this.employeeDialog = false
        }
        if (this.selectedTrafficDate && !(result?.traffic?.daily || []).some((day) => day.date === this.selectedTrafficDate)) {
          this.trafficDayDialog = false
          this.selectedTrafficDate = ""
        }
        if (this.selectedCustomerKey && !this.customerAnalysisRows.some((customer) => String(customer.key) === String(this.selectedCustomerKey))) {
          this.customerDialog = false
          this.selectedCustomerKey = ""
        }
        this.loadedRequestKey = requestedKey
      } catch (error) {
        if (requestId !== this.requestSerial) return
        this.error = error?.response?.data?.message || "Die Statistik konnte nicht geladen werden."
        if (this.data && this.$toast) this.$toast.error(this.error)
        console.error("[WORKCALENDAR_INSIGHTS_LOAD]", error)
      } finally {
        if (requestId === this.requestSerial) this.loading = false
      }
    },
    chartBase() {
      return {
        chart: {
          toolbar: { show: false },
          foreColor: this.chartText,
          fontFamily: "Inter, Roboto, sans-serif",
          animations: { speed: 380 },
          background: "transparent",
        },
        theme: { mode: this.isDark ? "dark" : "light" },
      }
    },
    areaOptions(colors) {
      return {
        ...this.chartBase(),
        colors,
        stroke: { curve: "smooth", width: 2.5 },
        fill: {
          type: "gradient",
          gradient: { shadeIntensity: 0.4, opacityFrom: 0.34, opacityTo: 0.03, stops: [0, 92, 100] },
        },
        dataLabels: { enabled: false },
        xaxis: { type: "datetime", labels: { style: { colors: this.chartText } } },
        yaxis: { min: 0, forceNiceScale: true, labels: { style: { colors: this.chartText } } },
        grid: { borderColor: this.chartGrid, strokeDashArray: 4 },
        legend: { position: "top", horizontalAlign: "right", labels: { colors: this.chartText } },
        tooltip: { theme: this.isDark ? "dark" : "light", x: { format: "dd.MM.yyyy" } },
      }
    },
    heatmapSeries(metric) {
      const cells = this.data?.hourlyHeatmap || []
      return WEEKDAYS_LONG.map((name, weekday) => ({
        name,
        data: Array.from({ length: 17 }, (_, offset) => {
          const hour = offset + 6
          const cell = cells.find((entry) => entry.weekday === weekday && entry.hour === hour)
          return { x: `${String(hour).padStart(2, "0")}:00`, y: Number(cell?.[metric] || 0) }
        }),
      }))
    },
    heatmapOptions(color, label, decimals = false) {
      return {
        ...this.chartBase(),
        colors: [color],
        dataLabels: {
          enabled: true,
          formatter: (value) => (decimals ? Number(value).toFixed(1) : value || ""),
          style: { colors: [this.isDark ? "#f8fafc" : "#173047"], fontSize: "12px" },
        },
        plotOptions: {
          heatmap: {
            radius: 4,
            shadeIntensity: 0.62,
            enableShades: true,
            colorScale: { min: 0 },
          },
        },
        xaxis: { labels: { style: { colors: this.chartText }, rotate: -45 } },
        yaxis: { labels: { style: { colors: this.chartText } } },
        grid: { padding: { left: 4, right: 4 } },
        legend: { show: false },
        tooltip: {
          theme: this.isDark ? "dark" : "light",
          y: { formatter: (value) => `${decimals ? Number(value).toFixed(1) : value} ${label}` },
        },
      }
    },
    percent(value, total) {
      return total ? Number(((Number(value || 0) / total) * 100).toFixed(1)) : 0
    },
    weekdayIndex(date) {
      if (!date) return -1
      if (this.$moment) {
        const parsed = this.$moment(date, "YYYY-MM-DD", true)
        return parsed.isValid() ? parsed.isoWeekday() - 1 : -1
      }
      const parsed = new Date(`${date}T12:00:00`)
      if (Number.isNaN(parsed.getTime())) return -1
      return (parsed.getDay() + 6) % 7
    },
    formatShortDate(date) {
      if (!date) return "–"
      return this.$moment ? this.$moment(date, "YYYY-MM-DD").format("DD.MM.YY") : date
    },
    formatDay(date) {
      if (!date) return ""
      return this.$moment ? this.$moment(date, "YYYY-MM-DD").format("ddd") : ""
    },
    feedbackTagLabel(tag) {
      return (
        {
          friendly: "Freundlich",
          competent: "Kompetent",
          helpful: "Hilfreich",
          motivating: "Motivierend",
          punctual: "Pünktlich",
          pleasant: "Angenehm",
        }[String(tag || "")] || String(tag || "")
      )
    },
    feedbackEmployeeName(employeeId) {
      if (!employeeId) return "Nicht zugeordnet"
      return (this.data?.employeeStats || []).find((employee) => String(employee.id) === String(employeeId))?.name || "Nicht zugeordnet"
    },
    feedbackCenterName(studioId) {
      if (!studioId) return this.meta.studioCount === 1 ? this.studioLabel : "Center nicht zugeordnet"
      return (this.data?.centers || []).find((center) => String(center.id) === String(studioId))?.name || "Center nicht zugeordnet"
    },
    trafficHourLabel(hour) {
      const start = Math.max(0, Math.min(23, Number(hour || 0)))
      const end = (start + 1) % 24
      return `${String(start).padStart(2, "0")}:00–${String(end).padStart(2, "0")}:00`
    },
    openTrafficDay(date) {
      if (!date) return
      this.selectedTrafficDate = date
      this.trafficDayDialog = true
    },
    openCustomer(customerOrKey) {
      const key = typeof customerOrKey === "object" ? customerOrKey?.key || customerOrKey?.id : customerOrKey
      if (!key) return
      this.selectedCustomerKey = String(key)
      this.customerDialog = true
    },
    appointmentStatusLabel(status) {
      return (
        {
          planned: "Geplant",
          checkedin: "Eingecheckt",
          done: "Durchgeführt",
          noshow: "Nicht gekommen",
          cancelled: "Abgesagt",
        }[String(status || "planned")] || "Geplant"
      )
    },
    appointmentStatusTone(status) {
      return (
        {
          planned: "neutral",
          checkedin: "good",
          done: "good",
          noshow: "danger",
          cancelled: "warning",
        }[String(status || "planned")] || "neutral"
      )
    },
    translateAppointmentType(key, fallback) {
      return APPOINTMENT_TYPE_LABELS[String(key || "").toLocaleLowerCase("de-CH")] || fallback || key || "Termin"
    },
    categoryLabel(category) {
      return { amtli: "Ämtli", task: "Aufgabe", info: "Info", unknown: "Nicht klassifiziert", appointment: "Termin" }[category] || "Aufgabe"
    },
    categoryIcon(category) {
      return (
        {
          amtli: "mdi-broom",
          task: "mdi-clipboard-text-outline",
          info: "mdi-information-outline",
          unknown: "mdi-help-rhombus-outline",
        }[category] || "mdi-clipboard-outline"
      )
    },
    frequencyLabel(frequency) {
      return { daily: "Täglich", weekly: "Wöchentlich", monthly: "Monatlich", once: "Einmalig" }[frequency] || "Einmalig"
    },
    rawTypeLabel(item) {
      return item.entryType === "appointment" ? "Termin" : this.categoryLabel(item.category)
    },
    employeeImage(employee) {
      return employee?.picture ? `/img/profilbilder/${employee.picture}` : "/img/default-avatar.png"
    },
    openEmployee(employee) {
      this.selectedEmployee = employee
      this.employeeDialogTab = 0
      this.employeeEntryFilter = "appointment"
      this.employeeDialog = true
    },
    exportWorkbook() {
      if (!this.data) return
      try {
        const XLSX = require("xlsx")
        const workbook = XLSX.utils.book_new()
        const summaryRows = [
          { Kennzahl: "Zeitraum", Wert: this.rangeLabel },
          { Kennzahl: "Center", Wert: this.studioLabel },
          { Kennzahl: "Termine gesamt", Wert: this.totals.totalAppointments || 0 },
          { Kennzahl: "Nicht-gekommen-Quote", Wert: `${this.totals.noShowRate || 0}%` },
          { Kennzahl: "Nicht gekommen", Wert: this.totals.noshow || 0 },
          { Kennzahl: "Abgesagt", Wert: this.totals.cancelled || 0 },
          { Kennzahl: "Ämtli", Wert: this.totals.amtli?.total || 0 },
          { Kennzahl: "Aufgaben", Wert: this.totals.tasks?.total || 0 },
          {
            Kennzahl: "Weitere Arbeitsaufträge",
            Wert: Number(this.totals.infoItems?.total || 0) + Number(this.totals.unclassifiedWorkItems?.total || 0),
          },
          { Kennzahl: "Netto-Arbeitsstunden", Wert: this.totals.workHours || 0 },
          { Kennzahl: "Abgezogene Krankheitsstunden", Wert: this.totals.sickHours || 0 },
          { Kennzahl: "Kalendergebundene Stunden", Wert: this.totals.productiveHours || 0 },
          { Kennzahl: "Kalenderbindung", Wert: `${this.totals.utilizationRate || 0}%` },
          { Kennzahl: "Besuche", Wert: this.traffic.checkins || 0 },
          { Kennzahl: "Aktive Mitglieder", Wert: this.traffic.activeMembers || 0 },
          { Kennzahl: "Ø Besuche je Mitglied", Wert: this.traffic.visitsPerMember || 0 },
          {
            Kennzahl: "Kundentermin-Stunden je 100 Besuche",
            Wert: this.traffic.customerAppointmentHoursPer100Visits || 0,
          },
          {
            Kennzahl: "Trainerpoint-Stunden je 100 Besuche",
            Wert: this.traffic.trainerpointHoursPer100Visits || 0,
          },
          { Kennzahl: "Termine außerhalb Schicht", Wert: this.totals.outsideShiftAppointments || 0 },
          { Kennzahl: "Parallel belegte Termine", Wert: this.totals.doubleBookings || 0 },
        ]
        if (this.canViewCustomerFeedback) {
          summaryRows.push(
            { Kennzahl: "Kundenfeedback Ø", Wert: this.feedbackSummary.ratings ? Number(this.feedbackSummary.average).toFixed(1) : "–" },
            { Kennzahl: "Bewertungen", Wert: this.feedbackSummary.ratings || 0 },
            { Kennzahl: "Feedback-Rücklauf", Wert: `${this.feedbackSummary.responseRate || 0}%` },
            { Kennzahl: "Positive Bewertungen", Wert: `${this.feedbackSummary.favorableRate || 0}%` },
            { Kennzahl: "Kritische Bewertungen", Wert: this.feedbackSummary.critical || 0 },
          )
        }
        const teamRows = this.employeeAnalysisRows.map((employee) => ({
          Mitarbeiter: employee.name,
          Schichten: employee.shifts,
          Einsatztage: employee.workDays,
          Wochenendtage: employee.weekendWorkDays,
          "Ø Netto-Stunden je Einsatztag": employee.averageWorkdayHours,
          Center: employee.centerCount,
          "Netto-Stunden": employee.workHours,
          Krankheitsstunden: employee.sickHours,
          "Kalendergebundene Stunden": employee.productiveHours,
          "Nicht gebundene Stunden": employee.unboundHours,
          "Kalenderbindung %": employee.utilizationRate,
          Termine: employee.appointments,
          "Nicht gekommen": employee.noShows,
          Abgesagt: employee.cancelled,
          Ämtli: employee.amtli,
          Aufgaben: employee.tasks,
          Arbeitsaufträge:
            Number(employee.amtli || 0) + Number(employee.tasks || 0) + Number(employee.infoItems || 0) + Number(employee.unclassified || 0),
          "Außerhalb Schicht": employee.outsideShiftAppointments,
          "Aufgaben außerhalb Schicht": employee.outsideShiftWorkItems,
          Parallelbelegungen: employee.doubleBookings,
          "Task-Terminkonflikte": employee.taskAppointmentConflicts,
          ...(this.canViewCustomerFeedback
            ? {
                "Kundenfeedback Ø": employee.feedbackRatings ? employee.feedbackAverage : "–",
                Bewertungen: employee.feedbackRatings,
                "Feedback-Rücklauf %": employee.feedbackResponseRate,
              }
            : {}),
        }))
        const templateRows = (this.data.templateStats || []).map((template) => ({
          Titel: template.title,
          Typ: this.categoryLabel(template.category),
          Frequenz: this.frequencyLabel(template.frequency),
          "Dauer Min.": template.duration,
          Einträge: template.planned,
          "Gesamtzeit (Std.)": Number(((Number(template.planned || 0) * Number(template.duration || 0)) / 60).toFixed(1)),
        }))
        const centerRows = (this.data.centers || []).map((center) => ({
          Center: center.name,
          Schichten: center.shifts,
          "Netto-Stunden": center.workHours,
          Krankheitsstunden: center.sickHours,
          "Produktive Stunden": center.productiveHours,
          "Auslastung %": center.utilizationRate,
          Termine: center.appointments,
          "Nicht gekommen %": center.noShowRate,
          Ämtli: center.amtli,
          Aufgaben: center.tasks,
          Arbeitsaufträge: Number(center.amtli || 0) + Number(center.tasks || 0) + Number(center.infoItems || 0) + Number(center.unclassified || 0),
          "Außerhalb Schicht": center.outsideShiftAppointments,
          "Aufgaben außerhalb Schicht": center.outsideShiftWorkItems,
          ...(this.canViewCustomerFeedback
            ? {
                "Kundenfeedback Ø": center.customerFeedback?.ratings ? center.customerFeedback.average : "–",
                Bewertungen: center.customerFeedback?.ratings || 0,
                "Feedback-Rücklauf %": center.customerFeedback?.responseRate || 0,
              }
            : {}),
        }))
        const feedbackRows = this.canViewCustomerFeedback
          ? (this.feedbackSummary.feedback || []).map((feedback) => ({
              Datum: feedback.date,
              Kunde: feedback.customerName || "Unbekannter Kunde",
              Termin: feedback.title,
              Mitarbeiter: this.feedbackEmployeeName(feedback.employeeId),
              Center: this.feedbackCenterName(feedback.studioId),
              Sterne: feedback.stars,
              Stichworte: (feedback.tags || []).map((tag) => this.feedbackTagLabel(tag)).join(", "),
              Kommentar: feedback.comment || "",
            }))
          : []
        const detailRows = (this.data.rawRows || []).map((row) => ({
          Datum: row.date,
          Start: row.start,
          Ende: row.end,
          Typ: this.rawTypeLabel(row),
          Titel: row.title,
          Mitarbeiter: row.employee,
          Kunde: row.customer,
          Dauer: row.durationMinutes,
          Center: row.studio,
        }))
        const trafficCenterRows = (this.traffic.centers || []).map((center) => ({
          Center: center.name,
          Besuche: center.checkins,
          "Aktive Mitglieder": center.activeMembers,
          "Mitgliederstand vom": center.memberSnapshotDate,
          "Ø Besuche je Mitglied": center.visitsPerMember,
          "Kundentermin-Stunden je 100 Besuche": center.customerAppointmentHoursPer100Visits,
          "Trainerpoint-Stunden je 100 Besuche": center.trainerpointHoursPer100Visits,
          "Kundentermin-Stunden": center.customerAppointmentHours,
          "Trainerpoint-Stunden": center.trainerpointHours,
        }))
        const trafficDailyRows = (this.traffic.daily || []).map((day) => ({
          Datum: day.date,
          Besuche: day.checkins,
          "Kundentermine (Std.)": day.customerAppointmentHours,
          "Trainerpoint (Std.)": day.trainerpointHours,
          "Netto-Schichtzeit (Std.)": day.workHours,
        }))
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(summaryRows), "Übersicht")
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(teamRows), "Team")
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(templateRows), "Ämtli-Aufgaben")
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(centerRows), "Center")
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(trafficCenterRows), "Besucher-Center")
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(trafficDailyRows), "Besucher-Tage")
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(detailRows), "Einzeldaten")
        if (this.canViewCustomerFeedback) {
          XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(feedbackRows), "Kundenfeedback")
        }
        XLSX.writeFile(workbook, `Center_Statistik_${this.meta.startDate || "Start"}_${this.meta.endDate || "Ende"}.xlsx`)
      } catch (error) {
        console.error("[WORKCALENDAR_INSIGHTS_EXPORT]", error)
        if (this.$toast) this.$toast.error("Export konnte nicht erstellt werden.")
      }
    },
  },
}
</script>

<style scoped>
.wc-insights {
  --surface: #ffffff;
  --surface-soft: #f6f8fa;
  --surface-muted: #edf2f5;
  --border: #dfe7ec;
  --text: #162332;
  --muted: #718096;
  --shadow: 0 12px 34px rgba(29, 50, 68, 0.08);
  position: relative;
  width: 100%;
  max-width: none;
  min-height: calc(100vh - 96px);
  margin: 0;
  padding: 0;
  color: var(--text);
  background: transparent;
  border-radius: 0;
}

.wc-insights--dark {
  --surface: #202124;
  --surface-soft: #18191b;
  --surface-muted: #2a2c30;
  --border: rgba(255, 255, 255, 0.09);
  --text: #f3f4f5;
  --muted: #9aa0a8;
  --shadow: 0 16px 40px rgba(0, 0, 0, 0.24);
  background: transparent;
  color-scheme: dark;
}

.insights-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 82px;
  padding: 10px 2px 14px;
  margin-bottom: 14px;
  color: var(--text);
  background: transparent;
  border: 0;
  border-bottom: 1px solid var(--border);
  border-radius: 0;
  box-shadow: none;
}

.hero-main,
.hero-actions,
.hero-meta,
.panel-heading,
.table-tools,
.primary-cell,
.employee-dialog-person {
  display: flex;
  align-items: center;
}

.hero-main {
  min-width: 0;
  gap: 13px;
}

.hero-icon {
  display: grid;
  flex: 0 0 52px;
  width: 52px;
  height: 52px;
  place-items: center;
  background: linear-gradient(145deg, #21b66f, #108a54);
  border-radius: 15px;
  box-shadow: 0 10px 22px rgba(22, 167, 101, 0.25);
}

.hero-copy {
  min-width: 0;
}

.hero-copy h1 {
  margin: 1px 0 4px;
  font-size: clamp(20px, 2vw, 27px);
  line-height: 1.05;
  letter-spacing: -0.025em;
}

.hero-eyebrow,
.panel-eyebrow {
  color: #16a765;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.16em;
}

.hero-eyebrow span {
  padding: 2px 6px;
  margin-left: 5px;
  color: #fff;
  background: #16a765;
  border-radius: 20px;
  font-size: 12px;
  letter-spacing: 0.08em;
}

.hero-meta {
  flex-wrap: wrap;
  gap: 6px 14px;
  color: var(--muted);
  font-size: 12px;
}

.hero-meta span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.hero-meta .v-icon {
  color: currentColor;
}

.hero-actions {
  gap: 6px;
}

.hero-action {
  color: var(--text) !important;
  background: var(--surface-soft);
  border: 1px solid var(--border);
}

.state-panel {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
  min-height: 280px;
  padding: 28px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 18px;
  text-align: left;
}

.state-panel div:not(.loading-orbit) {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.state-panel strong {
  font-size: 18px;
}

.state-panel span {
  color: var(--muted);
}

.loading-orbit {
  display: grid;
  width: 62px;
  height: 62px;
  place-items: center;
  background: rgba(22, 167, 101, 0.1);
  border-radius: 50%;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
}

.kpi-card {
  position: relative;
  min-width: 0;
  min-height: 145px;
  padding: 14px;
  overflow: hidden;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 16px;
  box-shadow: 0 7px 20px rgba(30, 49, 65, 0.05);
}

.kpi-card::before {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 3px;
  content: "";
  background: var(--accent);
}

.kpi-card::after {
  position: absolute;
  top: -40px;
  right: -40px;
  width: 100px;
  height: 100px;
  content: "";
  background: var(--accent);
  border-radius: 50%;
  opacity: 0.055;
}

.kpi-topline {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 30px;
}

.kpi-icon {
  display: grid;
  width: 31px;
  height: 31px;
  place-items: center;
  background: color-mix(in srgb, var(--accent) 10%, transparent);
  border-radius: 9px;
}

.kpi-badge {
  padding: 3px 7px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.badge--good {
  color: #0d8c54;
  background: rgba(22, 167, 101, 0.11);
}

.badge--warning {
  color: #b56d00;
  background: rgba(245, 158, 11, 0.13);
}

.badge--danger {
  color: #d63232;
  background: rgba(239, 68, 68, 0.11);
}

.kpi-value {
  margin-top: 7px;
  font-size: clamp(25px, 2.4vw, 34px);
  font-weight: 900;
  line-height: 1;
  letter-spacing: -0.045em;
}

.kpi-label {
  margin-top: 7px;
  font-size: 12px;
  font-weight: 800;
}

.kpi-sub {
  margin-top: 3px;
  overflow: hidden;
  color: var(--muted);
  font-size: 12px;
  line-height: 1.35;
  text-overflow: ellipsis;
}

.dashboard-shell {
  margin-top: 13px;
  overflow: visible;
  background: transparent;
  border: 0;
  border-radius: 0;
  box-shadow: none;
}

.insights-tabs {
  padding: 4px 0 6px;
  background: transparent !important;
  border-bottom: 1px solid var(--border);
}

.insights-tabs ::v-deep .v-tabs-bar {
  background: transparent !important;
}

.insights-tabs ::v-deep .v-tab {
  min-width: auto;
  min-height: 42px;
  padding: 0 15px;
  margin-right: 4px;
  color: var(--muted) !important;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0;
  opacity: 1;
  text-transform: none;
}

.insights-tabs ::v-deep .v-tab .v-icon {
  color: currentColor !important;
}

.insights-tabs ::v-deep .v-tab--active {
  color: #22c878 !important;
  background: rgba(34, 200, 120, 0.11);
}

.wc-insights--dark .insights-tabs ::v-deep .v-tab:not(.v-tab--active),
.wc-insights--dark .insights-tabs ::v-deep .v-tab:not(.v-tab--active) .v-icon {
  color: var(--muted) !important;
  opacity: 1;
}

.insights-tab-items {
  background: transparent !important;
}

.tab-content {
  padding: 14px 0 0;
}

.content-grid {
  display: grid;
  gap: 12px;
}

.content-grid--wide {
  grid-template-columns: 1fr 1fr 0.85fr;
}

.content-grid--half {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.panel--full {
  grid-column: 1 / -1;
}

.panel {
  min-width: 0;
  padding: 15px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 15px;
}

.panel-heading {
  justify-content: space-between;
  gap: 10px;
  min-height: 42px;
  margin-bottom: 8px;
}

.panel-heading h2 {
  margin: 2px 0 0;
  font-size: 15px;
  line-height: 1.2;
}

.panel-heading--wrap {
  flex-wrap: wrap;
}

.panel-context {
  flex: 0 0 auto;
  color: var(--muted);
  font-size: 12px;
}

.panel-context--interactive {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #2587e8;
  font-weight: 800;
}

.panel-context--interactive .v-icon {
  color: currentColor;
}

.traffic-click-chart {
  cursor: pointer;
}

.donut-wrap {
  position: relative;
}

.donut-caption {
  position: absolute;
  top: 102px;
  left: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  transform: translateX(-50%);
  pointer-events: none;
}

.donut-caption strong {
  font-size: 22px;
}

.donut-caption span {
  color: var(--muted);
  font-size: 12px;
  text-transform: uppercase;
}

.capacity-breakdown {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(105px, 1fr));
  gap: 7px;
}

.capacity-breakdown div {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 0 6px;
  padding: 8px;
  background: var(--surface-soft);
  border-radius: 9px;
}

.capacity-breakdown strong {
  font-size: 12px;
}

.capacity-breakdown small {
  grid-column: 2;
  color: var(--muted);
  font-size: 12px;
}

.dot {
  width: 7px;
  height: 7px;
  grid-row: span 2;
  border-radius: 50%;
}

.dot--blue {
  background: #2587e8;
}

.dot--violet {
  background: #8b5cf6;
}

.dot--green {
  background: #16a765;
}

.dot--red {
  background: #ef4444;
}

.top-days {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.top-day-row {
  display: grid;
  grid-template-columns: 48px minmax(50px, 1fr) minmax(90px, auto);
  align-items: center;
  gap: 9px;
}

.day-date,
.day-values {
  display: flex;
  flex-direction: column;
}

.day-date strong {
  font-size: 12px;
  text-transform: uppercase;
}

.day-date span,
.day-values span {
  color: var(--muted);
  font-size: 12px;
}

.day-values {
  text-align: right;
}

.day-values strong {
  font-size: 12px;
}

.day-bar {
  height: 7px;
  overflow: hidden;
  background: var(--surface-muted);
  border-radius: 10px;
}

.day-bar span {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, #16a765, #49d291);
  border-radius: inherit;
}

.mini-kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-bottom: 12px;
}

.mini-kpis--traffic {
  grid-template-columns: repeat(6, minmax(0, 1fr));
}

.mini-kpis > div {
  position: relative;
  display: flex;
  min-height: 92px;
  padding: 14px;
  overflow: hidden;
  flex-direction: column;
  justify-content: center;
  background: radial-gradient(circle at 90% 15%, color-mix(in srgb, var(--highlight-color) 18%, transparent), transparent 38%), var(--surface);
  border: 1px solid var(--border);
  border-radius: 13px;
}

.mini-kpis > div::before {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 3px;
  content: "";
  background: var(--highlight-color);
}

.mini-kpis span {
  color: var(--muted);
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.mini-kpis strong {
  max-width: calc(100% - 42px);
  margin-top: 2px;
  font-size: 24px;
  line-height: 1.1;
}

.mini-kpis .mini-kpi-value--text {
  font-size: clamp(14px, 1.35vw, 20px);
}

.mini-kpis small {
  max-width: calc(100% - 42px);
  margin-top: 4px;
  overflow: hidden;
  color: var(--muted);
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mini-kpis .v-icon {
  position: absolute;
  right: 12px;
  font-size: 28px;
  opacity: 0.52;
}

.traffic-method {
  display: flex;
  min-height: auto;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 18px;
  background: linear-gradient(135deg, rgba(37, 135, 232, 0.08), rgba(22, 167, 101, 0.05)), var(--surface);
}

.traffic-method > div {
  min-width: 0;
}

.traffic-method strong {
  display: block;
  margin-bottom: 4px;
  font-size: 14px;
}

.traffic-method p {
  max-width: 1100px;
  margin: 0;
  color: var(--muted);
  font-size: 12px;
  line-height: 1.55;
}

.traffic-empty {
  min-height: 180px;
}

.team-summary-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 12px;
}

.team-summary-card {
  position: relative;
  display: flex;
  min-height: 104px;
  align-items: center;
  gap: 11px;
  padding: 14px;
  overflow: hidden;
  background: radial-gradient(circle at 100% 0, color-mix(in srgb, var(--team-card-color) 14%, transparent), transparent 46%), var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
}

.team-summary-card::before {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 3px;
  content: "";
  background: var(--team-card-color);
}

.team-summary-icon {
  display: grid;
  flex: 0 0 38px;
  width: 38px;
  height: 38px;
  place-items: center;
  background: color-mix(in srgb, var(--team-card-color) 12%, transparent);
  border-radius: 11px;
}

.team-summary-card > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.team-summary-card small {
  overflow: hidden;
  color: var(--muted);
  font-size: 12px;
  font-weight: 800;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}

.team-summary-card strong {
  margin-top: 2px;
  font-size: clamp(19px, 1.35vw, 25px);
  line-height: 1.1;
}

.team-summary-card > div > span {
  margin-top: 4px;
  overflow: hidden;
  color: var(--muted);
  font-size: 12px;
  line-height: 1.25;
  text-overflow: ellipsis;
}

.analysis-caption {
  margin: 2px 0 0;
  color: var(--muted);
  font-size: 12px;
  line-height: 1.45;
}

.category-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(215px, 1fr));
  gap: 10px;
  margin-bottom: 12px;
}

.category-card {
  display: flex;
  min-width: 0;
  min-height: 108px;
  align-items: center;
  gap: 11px;
  padding: 13px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
}

.category-icon {
  display: grid;
  flex: 0 0 39px;
  width: 39px;
  height: 39px;
  place-items: center;
  background: color-mix(in srgb, var(--category-color) 10%, transparent);
  border-radius: 11px;
}

.category-main {
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  flex-direction: column;
}

.category-main span {
  color: var(--muted);
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.category-main strong {
  font-size: 23px;
  line-height: 1.1;
}

.category-main small {
  color: var(--muted);
  font-size: 12px;
  line-height: 1.35;
  white-space: normal;
}

.category-rate {
  display: grid;
  flex: 0 0 39px;
  height: 39px;
  place-items: center;
  color: var(--category-color);
  background: color-mix(in srgb, var(--category-color) 8%, transparent);
  border: 2px solid color-mix(in srgb, var(--category-color) 35%, transparent);
  border-radius: 50%;
  font-size: 12px;
  font-weight: 900;
}

.metric-toggle {
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 9px;
}

.metric-toggle ::v-deep .v-btn {
  color: var(--muted);
  background: var(--surface-soft) !important;
  border: 0 !important;
  font-size: 12px;
  text-transform: none;
}

.metric-toggle ::v-deep .v-btn--active {
  color: #fff !important;
  background: #16a765 !important;
}

.table-tools {
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.table-search {
  width: 230px;
  max-width: 100%;
}

.table-search--small {
  width: 170px;
}

.category-select {
  width: 190px;
}

.table-search ::v-deep .v-input__slot,
.category-select ::v-deep .v-input__slot {
  min-height: 36px !important;
  margin: 0;
  background: var(--surface-soft) !important;
  border-radius: 9px !important;
}

.insights-table {
  color: var(--text) !important;
  background: transparent !important;
}

.insights-table ::v-deep .v-data-table__wrapper {
  border: 1px solid var(--border);
  border-radius: 11px;
}

.insights-table ::v-deep table {
  background: var(--surface);
}

.insights-table ::v-deep th {
  height: 46px !important;
  color: var(--muted) !important;
  background: var(--surface-soft);
  border-color: var(--border) !important;
  font-size: 12px !important;
  font-weight: 900 !important;
  letter-spacing: 0.035em;
  text-transform: uppercase;
  white-space: nowrap;
}

.insights-table ::v-deep td {
  height: 56px !important;
  color: var(--text) !important;
  border-color: var(--border) !important;
  font-size: clamp(12px, 0.72vw, 13px) !important;
  line-height: 1.35;
}

.insights-table ::v-deep .v-data-footer {
  color: var(--muted);
  border-color: var(--border);
  font-size: 12px;
}

.insights-table ::v-deep .v-data-footer__select,
.insights-table ::v-deep .v-data-footer__pagination,
.insights-table ::v-deep .v-select__selection {
  font-size: 12px;
}

.wc-insights--dark .insights-table ::v-deep th,
.wc-insights--dark .primary-cell small {
  color: #b8bec7 !important;
}

.clickable-table ::v-deep tbody tr {
  cursor: pointer;
}

.clickable-table ::v-deep tbody tr:hover {
  background: rgba(22, 167, 101, 0.055) !important;
}

.primary-cell {
  min-width: 150px;
  gap: 9px;
}

.primary-cell > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.primary-cell strong {
  overflow: hidden;
  font-size: clamp(12px, 0.72vw, 13px);
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.primary-cell small {
  overflow: hidden;
  color: var(--muted);
  font-size: clamp(12px, 0.72vw, 13px);
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.employee-cell img {
  object-fit: cover;
}

.type-icon {
  display: grid;
  flex: 0 0 31px;
  width: 31px;
  height: 31px;
  place-items: center;
  color: #64748b;
  background: rgba(100, 116, 139, 0.1);
  border-radius: 9px;
}

.type-icon .v-icon {
  color: currentColor;
}

.type-icon--appointment {
  color: #2587e8;
  background: rgba(37, 135, 232, 0.1);
}

.type-icon--amtli {
  color: #7c3aed;
  background: rgba(139, 92, 246, 0.1);
}

.type-icon--trainerpoint {
  color: #0f9f7d;
  background: rgba(32, 201, 160, 0.14);
}

.type-icon--task {
  color: #d78200;
  background: rgba(245, 158, 11, 0.1);
}

.type-icon--info {
  color: #07938f;
  background: rgba(6, 167, 162, 0.1);
}

.type-icon--center {
  color: #16a765;
  background: rgba(22, 167, 101, 0.1);
}

.category-chip,
.manual-checks,
.alert-badge,
.clean-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 5px 8px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
}

.category-chip--appointment {
  color: #1472cc;
  background: rgba(37, 135, 232, 0.11);
}

.category-chip--amtli {
  color: #7c3aed;
  background: rgba(139, 92, 246, 0.11);
}

.wc-insights--dark .type-icon--amtli,
.employee-dialog--dark .type-icon--amtli {
  color: #ddd6fe;
  background: rgba(139, 92, 246, 0.28);
  border: 1px solid rgba(196, 181, 253, 0.42);
}

.wc-insights--dark .category-chip--amtli,
.employee-dialog--dark .category-chip--amtli {
  color: #f3e8ff;
  background: rgba(139, 92, 246, 0.3);
  border: 1px solid rgba(216, 180, 254, 0.35);
}

.wc-insights--dark .type-icon--trainerpoint,
.employee-dialog--dark .type-icon--trainerpoint {
  color: #99f6e4;
  background: rgba(20, 184, 166, 0.22);
  border: 1px solid rgba(94, 234, 212, 0.28);
}

.category-chip--task {
  color: #af6900;
  background: rgba(245, 158, 11, 0.13);
}

.category-chip--info {
  color: #07817d;
  background: rgba(6, 167, 162, 0.11);
}

.category-chip--unknown {
  color: #64748b;
  background: rgba(100, 116, 139, 0.11);
}

.calendar-status,
.time-share {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 6px 9px;
  border: 1px solid transparent;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 850;
  line-height: 1.2;
  white-space: nowrap;
}

.calendar-status .v-icon {
  color: currentColor;
}

.calendar-status--planned {
  color: #0d8c54;
  background: rgba(22, 167, 101, 0.11);
  border-color: rgba(22, 167, 101, 0.2);
}

.calendar-status--missing {
  color: #b56d00;
  background: rgba(245, 158, 11, 0.12);
  border-color: rgba(245, 158, 11, 0.23);
}

.time-share--amtli {
  color: #7c3aed;
  background: rgba(139, 92, 246, 0.12);
  border-color: rgba(139, 92, 246, 0.22);
}

.time-share--task {
  color: #a86400;
  background: rgba(245, 158, 11, 0.12);
  border-color: rgba(245, 158, 11, 0.22);
}

.wc-insights--dark .time-share--amtli {
  color: #f3e8ff;
  background: rgba(139, 92, 246, 0.28);
}

.wc-insights--dark .time-share--task {
  color: #fde68a;
  background: rgba(245, 158, 11, 0.2);
}

.manual-checks {
  color: #64748b;
  background: var(--surface-muted);
}

.data-note {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  padding: 9px 11px;
  margin-top: 9px;
  color: var(--muted);
  background: rgba(100, 116, 139, 0.08);
  border-radius: 9px;
  font-size: 12px;
}

.data-note .v-icon {
  color: currentColor;
}

.rate-with-bar {
  display: flex;
  min-width: 90px;
  align-items: center;
  gap: 6px;
}

.rate-with-bar strong {
  width: 40px;
  font-size: 12px;
}

.rate-with-bar > span {
  width: 48px;
  height: 5px;
  overflow: hidden;
  background: var(--surface-muted);
  border-radius: 10px;
}

.rate-with-bar i {
  display: block;
  height: 100%;
  background: #16a765;
  border-radius: inherit;
}

.hours-pill {
  display: inline-flex;
  min-width: 54px;
  justify-content: center;
  padding: 5px 8px;
  color: #7357d9;
  background: rgba(139, 92, 246, 0.12);
  border-radius: 999px;
  font-size: 12px;
  font-weight: 900;
}

.work-item-mix {
  display: inline-flex;
  justify-content: flex-end;
  gap: 5px;
}

.work-item-mix span {
  display: inline-flex;
  min-width: 38px;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 5px 7px;
  color: var(--text);
  background: var(--surface-muted);
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
}

.work-item-mix span:first-child {
  color: #7c3aed;
  background: rgba(139, 92, 246, 0.12);
}

.work-item-mix span:last-child {
  color: #af6900;
  background: rgba(245, 158, 11, 0.13);
}

.work-item-mix .v-icon {
  color: currentColor;
}

.no-show-count {
  display: inline-flex;
  min-width: 34px;
  justify-content: center;
  padding: 5px 8px;
  color: #d93636;
  background: rgba(239, 68, 68, 0.12);
  border-radius: 999px;
  font-size: 12px;
  font-weight: 900;
}

.insights-table ::v-deep .rate-pill {
  display: inline-flex;
  min-width: 48px;
  align-items: center;
  justify-content: center;
  padding: 5px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 900;
  line-height: 1;
}

.insights-table ::v-deep .rate-pill--good {
  color: #0b8b50;
  background: rgba(22, 167, 101, 0.13);
}

.insights-table ::v-deep .rate-pill--warning {
  color: #b36c00;
  background: rgba(245, 158, 11, 0.15);
}

.insights-table ::v-deep .rate-pill--danger {
  color: #d93636;
  background: rgba(239, 68, 68, 0.13);
}

.insights-table ::v-deep .rate-pill--neutral {
  color: var(--muted);
  background: var(--surface-muted);
}

.alert-badges {
  display: flex;
  justify-content: flex-end;
  gap: 4px;
}

.alert-badge .v-icon,
.clean-badge .v-icon {
  color: currentColor;
}

.alert-badge--orange {
  color: #b56d00;
  background: rgba(245, 158, 11, 0.13);
}

.alert-badge--red {
  color: #d63232;
  background: rgba(239, 68, 68, 0.11);
}

.alert-badge--violet {
  color: #7c3aed;
  background: rgba(139, 92, 246, 0.11);
}

.clean-badge {
  color: #0d8c54;
  background: rgba(22, 167, 101, 0.1);
}

.export-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 11px;
}

.export-footer > div {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--muted);
  font-size: 12px;
}

.export-footer .v-icon {
  color: #16a765;
}

.refresh-overlay {
  position: absolute;
  z-index: 20;
  inset: 0;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.34);
  border-radius: inherit;
  backdrop-filter: blur(2px);
}

.wc-insights--dark .refresh-overlay {
  background: rgba(8, 13, 19, 0.42);
}

.employee-dialog {
  --surface: #ffffff;
  --surface-soft: #f5f8fb;
  --surface-muted: #e9f0f5;
  --border: #dce6ed;
  --text: #142230;
  --muted: #66788a;
  overflow: hidden;
  color: var(--text) !important;
  background: var(--surface) !important;
  border-radius: 18px !important;
}

.employee-dialog--dark {
  --surface: #10161d;
  --surface-soft: #18212b;
  --surface-muted: #22303d;
  --border: rgba(203, 213, 225, 0.15);
  --text: #f8fafc;
  --muted: #b5c0cc;
  color-scheme: dark;
}

.employee-dialog--v2 {
  min-height: min(820px, 92vh);
}

.employee-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  background: linear-gradient(135deg, rgba(22, 167, 101, 0.14), transparent);
  border-bottom: 1px solid var(--border);
}

.employee-dialog-focus {
  display: flex;
  margin-left: auto;
  margin-right: 14px;
  align-items: flex-end;
  flex-direction: column;
}

.employee-dialog-focus span {
  color: #16a765;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.08em;
}

.employee-dialog-focus strong {
  font-size: 25px;
  line-height: 1.05;
}

.employee-dialog-focus small {
  color: var(--muted);
  font-size: 12px;
}

.employee-dialog-tabs {
  flex: 0 0 auto;
  padding: 0 16px;
  background: var(--surface) !important;
  border-bottom: 1px solid var(--border);
}

.employee-dialog-tabs ::v-deep .v-tab {
  min-height: 52px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: none;
}

.employee-dialog-tab-items,
.employee-dialog-tab-items ::v-deep .v-window__container,
.employee-dialog-tab-items ::v-deep .v-window-item {
  color: var(--text) !important;
  background: var(--surface) !important;
}

.employee-dialog-pane {
  display: grid;
  gap: 14px;
}

.employee-customer-hero {
  display: grid;
  grid-template-columns: minmax(250px, 0.72fr) minmax(0, 1.8fr);
  gap: 14px;
}

.employee-care-chart,
.employee-customer-kpis > div,
.employee-detail-toolbar {
  background: var(--surface-soft);
  border: 1px solid var(--border);
  border-radius: 14px;
}

.employee-care-chart {
  padding: 8px 16px 14px;
  text-align: center;
}

.employee-care-chart p {
  max-width: 330px;
  margin: -7px auto 0;
  color: var(--muted);
  font-size: 12px;
  line-height: 1.45;
}

.employee-customer-kpis {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.employee-customer-kpis > div {
  display: flex;
  min-height: 118px;
  padding: 15px;
  flex-direction: column;
  justify-content: center;
}

.employee-customer-kpis span {
  display: flex;
  align-items: center;
  gap: 7px;
  color: var(--muted);
  font-size: 12px;
  font-weight: 800;
}

.employee-customer-kpis strong {
  margin-top: 8px;
  font-size: 27px;
  line-height: 1;
}

.employee-customer-kpis small {
  margin-top: 7px;
  color: var(--muted);
  font-size: 12px;
}

.employee-customer-kpi--primary {
  background: linear-gradient(135deg, rgba(22, 185, 120, 0.16), var(--surface-soft)) !important;
}

.employee-customer-kpi--trainerpoint {
  background: linear-gradient(135deg, rgba(32, 201, 160, 0.16), var(--surface-soft)) !important;
}

.employee-customer-kpi--danger {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.11), var(--surface-soft)) !important;
}

.employee-detail-toolbar {
  display: flex;
  padding: 13px 15px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.employee-detail-toolbar > div {
  display: flex;
  flex-direction: column;
}

.employee-detail-toolbar span {
  color: #16a765;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.1em;
}

.employee-detail-toolbar h3 {
  margin: 2px 0 0;
  font-size: 17px;
}

.employee-dialog--v2 .employee-section-heading span {
  font-size: 12px;
}

.employee-dialog--v2 .employee-section-heading h3 {
  font-size: 17px;
}

.employee-dialog--v2 .employee-capacity-legend span,
.employee-dialog--v2 .employee-shift-mix small,
.employee-dialog--v2 .dialog-warnings > div {
  font-size: 12px;
}

.employee-dialog--v2 .employee-capacity-legend strong {
  font-size: 13px;
}

.employee-dialog-person {
  gap: 12px;
}

.employee-dialog-person img {
  object-fit: cover;
}

.employee-dialog-person > div {
  display: flex;
  flex-direction: column;
}

.employee-dialog-person span {
  color: #16a765;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.12em;
}

.employee-dialog-person h2 {
  margin: 1px 0;
  font-size: 21px;
}

.employee-dialog-person small {
  color: var(--muted);
}

.employee-dialog-body {
  padding: 16px !important;
  color: var(--text) !important;
  background: var(--surface) !important;
}

.employee-dialog-score {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
  margin-right: 12px;
}

.employee-dialog-score > div {
  display: flex;
  align-items: flex-end;
  flex-direction: column;
}

.employee-dialog-score span {
  color: var(--muted);
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.08em;
}

.employee-dialog-score strong {
  font-size: 22px;
  line-height: 1.1;
}

.employee-dialog-score small {
  color: var(--muted);
  font-size: 12px;
}

.employee-detail-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 9px;
}

.employee-detail-kpis > div {
  display: flex;
  min-height: 102px;
  padding: 12px;
  flex-direction: column;
  background: var(--surface-soft);
  border: 1px solid var(--border);
  border-radius: 12px;
}

.employee-detail-kpis span {
  display: flex;
  align-items: center;
  gap: 5px;
  color: var(--muted);
  font-size: 12px;
  font-weight: 800;
}

.employee-detail-kpis span .v-icon {
  color: #16a765;
}

.employee-detail-kpis strong {
  margin-top: 6px;
  font-size: 21px;
  line-height: 1.1;
}

.employee-detail-kpis small {
  margin-top: 5px;
  color: var(--muted);
  font-size: 12px;
  line-height: 1.35;
}

.employee-capacity-panel,
.employee-shift-profile,
.employee-insight-panel {
  padding: 14px;
  margin-top: 12px;
  background: var(--surface-soft);
  border: 1px solid var(--border);
  border-radius: 13px;
}

.employee-shift-mix {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 8px;
  margin-top: 12px;
}

.employee-shift-mix > div {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 9px;
  padding: 9px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
}

.employee-shift-mix > div > span {
  display: grid;
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  place-items: center;
  border-radius: 9px;
}

.employee-shift-mix > div > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.employee-shift-mix small {
  color: var(--muted);
  font-size: 12px;
  font-weight: 800;
}

.employee-shift-mix strong {
  color: var(--text);
  font-size: 16px;
}

.employee-shift-mix--empty {
  opacity: 0.48;
}

.employee-section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.employee-section-heading > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.employee-section-heading span {
  color: #16a765;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.1em;
}

.employee-section-heading h3 {
  margin: 2px 0 0;
  color: var(--text);
  font-size: 16px;
}

.employee-section-heading > strong {
  flex: 0 0 auto;
  color: var(--text);
  font-size: 13px;
}

.employee-capacity-bar {
  display: flex;
  height: 14px;
  margin-top: 14px;
  overflow: hidden;
  background: var(--surface-muted);
  border-radius: 999px;
}

.employee-capacity-bar span {
  display: block;
  min-width: 0;
  height: 100%;
  transition: width 220ms ease;
}

.employee-capacity-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 9px 18px;
  margin-top: 11px;
}

.employee-capacity-legend > div {
  display: grid;
  grid-template-columns: 8px auto;
  align-items: center;
  column-gap: 6px;
}

.employee-capacity-legend i {
  grid-row: span 2;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.employee-capacity-legend span {
  color: var(--muted);
  font-size: 12px;
}

.employee-capacity-legend strong {
  font-size: 12px;
}

.employee-analysis-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.employee-insight-panel--full {
  grid-column: 1 / -1;
}

.employee-top-entries {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-top: 12px;
}

.employee-top-entries > div {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 9px;
  padding: 9px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
}

.employee-top-entries > div > div {
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  flex-direction: column;
}

.employee-top-entries strong,
.employee-top-entries small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.employee-top-entries strong {
  color: var(--text);
  font-size: 12px;
}

.employee-top-entries small {
  color: var(--muted);
  font-size: 12px;
}

.employee-top-entries b {
  flex: 0 0 auto;
  color: #16a765;
  font-size: 13px;
}

.employee-empty-analysis {
  padding: 22px;
  color: var(--muted);
  text-align: center;
}

.employee-table-heading {
  margin: 18px 0 8px;
}

.dialog-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: 8px;
}

.dialog-kpis > div {
  display: flex;
  padding: 10px;
  flex-direction: column;
  background: var(--surface-soft);
  border: 1px solid var(--border);
  border-radius: 10px;
}

.dialog-kpis span {
  color: var(--muted);
  font-size: 12px;
  text-transform: uppercase;
}

.dialog-kpis strong {
  margin-top: 3px;
  font-size: 17px;
}

.dialog-warnings {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 7px;
  margin-top: 10px;
}

.dialog-warnings > div {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 9px;
  color: var(--muted);
  background: var(--surface-soft);
  border: 1px solid var(--border);
  border-radius: 9px;
  font-size: 12px;
}

.dialog-section-title {
  margin: 17px 0 8px;
  font-size: 13px;
}

.no-show-highlight-grid,
.traffic-day-summary,
.customer-dialog-kpis {
  display: grid;
  gap: 10px;
}

.no-show-highlight-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin: 4px 0 12px;
}

.no-show-highlight-grid > div,
.traffic-day-summary > div,
.customer-dialog-kpis > div {
  position: relative;
  display: flex;
  min-width: 0;
  overflow: hidden;
  flex-direction: column;
  background: var(--surface-soft);
  border: 1px solid var(--border);
  border-radius: 13px;
}

.no-show-highlight-grid > div {
  min-height: 112px;
  padding: 13px 13px 12px 48px;
  background: linear-gradient(135deg, color-mix(in srgb, var(--no-show-color) 9%, transparent), transparent 58%), var(--surface-soft);
}

.no-show-highlight-grid > div::before,
.traffic-day-summary > div::before,
.customer-dialog-kpis > div::before {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 3px;
  content: "";
}

.no-show-highlight-grid > div::before {
  background: var(--no-show-color);
}

.no-show-highlight-grid .v-icon {
  position: absolute;
  top: 17px;
  left: 13px;
}

.no-show-highlight-grid span,
.traffic-day-summary span,
.customer-dialog-kpis span {
  color: var(--muted);
  font-size: 12px;
  font-weight: 850;
  letter-spacing: 0.035em;
  text-transform: uppercase;
}

.no-show-highlight-grid strong {
  margin-top: 5px;
  font-size: 24px;
  line-height: 1.08;
}

.no-show-highlight-grid .no-show-value--text {
  font-size: 17px;
}

.no-show-highlight-grid small,
.traffic-day-summary small,
.customer-dialog-kpis small {
  margin-top: 5px;
  overflow: hidden;
  color: var(--muted);
  font-size: 12px;
  line-height: 1.35;
  text-overflow: ellipsis;
}

.repeat-customer-strip {
  display: flex;
  align-items: stretch;
  gap: 8px;
  padding: 9px;
  margin-bottom: 12px;
  overflow-x: auto;
  background: color-mix(in srgb, #f97316 7%, var(--surface-soft));
  border: 1px solid color-mix(in srgb, #f97316 25%, var(--border));
  border-radius: 13px;
}

.repeat-customer-strip__title {
  display: flex;
  min-width: 220px;
  padding: 4px 8px;
  align-items: center;
  gap: 8px;
}

.repeat-customer-strip__title > div {
  display: flex;
  flex-direction: column;
}

.repeat-customer-strip__title strong,
.repeat-customer-strip__title small {
  font-size: 12px;
}

.repeat-customer-strip__title small {
  color: var(--muted);
}

.repeat-customer {
  display: grid;
  min-width: 145px;
  padding: 8px 10px;
  color: var(--text);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  grid-template-columns: 1fr auto;
  text-align: left;
  cursor: pointer;
}

.repeat-customer:hover,
.customer-primary-link:hover {
  border-color: rgba(37, 135, 232, 0.42);
  transform: translateY(-1px);
}

.repeat-customer span {
  overflow: hidden;
  font-size: 12px;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.repeat-customer strong {
  color: #ef4444;
  font-size: 13px;
}

.repeat-customer small {
  color: var(--muted);
  font-size: 12px;
  grid-column: 1 / -1;
}

.date-time-cell {
  display: flex;
  flex-direction: column;
}

.date-time-cell small {
  color: var(--muted);
  font-size: 12px;
}

.customer-link-list {
  display: flex;
  min-width: 145px;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
}

.customer-link-list button {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 0;
  color: #2587e8;
  background: transparent;
  border: 0;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
}

.customer-link-list .v-icon {
  color: currentColor;
}

.appointment-note {
  display: -webkit-box;
  min-width: 190px;
  max-width: 440px;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  font-size: 12px;
  line-height: 1.35;
}

.appointment-note--empty {
  color: var(--muted);
  font-style: italic;
}

.customer-primary-link {
  display: flex;
  min-width: 210px;
  width: 100%;
  padding: 3px;
  align-items: center;
  gap: 8px;
  color: var(--text);
  background: transparent;
  border: 1px solid transparent;
  border-radius: 9px;
  text-align: left;
  cursor: pointer;
  transition: 0.18s ease;
}

.customer-primary-link > span:nth-child(2) {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
}

.customer-primary-link strong {
  overflow: hidden;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.customer-primary-link small {
  color: var(--muted);
  font-size: 12px;
}

.danger-value {
  color: #ef4444;
  font-weight: 900;
}

.traffic-day-dialog,
.customer-dialog {
  --surface: #ffffff;
  --surface-soft: #f5f8fb;
  --surface-muted: #e9f0f5;
  --border: #dce6ed;
  --text: #142230;
  --muted: #66788a;
  overflow: hidden;
  color: var(--text) !important;
  background: var(--surface) !important;
  border-radius: 18px !important;
}

.traffic-day-dialog--dark,
.customer-dialog--dark {
  --surface: #10161d;
  --surface-soft: #18212b;
  --surface-muted: #22303d;
  --border: rgba(203, 213, 225, 0.15);
  --text: #f8fafc;
  --muted: #b5c0cc;
  color-scheme: dark;
}

.traffic-day-dialog__header,
.customer-dialog__header {
  display: flex;
  min-height: 88px;
  padding: 15px 18px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: linear-gradient(135deg, rgba(37, 135, 232, 0.13), transparent 62%);
  border-bottom: 1px solid var(--border);
}

.customer-dialog__header {
  background: linear-gradient(135deg, rgba(22, 167, 101, 0.14), transparent 62%);
}

.traffic-day-dialog__identity,
.customer-dialog__identity {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 13px;
}

.traffic-day-dialog__icon,
.customer-dialog__avatar {
  display: grid;
  flex: 0 0 50px;
  width: 50px;
  height: 50px;
  place-items: center;
  border-radius: 14px;
}

.traffic-day-dialog__icon {
  background: rgba(37, 135, 232, 0.12);
  border: 1px solid rgba(37, 135, 232, 0.2);
}

.customer-dialog__avatar {
  color: #ffffff;
  background: linear-gradient(145deg, #20b26b, #108a54);
  box-shadow: 0 8px 20px rgba(22, 167, 101, 0.24);
  font-size: 20px;
  font-weight: 900;
}

.traffic-day-dialog__identity h2,
.customer-dialog__identity h2 {
  margin: 2px 0 2px;
  color: var(--text);
  font-size: 22px;
  line-height: 1.1;
}

.traffic-day-dialog__identity p,
.customer-dialog__identity p {
  margin: 0;
  color: var(--muted);
  font-size: 12px;
}

.traffic-day-dialog__content,
.customer-dialog__content {
  display: grid;
  gap: 12px;
  padding: 15px !important;
  color: var(--text) !important;
  background: var(--surface);
}

.traffic-day-summary {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.traffic-day-summary > div {
  min-height: 100px;
  padding: 13px 48px 12px 13px;
}

.traffic-day-summary > div::before {
  background: var(--day-color);
}

.traffic-day-summary strong,
.customer-dialog-kpis strong {
  margin-top: 4px;
  font-size: 23px;
  line-height: 1.08;
}

.traffic-day-summary .v-icon {
  position: absolute;
  top: 15px;
  right: 13px;
  opacity: 0.76;
}

.traffic-day-chart,
.traffic-day-table,
.customer-dialog-panel {
  padding: 13px;
  background: var(--surface-soft);
  border: 1px solid var(--border);
  border-radius: 14px;
}

.traffic-day-chart h3,
.traffic-day-table h3,
.customer-dialog-panel h3 {
  margin: 2px 0 0;
  font-size: 16px;
}

.traffic-day-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  color: var(--muted);
  background: rgba(37, 135, 232, 0.08);
  border: 1px solid rgba(37, 135, 232, 0.16);
  border-radius: 11px;
  font-size: 12px;
  line-height: 1.45;
}

.traffic-hour-value--visits {
  color: #2587e8;
}

.customer-dialog-kpis {
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.customer-dialog-kpis > div {
  min-height: 108px;
  padding: 13px 13px 12px 46px;
  background: linear-gradient(135deg, color-mix(in srgb, var(--customer-color) 9%, transparent), transparent 60%), var(--surface-soft);
}

.customer-dialog-kpis > div::before {
  background: var(--customer-color);
}

.customer-dialog-kpis .v-icon {
  position: absolute;
  top: 16px;
  left: 12px;
}

.customer-dialog-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.customer-dialog-panel--full {
  grid-column: 1 / -1;
}

.appointment-status {
  display: inline-flex;
  padding: 4px 8px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 850;
  white-space: nowrap;
}

.appointment-status--good {
  color: #0b8b53;
  background: rgba(22, 167, 101, 0.12);
}

.appointment-status--danger {
  color: #d93636;
  background: rgba(239, 68, 68, 0.13);
}

.appointment-status--warning {
  color: #b36c00;
  background: rgba(245, 158, 11, 0.15);
}

.appointment-status--neutral {
  color: var(--muted);
  background: var(--surface-muted);
}

.feedback-command-center {
  padding: 18px;
  margin-bottom: 18px;
  overflow: hidden;
  background: radial-gradient(circle at 92% 0%, rgba(22, 167, 101, 0.12), transparent 32%), var(--surface);
  border: 1px solid var(--border);
  border-radius: 18px;
  box-shadow: var(--shadow);
}

.feedback-command-header,
.feedback-command-title,
.feedback-feed-topline,
.feedback-feed-meta,
.employee-feedback-heading {
  display: flex;
  align-items: center;
}

.feedback-command-header,
.employee-feedback-heading {
  justify-content: space-between;
  gap: 18px;
}

.feedback-command-title {
  min-width: 0;
  gap: 13px;
}

.feedback-command-icon {
  display: grid;
  flex: 0 0 46px;
  width: 46px;
  height: 46px;
  place-items: center;
  background: linear-gradient(145deg, #21b66f, #108a54);
  border-radius: 14px;
  box-shadow: 0 9px 20px rgba(22, 167, 101, 0.25);
}

.feedback-command-title h2,
.employee-feedback-heading h3 {
  margin: 2px 0 3px;
  color: var(--text);
  font-size: 21px;
  letter-spacing: -0.02em;
}

.feedback-command-title p,
.employee-feedback-heading p,
.feedback-empty-state p {
  margin: 0;
  color: var(--muted);
  font-size: 12px;
  line-height: 1.45;
}

.feedback-kpi-grid,
.employee-feedback-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-top: 16px;
}

.feedback-kpi-grid > article,
.employee-feedback-kpis > div {
  position: relative;
  min-width: 0;
  padding: 14px;
  background: var(--surface-soft);
  border: 1px solid var(--border);
  border-radius: 14px;
}

.feedback-kpi-grid > article::before {
  position: absolute;
  top: 10px;
  bottom: 10px;
  left: 0;
  width: 3px;
  background: var(--feedback-accent);
  border-radius: 0 5px 5px 0;
  content: "";
}

.feedback-kpi-grid > article > span {
  display: grid;
  width: 34px;
  height: 34px;
  margin-bottom: 9px;
  place-items: center;
  background: color-mix(in srgb, var(--feedback-accent) 12%, transparent);
  border-radius: 10px;
}

.feedback-kpi-grid small,
.employee-feedback-kpis span {
  display: block;
  color: var(--muted);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.feedback-kpi-grid strong,
.employee-feedback-kpis strong {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 3px 0;
  color: var(--text);
  font-size: 24px;
  line-height: 1.1;
}

.feedback-kpi-grid p,
.employee-feedback-kpis small {
  margin: 0;
  color: var(--muted);
  font-size: 11px;
}

.feedback-analysis-grid,
.employee-feedback-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(300px, 0.75fr);
  gap: 12px;
  margin-top: 12px;
}

.feedback-panel {
  min-width: 0;
  padding: 15px;
  background: var(--surface-soft);
  border: 1px solid var(--border);
  border-radius: 15px;
}

.feedback-panel--full {
  grid-column: 1 / -1;
}

.feedback-distribution {
  display: grid;
  gap: 9px;
  margin-top: 12px;
}

.feedback-distribution > div {
  display: grid;
  grid-template-columns: 42px minmax(80px, 1fr) 30px;
  align-items: center;
  gap: 8px;
  color: var(--muted);
  font-size: 12px;
}

.feedback-distribution > div > span {
  display: flex;
  align-items: center;
  gap: 2px;
  font-weight: 800;
}

.feedback-distribution > div > div {
  height: 7px;
  overflow: hidden;
  background: var(--surface-muted);
  border-radius: 999px;
}

.feedback-distribution i {
  display: block;
  min-width: 0;
  height: 100%;
  background: linear-gradient(90deg, #16a765, #4ed39a);
  border-radius: inherit;
}

.feedback-distribution > div > strong {
  color: var(--text);
  text-align: right;
}

.feedback-tag-cloud {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 16px;
}

.feedback-tag-cloud > span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 8px;
  color: #13784d;
  background: rgba(22, 167, 101, 0.09);
  border: 1px solid rgba(22, 167, 101, 0.17);
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
}

.feedback-tag-cloud .v-icon {
  color: currentColor;
}

.feedback-tag-cloud strong {
  display: grid;
  min-width: 20px;
  height: 20px;
  place-items: center;
  color: #fff;
  background: #16a765;
  border-radius: 50%;
  font-size: 10px;
}

.wc-insights--dark .feedback-tag-cloud > span {
  color: #74d7a7;
}

.feedback-feed {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-top: 12px;
}

.feedback-feed-card {
  min-width: 0;
  padding: 13px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-left: 3px solid #16a765;
  border-radius: 12px;
}

.feedback-feed-card--attention {
  border-left-color: #f59e0b;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.07), var(--surface));
}

.feedback-feed-topline {
  justify-content: space-between;
  color: var(--muted);
}

.feedback-feed-topline > span {
  display: flex;
  align-items: center;
  gap: 3px;
  color: var(--text);
}

.feedback-customer {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 9px;
  color: var(--text);
  font-size: 12px;
}

.feedback-customer .v-icon {
  color: #16a765;
}

.feedback-all-dialog__body {
  padding: 16px !important;
  background: var(--surface-soft);
}

.feedback-feed--all {
  margin-top: 0;
}

.feedback-all-iterator ::v-deep .v-data-footer {
  margin-top: 14px;
  color: var(--muted);
  border-top: 1px solid var(--border);
}

.feedback-feed-card p {
  min-height: 38px;
  margin: 10px 0;
  color: var(--text);
  font-size: 12px;
  line-height: 1.5;
}

.feedback-comment--empty {
  color: var(--muted) !important;
  font-style: italic;
}

.feedback-feed-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 9px;
}

.feedback-feed-tags span {
  padding: 3px 6px;
  color: #13784d;
  background: rgba(22, 167, 101, 0.09);
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
}

.feedback-feed-meta {
  flex-wrap: wrap;
  gap: 5px 10px;
  color: var(--muted);
  font-size: 10px;
}

.feedback-feed-meta span {
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

.feedback-feed-meta .v-icon {
  color: currentColor;
}

.feedback-empty-state {
  display: flex;
  align-items: center;
  gap: 13px;
  min-height: 108px;
  padding: 18px;
  margin-top: 14px;
  background: var(--surface-soft);
  border: 1px dashed rgba(22, 167, 101, 0.35);
  border-radius: 14px;
}

.feedback-empty-state > span {
  display: grid;
  flex: 0 0 52px;
  width: 52px;
  height: 52px;
  place-items: center;
  background: rgba(22, 167, 101, 0.1);
  border-radius: 14px;
}

.feedback-empty-state strong {
  display: block;
  margin-bottom: 3px;
  color: var(--text);
}

.feedback-table-score,
.feedback-response-pill {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 3px;
  white-space: nowrap;
}

.feedback-table-score small {
  color: var(--muted);
  font-size: 10px;
}

.feedback-table-score--clickable {
  padding: 4px 7px;
  background: rgba(245, 185, 66, 0.1);
  border-radius: 999px;
}

.feedback-response-pill {
  padding: 4px 7px;
  color: #13784d;
  background: rgba(22, 167, 101, 0.1);
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
}

.muted-value {
  color: var(--muted);
}

.center-feedback-panel {
  background: radial-gradient(circle at 92% 0%, rgba(245, 185, 66, 0.09), transparent 32%), var(--surface);
}

.employee-feedback-pane {
  display: grid;
  gap: 14px;
}

.employee-feedback-heading {
  padding: 15px;
  background: linear-gradient(135deg, rgba(22, 167, 101, 0.1), rgba(22, 167, 101, 0.02));
  border: 1px solid rgba(22, 167, 101, 0.18);
  border-radius: 15px;
}

.employee-feedback-heading h3 {
  font-size: 19px;
}

.employee-feedback-kpis {
  margin-top: 0;
}

.employee-feedback-kpis > div:first-child {
  background: linear-gradient(135deg, rgba(245, 185, 66, 0.12), var(--surface-soft));
  border-color: rgba(245, 185, 66, 0.25);
}

.employee-feedback-grid {
  margin-top: 0;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.feedback-tag-cloud--employee {
  margin-top: 10px;
}

.feedback-feed--employee {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.employee-feedback-soft-empty {
  padding: 18px;
  color: var(--muted);
  background: var(--surface-soft);
  border-radius: 11px;
  font-size: 12px;
  text-align: center;
}

.feedback-empty-state--employee {
  margin-top: 0;
}

@media (max-width: 1450px) {
  .kpi-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .team-summary-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .mini-kpis--traffic {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .content-grid--wide {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .feedback-feed {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 980px) {
  .wc-insights {
    padding: 0;
    border-radius: 0;
  }

  .content-grid--half,
  .content-grid--wide {
    grid-template-columns: 1fr;
  }

  .panel--full {
    grid-column: auto;
  }

  .category-grid,
  .mini-kpis {
    grid-template-columns: repeat(2, 1fr);
  }

  .employee-detail-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .employee-analysis-grid {
    grid-template-columns: 1fr;
  }

  .employee-customer-hero {
    grid-template-columns: 1fr;
  }

  .employee-insight-panel--full {
    grid-column: auto;
  }

  .employee-top-entries {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .employee-shift-mix {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .dialog-kpis {
    grid-template-columns: repeat(3, 1fr);
  }

  .dialog-warnings {
    grid-template-columns: 1fr;
  }

  .no-show-highlight-grid,
  .traffic-day-summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .customer-dialog-kpis {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .customer-dialog-grid {
    grid-template-columns: 1fr;
  }

  .feedback-command-header,
  .employee-feedback-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .feedback-kpi-grid,
  .employee-feedback-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .feedback-analysis-grid,
  .employee-feedback-grid {
    grid-template-columns: 1fr;
  }

  .feedback-panel--full {
    grid-column: auto;
  }

  .customer-dialog-panel--full {
    grid-column: auto;
  }
}

@media (max-width: 650px) {
  .insights-hero {
    align-items: flex-start;
  }

  .hero-icon {
    flex-basis: 43px;
    width: 43px;
    height: 43px;
  }

  .hero-meta span:nth-child(3) {
    display: none;
  }

  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .kpi-card {
    min-height: 132px;
    padding: 11px;
  }

  .tab-content {
    padding: 9px;
  }

  .category-grid,
  .mini-kpis {
    grid-template-columns: 1fr 1fr;
  }

  .team-summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .panel {
    padding: 11px;
  }

  .capacity-breakdown {
    grid-template-columns: 1fr;
  }

  .table-tools {
    width: 100%;
    align-items: stretch;
    flex-direction: column;
  }

  .table-search,
  .table-search--small,
  .category-select {
    width: 100%;
  }

  .export-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .dialog-kpis {
    grid-template-columns: repeat(2, 1fr);
  }

  .employee-dialog-header {
    align-items: flex-start;
    padding: 13px;
  }

  .employee-dialog-score {
    display: none;
  }

  .employee-dialog-focus {
    display: none;
  }

  .employee-customer-kpis {
    grid-template-columns: 1fr;
  }

  .employee-detail-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .employee-dialog-person h2 {
    font-size: 18px;
  }

  .employee-dialog-person small {
    font-size: 12px;
  }

  .employee-dialog-body {
    padding: 10px !important;
  }

  .employee-detail-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .employee-top-entries {
    grid-template-columns: 1fr;
  }

  .employee-shift-mix {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .employee-capacity-legend {
    align-items: flex-start;
    flex-direction: column;
  }

  .no-show-highlight-grid,
  .traffic-day-summary,
  .customer-dialog-kpis {
    grid-template-columns: 1fr;
  }

  .repeat-customer-strip {
    align-items: stretch;
    flex-direction: column;
  }

  .repeat-customer-strip__title,
  .repeat-customer {
    min-width: 0;
    width: 100%;
  }

  .feedback-command-center {
    padding: 12px;
  }

  .feedback-command-title {
    align-items: flex-start;
  }

  .feedback-kpi-grid,
  .employee-feedback-kpis,
  .feedback-feed,
  .feedback-feed--employee {
    grid-template-columns: 1fr;
  }

  .traffic-day-dialog__header,
  .customer-dialog__header {
    align-items: flex-start;
    padding: 12px;
  }

  .traffic-day-dialog__identity h2,
  .customer-dialog__identity h2 {
    font-size: 18px;
  }
}
</style>
